import { useState, useEffect, useRef } from 'react';
import { api } from './api';
import {
  C, CATS, BIZ_CATS, VISIBLE_CATS, VISIBLE_BIZ_CATS, ALL_CATS, ALL_BIZ_CATS,
  getCat, isBusinessAccount, isUncategorized, tintColor, pillTextColor,
  visibleCustomCats, isIncomeCat, isExpenseCat, isGiftCat, isKpiIncomeRow, isKpiSpendingRow,
  catBucket, BUCKETS, BUCKET_LABELS,
  fmt, fmtDate, monthLabel
} from './constants';
import { findMatchingRule } from './ruleMatch';
// Only the validator — investmentDates also exports a monthLabel, and constants
// already exports one with different semantics (a transaction month header).
// That file must stay free of destructuring or this import breaks the build;
// its header explains why.
import { validateSnapshotDate } from './investmentDates';
import { computeMonthlyFindings, GROUP_LABELS } from './monthlyAnalysis';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar,
         XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList, ReferenceLine } from 'recharts';

// Chart series colors. Validated for colorblind separation (protan ΔE 18.1) — the
// brand's sage/coral pair scored 4.6 and was unreadable for red-green CVD.
// Color follows the entity: spending is gold in every chart, income always blue.
const CHART = { income: '#2F7F9E', spending: '#C4954A' };
// Mirrors INV_SOURCE_LABELS in server/index.js — used when naming the snapshot
// an import is about to replace.
const INV_SOURCE_LABEL = { summary: 'Portfolio summary', returns: 'Returns' };
// One line per investment account. Reuses the colourblind-safe pair above and
// extends it; sage/coral are deliberately not adjacent in this list.
const INV_LINE_COLORS = ['#2F7F9E', '#C4954A', '#5B7B5E', '#8A6BA1'];
const TREND_RANGES = { '3M':3, '6M':6, '12M':12, 'YTD':'ytd' };
const TREND_RANGE_LABELS = { '3M':'3 months', '6M':'6 months', '12M':'12 months', 'YTD':'year to date' };

// Admin sub-tabs. Always owner view, so these stay on the sage palette rather
// than picking up the business navy.
const ADMIN_TABS = [
  ['upload',     '📄 Upload'],
  ['rules',      '🏷️ Rules'],
  ['categories', '🎨 Categories'],
  ['accounts',   '🏦 Accounts'],
  ['envelopes',  '✉️ Envelopes'],
  ['settings',   '⚙️ Settings'],
];

// ─── STYLES ───────────────────────────────────────────────────────────────────
const S = {
  app:          { minHeight: '100vh', background: C.cream, fontFamily: "'DM Sans', -apple-system, sans-serif", color: C.ink, overflowX: 'hidden' },
  nav:          { background: 'white', borderBottom: `1px solid ${C.warmDark}`, padding: '0 20px', display: 'flex', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 },
  logo:         { fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em', padding: '14px 0', marginRight: 20, whiteSpace: 'nowrap' },
  navTab:       { padding: '16px 14px 14px', fontSize: 14, fontWeight: 500, color: C.stone, cursor: 'pointer', borderBottom: '2px solid transparent', background: 'none', borderTop: 'none', borderLeft: 'none', borderRight: 'none', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  navTabActive: (biz) => ({ color: biz ? C.navy : C.sage, borderBottom: `2px solid ${biz ? C.navy : C.sage}` }),
  content:      { maxWidth: 1000, margin: '0 auto', padding: '24px 20px' },
  card:         { background: 'white', borderRadius: 14, border: `1px solid ${C.warmDark}`, padding: '16px 20px' },
  cardSm:       { background: 'white', borderRadius: 12, border: `1px solid ${C.warmDark}`, padding: '10px 14px' },
  grid4:        { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 },
  grid2:        { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  kpiLabel:     { fontSize: 12, fontWeight: 600, color: C.stoneMid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 },
  kpiVal:       { fontSize: 26, fontWeight: 700 },
  kpiSub:       { fontSize: 12, color: C.stoneMid, marginTop: 3 },
  secTitle:     { fontSize: 12, fontWeight: 600, color: C.stoneMid, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 },
  monthNav:     { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 },
  monthBtn:     { background: 'white', border: `1px solid ${C.warmDark}`, borderRadius: 8, width: 34, height: 34, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: C.stone },
  monthLabel:   { fontSize: 20, fontWeight: 800, minWidth: 150 },
  catRow:       { display: 'flex', alignItems: 'center', gap: 10, padding: 8, borderRadius: 8, cursor: 'pointer' },
  catIcon:      { width: 32, height: 32, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 },
  barWrap:      { width: '100%', height: 3, background: C.warmDark, borderRadius: 2, marginTop: 3 },
  bar:          { height: 3, borderRadius: 2 },
  txRow:        { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 6px', borderRadius: 10, cursor: 'pointer', transition: 'background .1s', borderBottom: '1px solid #F5F1EC' },
  txIcon:       { width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 },
  txName:       { fontSize: 14, fontWeight: 600, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 },
  txMeta:       { fontSize: 12, color: C.stoneMid, marginTop: 2, lineHeight: 1.3 },
  insCard:      { background: 'white', borderRadius: 10, border: `1px solid ${C.warmDark}`, padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'flex-start' },
  pill:         { display: 'inline-flex', alignItems: 'center', padding: '5px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', border: '1px solid', fontFamily: 'inherit' },
  input:        { width: '100%', padding: '9px 12px', border: `1px solid ${C.warmDeep}`, borderRadius: 10, fontSize: 13, color: C.ink, background: 'white', outline: 'none', fontFamily: 'inherit' },
  btnPrimary:   (biz) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none', background: biz ? C.navy : C.sage, color: 'white', fontFamily: 'inherit' }),
  btnGhost:     { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `1px solid ${C.warmDeep}`, background: 'transparent', color: C.stone, fontFamily: 'inherit' },
  btnSm:        { display: 'inline-flex', alignItems: 'center', padding: '5px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  cfBtn:        { display: 'inline-flex', alignItems: 'center', padding: '5px 12px', borderRadius: 20, background: C.sage, color: 'white', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit', whiteSpace: 'nowrap' },
  pendBadge:    { fontSize: 10, fontWeight: 700, background: C.amberLight, color: C.amber, padding: '2px 6px', borderRadius: 10 },
  bottomNav:    { position: 'fixed', bottom: 0, left: 0, right: 0, background: 'white', borderTop: `1px solid ${C.warmDark}`, display: 'flex', zIndex: 100, paddingBottom: 'env(safe-area-inset-bottom)' },
  bottomNavItem:(active, accent) => ({ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, padding: '8px 0 6px', minHeight: 56, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 600, color: active ? accent : C.stoneMid }),
  backdrop:     { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.3)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal:        { background: 'white', borderRadius: 18, padding: 24, maxWidth: 440, width: '100%', maxHeight: '80vh', overflowY: 'auto' },
  modalLabel:   { fontSize: 11, fontWeight: 600, color: C.stoneMid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 7 },
  uploadZone:   { border: `2px dashed ${C.warmDeep}`, borderRadius: 14, padding: '36px 24px', textAlign: 'center', cursor: 'pointer', background: C.warm },
  uploadDrag:   { borderColor: C.sage, background: C.sageLight },
};

// Single source of truth for the mobile breakpoint (<=767px). matchMedia keeps
// this in sync without a resize listener firing on every pixel of a drag.
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = e => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isMobile;
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('email');

  async function submit(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const user = await api.login(mode === 'viewer' ? '' : email, password);
      onLogin(user);
    } catch { setError('Invalid credentials. Try again.'); }
    setLoading(false);
  }

  return (
    <div style={{ minHeight: '100vh', background: C.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
      <div style={{ width: '100%', maxWidth: 400, padding: '0 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🌿</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: C.ink }}>Tally</div>
          <div style={{ fontSize: 14, color: C.stone, marginTop: 6 }}>Chiasson family finance tracker</div>
        </div>
        <div style={{ background: 'white', borderRadius: 20, border: `1px solid ${C.warmDark}`, padding: 32 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
            {[['email','👤 Sign in'],['viewer','👁️ View only']].map(([m,l]) => (
              <button key={m} onClick={() => setMode(m)} style={{ flex:1, padding:8, borderRadius:10, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:600, background: mode===m ? C.sage : C.warm, color: mode===m ? 'white' : C.stone }}>{l}</button>
            ))}
          </div>
          <form onSubmit={submit}>
            {mode === 'email' && (
              <div style={{ marginBottom: 14 }}>
                <div style={S.modalLabel}>Email</div>
                <input style={S.input} type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} required />
              </div>
            )}
            <div style={{ marginBottom: 20 }}>
              <div style={S.modalLabel}>{mode === 'viewer' ? 'Viewer password' : 'Password'}</div>
              <input style={S.input} type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required />
            </div>
            {error && <div style={{ fontSize: 13, color: C.coral, marginBottom: 14 }}>{error}</div>}
            <button style={{ ...S.btnPrimary(false), width: '100%', justifyContent: 'center', padding: 12 }} type="submit" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── TX MODAL ─────────────────────────────────────────────────────────────────
function TxModal({ tx, mode, customCats = [], rules = [], accounts = [], envelopes = [], onClose, onUpdate, canEdit, onCreateRule, onEnvelopeChange }) {
  const isBiz = mode === 'business';
  // Defined here to match DashboardPanel/TxListPanel. It was referenced by the
  // Stage 7 Budget-bucket buttons below with no binding in this scope, which
  // threw a ReferenceError the moment that section rendered — CRA's build did
  // not catch it. See the "Verification" note in TALLY_RULES.md.
  const accent = isBiz ? C.navy : C.sage;
  const cats = isBiz ? VISIBLE_BIZ_CATS : VISIBLE_CATS;
  const [cat, setCat] = useState(tx.category);
  const [status, setStatus] = useState(tx.status);
  const [taxTag, setTaxTag] = useState(tx.tax_tag);
  const [bucketOvr, setBucketOvr] = useState(tx.bucket_override || null);
  const [envId, setEnvId] = useState(tx.envelope_id || null);
  const [trendExcl, setTrendExcl] = useState(tx.trend_exclude || false);
  const [editingAmt, setEditingAmt] = useState(false);
  const [amtDraft, setAmtDraft] = useState(String(tx.amount));

  // Escape closes the modal, same as clicking the backdrop. The backdrop is a
  // full-screen overlay, so without this a left-open modal swallows clicks on the
  // nav behind it.
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function save(changes) {
    try { await api.updateTx(tx.id, changes); onUpdate({ ...tx, ...changes }); }
    catch (e) { console.error(e); }
  }

  const cfg = getCat(cat, mode);
  // Reads the live dropdown value, not tx.category — picking a category unlocks
  // Confirm immediately, without waiting for the PATCH to land.
  const canConfirm = !isUncategorized(cat);
  const pickCats = visibleCustomCats(customCats, mode);
  // Grouping here is a UI convenience, not a type claim. Savings & Investing is
  // typed 'expense' — money going out — but sits with income because that is
  // where someone looks for it. Everything else in this group is genuinely
  // type='income', built-in or custom.
  const PICKER_SAVINGS = ['Savings & Investing'];
  // Gifts are inflow too — they belong in the income/other group, not spending.
  const isInflow = c => isIncomeCat(c, mode, customCats) || isGiftCat(c, mode, customCats);
  const inIncomeGroup = c => isInflow(c) || PICKER_SAVINGS.includes(c);
  const customIncome  = pickCats.filter(c => isInflow(c.name));
  const customSpend   = pickCats.filter(c => !isInflow(c.name));
  // Recomputed, not stored: transactions carry no record of which rule set their
  // category, and POST /api/rules applies retroactively without consulting
  // precedence — so a stored id would be wrong for most rows anyway.
  // Transfers never consult rules at all (buildTx short-circuits on
  // detectTransfer), so a matching keyword there would be a lie.
  const matchedRule = tx.transfer_hide
    ? null
    : findMatchingRule(tx.description, rules,
        isBusinessAccount(tx.account, accounts) ? 'business' : 'household');
  return (
    <div style={S.backdrop} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:18 }}>
          <div style={{ flex:1, marginRight:12 }}>
            <div style={{ fontSize:15, fontWeight:700, marginBottom:3 }}>{tx.description}</div>
            <div style={{ fontSize:12, color:C.stoneMid }}>{fmtDate(tx.date)} · {tx.account}</div>
          </div>
          <div style={{ textAlign:'right', whiteSpace:'nowrap' }}>
            {editingAmt ? (
              <div style={{ display:'flex', flexDirection:'column', gap:8, width:200 }}>
                <input type="number" step="0.01" autoFocus value={amtDraft}
                  onChange={e => setAmtDraft(e.target.value)}
                  style={{ ...S.input, fontSize:15, textAlign:'right' }} />
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => { setAmtDraft(String(tx.amount)); setEditingAmt(false); }}
                    style={{ ...S.btnGhost, flex:1, justifyContent:'center', fontSize:12 }}>Cancel</button>
                  <button onClick={async () => { const n = parseFloat(amtDraft);
                      if (!isNaN(n)) await save({ amount:n }); setEditingAmt(false); }}
                    style={{ ...S.btnPrimary(isBiz), flex:1, justifyContent:'center', fontSize:12 }}>Save</button>
                </div>
              </div>
            ) : (<>
              <div style={{ fontSize:20, fontWeight:700, color: parseFloat(tx.amount)>0 ? C.sage : C.ink }}>
                {parseFloat(tx.amount)>0?'+':''}{fmt(parseFloat(tx.amount))}
              </div>
              {canEdit && (
                <button onClick={() => { setAmtDraft(String(tx.amount)); setEditingAmt(true); }}
                  style={{ background:'none', border:'none', color:C.stoneMid, fontSize:11, cursor:'pointer',
                           fontFamily:'inherit', padding:'2px 0', textDecoration:'underline' }}>Edit amount</button>
              )}
            </>)}
          </div>
        </div>

        {canEdit && (<>
          <div style={{ marginBottom:14 }}>
            <div style={S.modalLabel}>Status</div>
            <div style={{ display:'flex', gap:8 }}>
              {[['confirmed','✅ Confirmed',C.sage],['pending','⏳ Needs review',C.amber]].map(([v,l,bg]) => {
                const locked = v==='confirmed' && !canConfirm;
                return (
                  <button key={v} disabled={locked}
                    title={locked ? 'Pick a category before confirming' : undefined}
                    onClick={() => { setStatus(v); save({ status:v }); }}
                    style={{ flex:1, padding:8, borderRadius:10, border:'none', cursor: locked?'not-allowed':'pointer', opacity: locked?0.5:1, fontFamily:'inherit', fontSize:13, fontWeight:600, background: status===v ? bg : C.warm, color: status===v ? 'white' : C.stone }}>{l}</button>
                );
              })}
            </div>
          </div>

          <div style={{ marginBottom:14 }}>
            <div style={S.modalLabel}>Category</div>
            <select style={S.input} value={cat} onChange={e => { setCat(e.target.value); save({ category:e.target.value }); }}>
              <optgroup label={isBiz ? 'Business spending' : 'Spending'}>
                {cats.filter(c => !inIncomeGroup(c)).map(c => (
                  <option key={c} value={c}>{getCat(c,mode,customCats).icon} {c}</option>
                ))}
              </optgroup>
              {customSpend.length > 0 && (
                <optgroup label="Custom">
                  {customSpend.map(c => (
                    <option key={c.id} value={c.name}>{c.icon} {c.name}</option>
                  ))}
                </optgroup>
              )}
              <optgroup label="Income & Savings">
                {cats.filter(inIncomeGroup).map(c => (
                  <option key={c} value={c}>{getCat(c,mode,customCats).icon} {c}</option>
                ))}
                {customIncome.map(c => (
                  <option key={c.id} value={c.name}>{c.icon} {c.name}</option>
                ))}
              </optgroup>
              <optgroup label="Hidden">
                <option value="Credit Card Payment">💳 Credit Card Payment</option>
                {!isBiz && <option value="Internal Transfer">↔️ Internal Transfer</option>}
              </optgroup>
            </select>
            {matchedRule && (
              <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:7, fontSize:11, flexWrap:'wrap',
                            color: matchedRule.category === cat ? C.stoneMid : C.amber }}>
                <span>🏷️</span>
                <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:8,
                               background: matchedRule.view==='business' ? C.navyLight : C.sageLight,
                               color: matchedRule.view==='business' ? C.navy : C.sage }}>
                  {matchedRule.view==='business' ? '💼' : '🏠'}
                </span>
                {matchedRule.category === cat
                  ? <span>Rule "{matchedRule.keyword}" → {matchedRule.category}</span>
                  : <span>Rule "{matchedRule.keyword}" sets {matchedRule.category} · changed to {cat}</span>}
              </div>
            )}
          </div>

          <div style={{ marginBottom:18 }}>
            <div style={S.modalLabel}>Tax tags</div>
            <div style={{ display:'flex', gap:8 }}>
              {isBiz ? (
                [['deductible','💼 Deductible',C.navy],['50pct','🍽️ 50% (meals)',C.gold]].map(([tag,l,bg]) => (
                  <button key={tag} onClick={() => { const nt=taxTag===tag?null:tag; setTaxTag(nt); save({ tax_tag:nt }); }}
                    style={{ flex:1, padding:8, borderRadius:10, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:600, background: taxTag===tag ? bg : C.warm, color: taxTag===tag ? 'white' : C.stone }}>{l}</button>
                ))
              ) : (
                [['medical','🏥 Medical',C.mint],['deductible','💼 Business',C.gold]].map(([tag,l,bg]) => (
                  <button key={tag} onClick={() => { const nt=taxTag===tag?null:tag; setTaxTag(nt); save({ tax_tag:nt }); }}
                    style={{ flex:1, padding:8, borderRadius:10, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:600, background: taxTag===tag ? bg : C.warm, color: taxTag===tag ? 'white' : C.stone }}>{l}</button>
                ))
              )}
            </div>
          </div>

          {catBucket(cat, mode, customCats) !== null && (
            <div style={{ marginBottom:18 }}>
              <div style={S.modalLabel}>Budget bucket</div>
              <div style={{ display:'flex', gap:8 }}>
                {BUCKETS.map(b => {
                  const effective = bucketOvr || catBucket(cat, mode, customCats);
                  const isActive = effective === b;
                  return (
                    <button key={b} onClick={() => {
                      const nb = bucketOvr === b ? null : b;
                      setBucketOvr(nb);
                      save({ bucket_override: nb });
                    }}
                      style={{ flex:1, padding:8, borderRadius:10, border:'none', cursor:'pointer',
                               fontFamily:'inherit', fontSize:12, fontWeight:600,
                               background: isActive ? accent : C.warm,
                               color: isActive ? 'white' : C.stone }}>
                      {BUCKET_LABELS[b]}
                    </button>
                  );
                })}
              </div>
              <div style={{ marginTop:6, fontSize:11, color: bucketOvr ? C.amber : C.stoneMid }}>
                {bucketOvr
                  ? `Override · category default: ${catBucket(cat, mode, customCats)}`
                  : `Inherited from category: ${catBucket(cat, mode, customCats)}`}
              </div>
            </div>
          )}

          {(catBucket(cat, mode, customCats) !== null || envId) && (() => {
            // Open envelopes, plus the currently-tagged one even if closed, so a
            // tag is never stranded and the operator can always untag it.
            const opts = envelopes.filter(e => e.status === 'open' || e.id === envId);
            if (!opts.length) return null;
            const tagged = envId ? envelopes.find(e => e.id === envId) : null;
            return (
              <div style={{ marginBottom:18 }}>
                <div style={S.modalLabel}>Envelope</div>
                <select style={S.input} value={envId || ''}
                  onChange={e => {
                    const nv = e.target.value || null;
                    setEnvId(nv);
                    save({ envelope_id: nv });
                    if (onEnvelopeChange) onEnvelopeChange();
                  }}>
                  <option value="">None</option>
                  {opts.map(e => (
                    <option key={e.id} value={e.id}>{e.name}{e.status==='closed' ? ' (closed)' : ''}</option>
                  ))}
                </select>
                <div style={{ marginTop:6, fontSize:11, color: envId ? C.amber : C.stoneMid }}>
                  {envId
                    ? `Funded from "${tagged ? tagged.name : 'envelope'}" — excluded from Needs/Wants/Savings, still counted in Spending.`
                    : 'Tag to a funded pot to keep it out of the Needs/Wants/Savings percentages.'}
                </div>
              </div>
            );
          })()}

          {/* Not gated by catBucket — a one-off income row (a gift) is the first
              case, so this applies to income and expense alike. Distinct from
              "Hide transfer": that removes double-counted money from everything;
              this keeps the money in the month's totals and only drops it from
              the trend line. */}
          <div style={{ marginBottom:18 }}>
            <div style={S.modalLabel}>Trend chart</div>
            <button onClick={() => { const nv = !trendExcl; setTrendExcl(nv); save({ trend_exclude: nv }); }}
              style={{ width:'100%', padding:8, borderRadius:10, border:'none', cursor:'pointer',
                       fontFamily:'inherit', fontSize:12, fontWeight:600,
                       background: trendExcl ? C.amber : C.warm,
                       color: trendExcl ? 'white' : C.stone }}>
              {trendExcl ? '✓ Excluded from trends' : 'Exclude from trends'}
            </button>
            <div style={{ marginTop:6, fontSize:11, color: trendExcl ? C.amber : C.stoneMid }}>
              {trendExcl
                ? 'One-off — hidden from the trend line only. Still counted in this month’s KPI cards and Needs/Wants/Savings.'
                : 'A one-time amount can be dropped from the trend chart while still counting in this month’s totals and buckets.'}
            </div>
          </div>
        </>)}

        <div style={{ borderTop:`1px solid ${C.warmDark}`, paddingTop:14, display:'flex', gap:8, justifyContent:'space-between' }}>
          <button style={{ ...S.btnGhost, fontSize:12 }} onClick={() => { onCreateRule(tx); onClose(); }}>Create rule</button>
          <div style={{ display:'flex', gap:8 }}>
            {canEdit && !tx.transfer_hide && (
              <button style={{ ...S.btnGhost, fontSize:12, color:C.coral, borderColor:C.coral }}
                onClick={async () => { await save({ transfer_hide:true, status:'hidden' }); onClose(); }}>Hide transfer</button>
            )}
            {canEdit && status==='pending' ? (
              <button style={{ ...S.btnPrimary(isBiz), fontSize:12,
                               opacity:canConfirm?1:0.5, cursor:canConfirm?'pointer':'not-allowed' }}
                disabled={!canConfirm}
                title={canConfirm ? undefined : 'Pick a category before confirming'}
                onClick={async () => { setStatus('confirmed'); await save({ status:'confirmed' }); onClose(); }}>
                {canConfirm ? '✓ Confirm' : 'Categorize first'}
              </button>
            ) : (
              <button style={S.btnPrimary(isBiz)} onClick={onClose}>Close</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── RULE MODAL ───────────────────────────────────────────────────────────────
function RuleModal({ initial, mode, customCats = [], onSave, onClose }) {
  const isBiz = mode === 'business';
  const cats = isBiz ? VISIBLE_BIZ_CATS : VISIBLE_CATS;
  // A category carried in from a transaction can be a household name while we
  // are in business view — parsers wrote household names onto every account. The
  // <select> would then show its first option while state still held the stale
  // name, and Save would store that name. Seed with a valid one instead.
  const pickCats = visibleCustomCats(customCats, mode);
  const seed = initial?.category;
  // Must validate against the *filtered* list: seeding from the raw list would
  // let a business rule adopt a household-only custom category, with the
  // <select> showing its first option while state held the unlisted name —
  // exactly the seed leak fixed in 6dcf130.
  const seedOk = seed && (cats.includes(seed) || pickCats.some(c => c.name === seed));
  const [kw, setKw] = useState(initial?.keyword || '');
  const [cat, setCat] = useState(seedOk ? seed : (isBiz ? 'Software & Subscriptions' : 'Groceries'));
  return (
    <div style={S.backdrop} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize:17, fontWeight:700, marginBottom:16 }}>Add merchant rule</div>
        <div style={{ marginBottom:14 }}>
          <div style={S.modalLabel}>If description contains</div>
          <input style={S.input} placeholder='e.g. "westside" or "centre church"' value={kw} onChange={e => setKw(e.target.value)} />
        </div>
        <div style={{ marginBottom:20 }}>
          <div style={S.modalLabel}>Categorize as</div>
          <select style={S.input} value={cat} onChange={e => setCat(e.target.value)}>
            {cats.map(c => <option key={c} value={c}>{getCat(c,mode,customCats).icon} {c}</option>)}
            {pickCats.length > 0 && (
              <optgroup label="Custom">
                {pickCats.map(c => <option key={c.id} value={c.name}>{c.icon} {c.name}</option>)}
              </optgroup>
            )}
          </select>
        </div>
        <div style={{ fontSize:12, color:C.stone, marginBottom:16 }}>Applies retroactively to all existing transactions and all future imports.</div>
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button style={S.btnGhost} onClick={onClose}>Cancel</button>
          <button style={S.btnPrimary(isBiz)} onClick={() => onSave(kw, cat, isBiz ? 'business' : 'household')}>Save rule</button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDeleteBatchModal({ batch, busy, onCancel, onConfirm }) {
  return (
    <div style={S.backdrop} onClick={busy?undefined:onCancel}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize:17, fontWeight:700, marginBottom:12 }}>Undo import?</div>
        <div style={{ fontSize:13, color:C.ink, marginBottom:20, lineHeight:1.6 }}>
          This will permanently delete {batch.imported_count} transaction{batch.imported_count===1?'':'s'} from{' '}
          <strong>{batch.filename}</strong>. This cannot be undone.
        </div>
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button style={S.btnGhost} onClick={onCancel} disabled={busy}>Cancel</button>
          <button style={{ ...S.btnPrimary(false), background:C.coral, borderColor:C.coral,
                           opacity:busy?0.6:1, cursor:busy?'not-allowed':'pointer' }}
                  disabled={busy} onClick={onConfirm}>
            {busy ? 'Deleting...' : 'Confirm Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDeleteEnvelopeModal({ envelope, onCancel, onConfirm }) {
  const n = parseInt(envelope.tx_count) || 0;
  return (
    <div style={S.backdrop} onClick={onCancel}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize:17, fontWeight:700, marginBottom:12 }}>Delete envelope?</div>
        <div style={{ fontSize:13, color:C.ink, marginBottom:20, lineHeight:1.6 }}>
          This deletes <strong>{envelope.name}</strong>.{' '}
          {n > 0
            ? <>Its {n} tagged transaction{n===1?'':'s'} will be <strong>untagged</strong>, not deleted — they return to normal spending and rejoin the Needs/Wants/Savings percentages.</>
            : <>No transactions are tagged to it.</>}
        </div>
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button style={S.btnGhost} onClick={onCancel}>Cancel</button>
          <button style={{ ...S.btnPrimary(false), background:C.coral, borderColor:C.coral }}
                  onClick={onConfirm}>Delete envelope</button>
        </div>
      </div>
    </div>
  );
}

// ─── DASHBOARD PANEL (shared by household + business) ─────────────────────────
function DashboardPanel({ txs, summary, mode, customCats, medTotal, onSelectTx, onOpenTxs, pendingCount, loading, trends = [], accounts = [], month, trendRange, onSetTrendRange, onSelectMonth, isMobile, settings = {}, filterBucket, filterKpi, envelopes = [] }) {
  const isBiz = mode === 'business';
  const accent = isBiz ? C.navy : C.sage;

  // isKpiIncomeRow/isKpiSpendingRow are shared with the transaction list's
  // click-to-audit filter (TxListPanel, filterKpi) — same predicate, so the
  // card and the filtered list summing to it can never drift apart.
  const income = txs.filter(t => isKpiIncomeRow(t, mode, customCats))
                    .reduce((s,t)=>s+parseFloat(t.amount),0);
  const spending = txs.filter(t => isKpiSpendingRow(t, mode, customCats))
                      .reduce((s,t)=>s+Math.abs(parseFloat(t.amount)),0);
  const savingsAmt = txs.filter(t => t.category==='Savings & Investing').reduce((s,t)=>s+Math.abs(parseFloat(t.amount)),0);
  // Cashflow is OPERATING cashflow: earned income minus spending. Gifts and
  // windfalls (gift type) genuinely arrive and still show in the transaction list
  // and account balances, but they are not operations, so they are deliberately
  // excluded here. This reverses the 2026-08-13 cf = income + giftInflow − spending,
  // which made the card read e.g. +$35,799.38 next to Income $1,939.82 / Spending
  // $1,140.44 — reconciling with neither. See TALLY_RULES.
  const cf = income - spending;
  const donations = txs.filter(t => t.category==='Gifts & Donations').reduce((s,t)=>s+Math.abs(parseFloat(t.amount)),0);

  // Ranked on |net| over expense- AND gift-typed categories — a gift outflow is
  // excluded from Spending/Cashflow (it's not operations) but the money still
  // left, so it belongs in the breakdown same as any other category. Values
  // stay SIGNED: a net-positive expense category is a refund month and renders
  // as a credit rather than disappearing — 29 category-months in this data
  // would otherwise silently vanish.
  const byCat = {};
  summary.filter(r => isExpenseCat(r.category, mode, customCats) || isGiftCat(r.category, mode, customCats))
         .forEach(r => { byCat[r.category] = parseFloat(r.total); });
  const sortedCats = Object.entries(byCat).sort((a,b)=>Math.abs(b[1])-Math.abs(a[1]));
  const maxCat = Math.abs(sortedCats[0]?.[1]||1);

  const byIncomeCat = {};
  summary.filter(r => isIncomeCat(r.category, mode, customCats))
         .forEach(r => { byIncomeCat[r.category] = parseFloat(r.total); });
  const sortedIncomeCats = Object.entries(byIncomeCat).sort((a,b)=>b[1]-a[1]);
  const maxIncomeCat = sortedIncomeCats[0]?.[1]||1;

  // Per-account monthly totals folded into the current view.
  const monthly = (() => {
    const byMonth = new Map();
    trends.forEach(r => {
      if (isBusinessAccount(r.account, accounts) !== isBiz) return;
      const cur = byMonth.get(r.month) || { spending:0, income:0 };
      cur.spending += parseFloat(r.spending||0);
      cur.income   += parseFloat(r.income||0);
      byMonth.set(r.month, cur);
    });
    return [...byMonth.entries()].sort((a,b)=>a[0].localeCompare(b[0])).map(([m,v]) => {
      const [yy,mm] = m.split('-');
      return { month: new Date(+yy, +mm-1).toLocaleString('en-US',{month:'short'}), y:+yy, mIdx:+mm-1,
               spending: Math.round(v.spending), income: Math.round(v.income) };
    });
  })();
  // Only set when the navigator's month falls inside the chart's current range —
  // ReferenceLine's x must match a category actually present in the data.
  const selectedMonthLabel = month ? monthly.find(d => d.y===month.y && d.mIdx===month.m)?.month : null;
  const goToMonth = d => onSelectMonth && onSelectMonth({ y:d.y, m:d.mIdx });
  const renderSpendDot = props => {
    const { cx, cy, payload } = props;
    return <circle key={`${payload.y}-${payload.mIdx}`} cx={cx} cy={cy} r={4} fill={CHART.spending}
                   style={{ cursor:'pointer' }} onClick={()=>goToMonth(payload)} />;
  };
  const renderActiveSpendDot = props => {
    const { cx, cy, payload } = props;
    return <circle key={`a-${payload.y}-${payload.mIdx}`} cx={cx} cy={cy} r={5} fill={CHART.spending}
                   style={{ cursor:'pointer' }} onClick={()=>goToMonth(payload)} />;
  };
  const curLabel = month ? monthLabel(month.y, month.m) : 'This month';
  const incomeVsSpend = [{ name: curLabel, Income: Math.round(income), Spending: Math.round(spending) }];
  const axisTick = { fill:C.stoneMid, fontSize:11 };
  const tipStyle = { borderRadius:10, border:`1px solid ${C.warmDark}`, fontSize:12 };

  const insights = [];
  // status, not category — this used to call the category setter with 'pending',
  // which matches no category and landed on an empty list.
  if (pendingCount>0) insights.push({ col:C.amber, title:`${pendingCount} transactions need review`, body:'E-transfers and flagged items waiting.', action:()=>onOpenTxs({ status:'pending' }) });
  // byCat values are signed now, so these read through Math.abs.
  const diningOut = Math.abs(byCat['Dining Out']||0);
  if (!isBiz && diningOut>300) insights.push({ col:C.blush, title:'Dining Out', body:`${fmt(diningOut)} on restaurants - ${Math.round(diningOut/spending*100)}% of spending.` });
  if (cf>0) insights.push({ col:C.mint, title:'Positive cashflow', body:`${fmt(cf)} more in than out.` });
  if (!isBiz && medTotal>0) insights.push({ col:C.gold, title:'Medical receipts', body:`${fmt(medTotal)} tagged. Threshold at 3% of $54K = $1,620.` });
  if (!isBiz && donations>0) insights.push({ col:C.lavender, title:'Charitable giving', body:`${fmt(donations)} in donations - counts toward T1 donation credits.` });
  const meals = Math.abs(byCat['Meals & Entertainment']||0);
  if (isBiz && meals>0) insights.push({ col:C.blush, title:'Meals & Entertainment', body:`${fmt(meals)} this month - remember only 50% is deductible.` });

  return (
    <>
      <div style={{ ...S.grid4, ...(isMobile ? { gridTemplateColumns:'1fr 1fr', gap:10 } : {}) }}>
        {[
          // onOpenTxs({kpi:'income'|'spending'}) routes through filterKpi, which
          // filters the transaction list with isKpiIncomeRow/isKpiSpendingRow —
          // the identical predicate this card's own total is computed from, so
          // the filtered list's sum can never drift from what's shown here.
          { label:'Income', val:income, color:accent, onClick:()=>onOpenTxs({ kpi:'income' }), active:filterKpi==='income' },
          { label:'Spending', val:spending, color:C.ink, onClick:()=>onOpenTxs({ kpi:'spending' }), active:filterKpi==='spending' },
          // Savings routes through the existing category filter rather than a
          // KPI mode — savingsAmt IS category==='Savings & Investing', nothing
          // more, so the general-purpose category filter already reproduces it
          // exactly with no new predicate to keep in sync.
          ...(isBiz ? [] : [{ label:'Savings', val:savingsAmt, color:accent, onClick:()=>onOpenTxs({ cat:'Savings & Investing' }) }]),
          // Cashflow is deliberately NOT clickable — it's income minus spending,
          // a derived number, not a set of rows. There is no single filter whose
          // rows sum to "cashflow"; summing income and spending rows together
          // would need a mixed sign convention no other filter in this list
          // uses, and would mislead more than it'd audit.
          { label:'Cashflow', val:cf, color:cf>=0?accent:C.coral, prefix:cf>0?'+':'' },
        ].map(k => (
          <div key={k.label} style={{ ...S.cardSm, ...(k.onClick ? { cursor:'pointer' } : {}),
                        ...(k.active ? { background:C.warm, border:`1px solid ${C.warmDeep}` } : {}) }}
               onClick={k.onClick}>
            <div style={S.kpiLabel}>{k.label}</div>
            <div style={{ ...S.kpiVal, color:k.color, ...(isMobile ? { fontSize:22 } : {}) }}>{k.prefix||''}{fmt(k.val)}</div>
            <div style={S.kpiSub}>{k.label==='Income'?'this month':k.label==='Savings'?'invested':k.label==='Cashflow'?(cf>=0?'positive':'negative'):'all accounts'}</div>
          </div>
        ))}
      </div>

      {/* Needs / Wants / Savings — household view only */}
      {!isBiz && (() => {
        const bucketAcc = { needs: 0, wants: 0, savings: 0 };
        txs.filter(t => isExpenseCat(t.category, mode, customCats) && !t.envelope_id)
           .forEach(t => {
             const b = t.bucket_override || catBucket(t.category, mode, customCats);
             if (b) bucketAcc[b] += parseFloat(t.amount);
           });
        const netSpend = {};
        BUCKETS.forEach(b => { netSpend[b] = -bucketAcc[b]; });
        const totalSpend = BUCKETS.reduce((s, b) => s + Math.max(netSpend[b], 0), 0);
        const BUCKET_COLORS = { needs: C.sky, wants: C.lavender, savings: C.sage };
        return (
          <div style={{ ...S.card, marginBottom:14 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <div style={S.secTitle}>Needs · Wants · Savings</div>
              <div style={{ fontSize:11, color:C.stoneMid }}>
                % of spending · target {settings.needs_pct||50}/{settings.wants_pct||30}/{settings.savings_pct||20}
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
              {BUCKETS.map(b => {
                const net = netSpend[b];
                const isCredit = net < 0;
                const target = settings[b + '_pct'] || 0;
                const pct = (totalSpend > 0 && net > 0) ? Math.round(net / totalSpend * 100) : 0;
                const over = pct > target;
                const barColor = over ? C.coral : BUCKET_COLORS[b];
                const active = filterBucket === b;
                return (
                  <div key={b}
                    style={{ padding:'10px 12px', borderRadius:10, cursor:'pointer',
                             background: active ? C.warm : 'transparent',
                             border: `1px solid ${active ? C.warmDeep : 'transparent'}` }}
                    onClick={() => onOpenTxs({ bucket: b })}>
                    <div style={{ fontSize:11, fontWeight:700, color:C.stoneMid,
                                  textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>
                      {BUCKET_LABELS[b]}
                    </div>
                    {isCredit ? (
                      <div style={{ fontSize:11, color:CHART.income }}>Net credit this period</div>
                    ) : net === 0 ? (
                      <div style={{ fontSize:11, color:C.stoneMid }}>No spending</div>
                    ) : (<>
                      <div style={{ fontSize:22, fontWeight:700, color: over ? C.coral : C.ink, lineHeight:1 }}>
                        {pct}%
                      </div>
                      <div style={{ fontSize:11, color: over ? C.coral : C.stoneMid, marginBottom:8 }}>
                        {fmt(net)} · target {target}%{over ? ' ▲' : ''}
                      </div>
                      <div style={{ position:'relative', height:9 }}>
                        <div style={{ position:'absolute', bottom:0, left:0, right:0, height:5,
                                      background:C.warmDark, borderRadius:3 }}>
                          <div style={{ height:'100%', width:`${Math.min(pct,100)}%`,
                                        background:barColor, borderRadius:3 }} />
                        </div>
                        {target > 0 && (
                          <div style={{ position:'absolute', bottom:0, left:`${Math.min(target,100)}%`,
                                        width:2, height:9, background:C.stoneMid, borderRadius:1,
                                        transform:'translateX(-50%)' }} />
                        )}
                      </div>
                    </>)}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Envelopes — funded pots. ALL-TIME by nature (funded once, drawn down over
          months), unlike the month-scoped card above — the "all time" label and
          the per-pot date span keep the two from being read as the same window.
          Tagged transactions have already left the bucket percentages above. */}
      {!isBiz && envelopes.filter(e => e.status==='open' && e.view!=='business').length > 0 && (
        <div style={{ ...S.card, marginBottom:14 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div style={S.secTitle}>Envelopes</div>
            <div style={{ fontSize:11, color:C.stoneMid }}>funded pots · all time</div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2,1fr)', gap:12 }}>
            {envelopes.filter(e => e.status==='open' && e.view!=='business').map(e => {
              const spent = parseFloat(e.spent||0);
              const budget = parseFloat(e.budget||0);
              const remaining = budget - spent;
              const over = spent > budget && budget > 0;
              const pct = budget > 0 ? Math.round(spent / budget * 100) : 0;
              const span = e.first_tagged
                ? `${fmtDate(e.first_tagged)} – ${fmtDate(e.last_tagged)}`
                : 'nothing tagged yet';
              return (
                <div key={e.id}
                  style={{ padding:'10px 12px', borderRadius:10, cursor:'pointer',
                           background:C.warm, border:`1px solid ${C.warmDeep}` }}
                  onClick={() => onOpenTxs({ envelope: e.id })}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:6 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:C.ink }}>{e.name}</div>
                    <div style={{ fontSize:11, color: over ? C.coral : C.stoneMid }}>{fmt(spent)} / {fmt(budget)}</div>
                  </div>
                  <div style={{ position:'relative', height:9, marginBottom:6 }}>
                    <div style={{ position:'absolute', bottom:0, left:0, right:0, height:5,
                                  background:C.warmDark, borderRadius:3 }}>
                      <div style={{ height:'100%', width:`${Math.min(pct,100)}%`,
                                    background: over ? C.coral : C.sage, borderRadius:3 }} />
                    </div>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:11 }}>
                    <span style={{ color: over ? C.coral : C.stoneMid }}>
                      {over ? `${fmt(-remaining)} over` : `${fmt(remaining)} left`}
                    </span>
                    <span style={{ color:C.stoneMid }}>{span}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ ...S.grid2, marginBottom:14, ...(isMobile ? { gridTemplateColumns:'1fr' } : {}) }}>
        <div style={S.card}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12, flexWrap:'wrap', gap:8 }}>
            <div style={{ ...S.secTitle, marginBottom:0 }}>Income &amp; spending · {TREND_RANGE_LABELS[trendRange] || '6 months'}</div>
            <div style={{ display:'flex', gap:6 }}>
              {Object.keys(TREND_RANGES).map(r => (
                <button key={r} style={{ ...S.pill, padding:'3px 10px', fontSize:11,
                           background:trendRange===r?accent:'white',
                           color:trendRange===r?'white':C.stone,
                           borderColor:trendRange===r?accent:C.warmDeep }}
                  onClick={()=>onSetTrendRange && onSetTrendRange(r)}>{r}</button>
              ))}
            </div>
          </div>
          {monthly.length===0
            ? <div style={{ color:C.stoneMid, fontSize:13, textAlign:'center', padding:'40px 0' }}>{loading?'Loading...':'No history yet'}</div>
            : <ResponsiveContainer width="100%" height={200}>
                <LineChart data={monthly} margin={{ top:8, right:8, left:-12, bottom:0 }}>
                  <CartesianGrid stroke={C.warmDark} vertical={false} />
                  <XAxis dataKey="month" tick={axisTick} axisLine={false} tickLine={false} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v,n)=>[fmt(v), n==='income'?'Income':'Spending']} contentStyle={tipStyle} />
                  <Legend wrapperStyle={{ fontSize:12, color:C.stone }}
                          formatter={n=>n==='income'?'Income':'Spending'} />
                  {selectedMonthLabel && <ReferenceLine x={selectedMonthLabel} stroke={C.warmDeep} strokeDasharray="4 4" />}
                  <Line type="monotone" dataKey="spending" stroke={CHART.spending} strokeWidth={2}
                        dot={renderSpendDot} activeDot={renderActiveSpendDot} />
                  <Line type="monotone" dataKey="income" stroke={CHART.income} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>}
        </div>

        <div style={S.card}>
          <div style={S.secTitle}>Income vs spending · {curLabel}</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={incomeVsSpend} margin={{ top:8, right:8, left:-12, bottom:0 }} barGap={2}>
              <CartesianGrid stroke={C.warmDark} vertical={false} />
              <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
              <YAxis tick={axisTick} axisLine={false} tickLine={false} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={v=>fmt(v)} contentStyle={tipStyle} />
              <Legend wrapperStyle={{ fontSize:12, color:C.stone }} />
              <Bar dataKey="Income" fill={CHART.income} radius={[4,4,0,0]} maxBarSize={64}>
                <LabelList dataKey="Income" position="top" formatter={v=>fmt(v)} style={{ fill:C.stone, fontSize:11 }} />
              </Bar>
              <Bar dataKey="Spending" fill={CHART.spending} radius={[4,4,0,0]} maxBarSize={64}>
                <LabelList dataKey="Spending" position="top" formatter={v=>fmt(v)} style={{ fill:C.stone, fontSize:11 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ ...S.grid2, ...(isMobile ? { gridTemplateColumns:'1fr' } : {}) }}>
        <div style={{ display:'flex', flexDirection:'column', gap:14, minWidth:0 }}>
        <div style={{ ...S.card, minWidth:0 }}>
          <div style={S.secTitle}>Spending by category</div>
          {sortedCats.length===0
            ? <div style={{ color:C.stoneMid, fontSize:13, textAlign:'center', padding:'20px 0' }}>{loading?'Loading...':'No data yet - upload a statement'}</div>
            : sortedCats.slice(0,8).map(([cat,net]) => {
                const cfg = getCat(cat, mode, customCats);
                // net > 0 means more came back than went out this month. Showing
                // it as a credit beats dropping the row, which is what the old
                // negative-totals-only rule did.
                const credit = net > 0;
                return (
                  <div key={cat} style={S.catRow} onClick={()=>onOpenTxs({ cat })}>
                    <div style={{ ...S.catIcon, background:cfg.light }}>{cfg.icon}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3, gap:8 }}>
                        <span style={{ fontSize:14, fontWeight:500 }}>{cat}</span>
                        <span style={{ fontSize:14, fontWeight:600, color:credit?CHART.income:'inherit', whiteSpace:'nowrap' }}>
                          {credit ? `+${fmt(net)}` : fmt(Math.abs(net))}
                        </span>
                      </div>
                      <div style={S.barWrap}><div style={{ ...S.bar, width:`${(Math.abs(net)/maxCat)*100}%`, background:credit?CHART.income:cfg.color }} /></div>
                    </div>
                  </div>
                );
              })}
        </div>

        <div style={{ ...S.card, minWidth:0 }}>
          <div style={S.secTitle}>Income by category</div>
          {sortedIncomeCats.length===0
            ? <div style={{ color:C.stoneMid, fontSize:13, textAlign:'center', padding:'20px 0' }}>{loading?'Loading...':'No income recorded this month'}</div>
            : sortedIncomeCats.map(([cat,amt]) => {
                const cfg = getCat(cat, mode, customCats);
                return (
                  <div key={cat} style={S.catRow} onClick={()=>onOpenTxs({ cat })}>
                    <div style={{ ...S.catIcon, background:cfg.light }}>{cfg.icon}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3, gap:8 }}>
                        <span style={{ fontSize:14, fontWeight:500 }}>{cat}</span>
                        <span style={{ fontSize:14, fontWeight:600, whiteSpace:'nowrap' }}>{fmt(amt)}</span>
                      </div>
                      <div style={S.barWrap}><div style={{ ...S.bar, width:`${(Math.abs(amt)/Math.abs(maxIncomeCat))*100}%`, background:CHART.income }} /></div>
                    </div>
                  </div>
                );
              })}
        </div>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:14, minWidth:0 }}>
          <div style={S.card}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <div style={S.secTitle}>Insights</div>
              {pendingCount>0 && <span style={{ fontSize:10, fontWeight:700, background:C.amberLight, color:C.amber, padding:'2px 7px', borderRadius:10 }}>{pendingCount} pending</span>}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {insights.length===0
                ? <div style={{ fontSize:13, color:C.stoneMid }}>All clear</div>
                : insights.map((ins,i) => (
                    <div key={i} style={{ ...S.insCard, cursor:ins.action?'pointer':'default' }} onClick={ins.action}>
                      <div style={{ width:8, height:8, borderRadius:'50%', background:ins.col, marginTop:4, flexShrink:0 }} />
                      <div>
                        <div style={{ fontSize:13, fontWeight:600, marginBottom:2 }}>{ins.title}</div>
                        <div style={{ fontSize:12, color:C.stone, lineHeight:1.5 }}>{ins.body}</div>
                      </div>
                    </div>
                  ))}
            </div>
          </div>

          <div style={S.card}>
            <div style={S.secTitle}>Recent transactions</div>
            {txs.slice(0,5).map((tx, i, arr) => {
              const cfg = getCat(tx.category, mode, customCats);
              const amt = parseFloat(tx.amount);
              return (
                <div key={tx.id} style={{ ...S.txRow, ...(isMobile ? { padding:'12px 6px' } : {}), ...(i===arr.length-1 ? { borderBottom:'none' } : {}) }} onClick={()=>onSelectTx(tx)}>
                  <div style={{ ...S.txIcon, background:cfg.light }}>{cfg.icon}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:5, minWidth:0 }}>
                      <div style={{ ...S.txName, ...(isMobile ? { maxWidth:'60vw' } : {}) }}>{tx.description}</div>
                      {Math.abs(amt) >= 2500 && <span title="Large amount" style={{ fontSize:10 }}>⚠️</span>}
                    </div>
                    <div style={S.txMeta}>{fmtDate(tx.date)} · {tx.account}</div>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:2 }}>
                    <div style={{ fontSize:15, fontWeight:700, color:amt>0?accent:C.ink }}>{amt>0?'+':''}{fmt(amt)}</div>
                    {tx.status==='pending' && <span style={S.pendBadge}>⏳ Review</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {!isBiz && (
        <MonthlyAnalysisCard mode={mode} customCats={customCats} accounts={accounts}
          envelopes={envelopes} settings={settings} month={month} onOpenTxs={onOpenTxs}
          onSelectMonth={onSelectMonth} />
      )}
    </>
  );
}

// ─── MONTHLY ANALYSIS CARD ────────────────────────────────────────────────────
// Read-only audit of the viewed month. It reports, it never fixes — a "fix all"
// button on a categorization audit is how a month gets bulk-mistaken. Runs
// automatically whenever the month changes; a Re-run button exists for after
// Cory fixes something, since nothing here re-checks itself.
function MonthlyAnalysisCard({ mode, customCats, accounts, envelopes, settings, month, onOpenTxs, onSelectMonth }) {
  const [loadingA, setLoadingA] = useState(false);
  const [result, setResult] = useState(null); // { activity, findings }
  const [errorA, setErrorA] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState({ rows: [], total: 0, page: 1, pages: 1 });

  const now = new Date();
  const isCurrentMonth = month.y === now.getFullYear() && month.m === now.getMonth();
  const isPastMonth = month.y < now.getFullYear() || (month.y === now.getFullYear() && month.m < now.getMonth());

  async function run() {
    setLoadingA(true); setErrorA(''); setSavedAt(null);
    try {
      // Forced show_hidden=true regardless of the page's own toggle — a
      // transfer-hidden bill payment still proves an account was touched, and
      // excluding it would false-flag check 1 on an account that's actually
      // current. Three prior months feed the trailing-average checks (8, 11);
      // fetched plain (no hidden) to match how income/spending are computed
      // everywhere else on the dashboard.
      const monthTxsAll = await api.getTxs(month.y, String(month.m + 1), true);
      const priorMonthsTxs = [];
      for (let i = 1; i <= 3; i++) {
        const d = new Date(month.y, month.m - i, 1);
        const txs = await api.getTxs(d.getFullYear(), String(d.getMonth() + 1), false);
        // A prior month with genuinely zero rows (before Tally had this
        // account, say) isn't a "$0 average month" — it's no data, and
        // averaging it in would understate the baseline. Skip it entirely.
        if (txs.length) priorMonthsTxs.push({ y: d.getFullYear(), m: d.getMonth(), txs });
      }
      // "As of this month's end" — NOT accounts' global last-activity, which
      // for a past month always reads as more-recent-than-the-window and
      // would make check 1 never fire. See server/index.js on this endpoint.
      const monthEndDate = new Date(month.y, month.m + 1, 0);
      const monthEndStr = monthEndDate.toISOString().slice(0, 10);
      const activityRows = await api.getAccountActivity(monthEndStr);
      const activityAsOf = new Map(activityRows.map(r => [r.account, r.last_date]));
      setResult(computeMonthlyFindings({
        monthTxsAll, priorMonthsTxs, accounts, activityAsOf, envelopes, settings, mode, customCats,
        month, isCurrentMonth,
      }));
    } catch (e) { setErrorA(e.message); }
    setLoadingA(false);
  }

  // accounts/envelopes/settings/customCats are fetched by the parent's own
  // loadAll() and can still be [] on this card's very first mount (the
  // Dashboard renders before that Promise.all resolves). Without them as
  // dependencies here, a fresh page load on the current month computes once
  // against an empty registry and never reruns once the real data arrives —
  // caught live: September showed no last-imported strip at all on a cold
  // load, only after clicking Re-run. accounts etc. only get a new reference
  // when loadAll() actually refetches, not on every render, so this can't loop.
  useEffect(() => { run(); }, [month.y, month.m, mode, accounts, envelopes, settings, customCats]);

  async function loadHistory(page) {
    try { setHistory(await api.getMonthlyAnalysisHistory(mode, page)); } catch (e) { console.error(e); }
  }
  useEffect(() => { if (showHistory) loadHistory(1); }, [showHistory, mode]);

  async function closeOutMonth() {
    if (!result) return;
    setSaving(true);
    try {
      await api.saveMonthlyAnalysis(month.y, month.m + 1, mode, result.findings);
      setSavedAt(new Date());
      if (showHistory) loadHistory(1);
    } catch (e) { setErrorA(e.message); }
    setSaving(false);
  }

  const activity = result?.activity || [];
  const findings = result?.findings || [];
  const spendingSuppressed = result?.spendingSuppressed || null;
  // Check 1's own findings are pulled out of the generic list and rendered as
  // the always-visible "last imported" strip below instead — the most common
  // real problem here is a missing statement, not a bad number, so it gets top
  // billing rather than being one more row in a list.
  const silentSet = new Set(findings.filter(f => f.key.startsWith('silent-')).map(f => f.key));
  const listFindings = findings.filter(f => !silentSet.has(f.key));
  // A suppression notice is itself something to report, not "nothing to see" —
  // so it's excluded from "clean" the same way an actual finding would be.
  const isClean = findings.length === 0 && !spendingSuppressed;
  const monthStr = monthLabel(month.y, month.m);

  // Freshest-first within the strip so a silent account isn't buried past a
  // dozen accounts that are perfectly current.
  const sortedActivity = [...activity].sort((a, b) =>
    new Date(a.last_transaction_date) - new Date(b.last_transaction_date));

  return (
    <div style={{ ...S.card, marginTop:14 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4, flexWrap:'wrap', gap:8 }}>
        <div style={S.secTitle}>Monthly Analysis · {monthStr}</div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {savedAt && <span style={{ fontSize:11, color:C.mint }}>Saved {savedAt.toLocaleTimeString()}</span>}
          {isPastMonth && (
            <button onClick={closeOutMonth} disabled={saving || loadingA || !result}
              style={{ ...S.btnGhost, fontSize:11, padding:'5px 10px', opacity:(saving||loadingA)?0.6:1 }}>
              {saving ? 'Saving...' : 'Close out this month'}
            </button>
          )}
          <button onClick={run} disabled={loadingA}
            style={{ ...S.btnGhost, fontSize:11, padding:'5px 10px', opacity:loadingA?0.6:1 }}>
            {loadingA ? 'Checking...' : 'Re-run'}
          </button>
        </div>
      </div>
      <div style={{ fontSize:11, color:C.stoneMid, marginBottom:12 }}>
        Read-only — reports what looks wrong, never fixes it.
      </div>

      {errorA && <div style={{ fontSize:12, color:C.coral, marginBottom:10 }}>{errorA}</div>}

      {/* Always visible: when each account was last touched. This is the single
          most common real problem (a missing statement, not a bad number), so
          it's a strip on its own rather than buried as one finding among many. */}
      {sortedActivity.length > 0 && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:14 }}>
          {sortedActivity.map(a => {
            const stale = silentSet.has(`silent-${a.account}`);
            return (
              <button key={a.account} onClick={() => onOpenTxs({ account: a.account })}
                style={{ ...S.pill, fontSize:11, padding:'5px 10px', cursor:'pointer',
                         background: stale ? C.coralLight : C.warm,
                         color: stale ? C.coral : C.stone,
                         borderColor: stale ? C.coral : C.warmDeep }}>
                {stale ? '⚠️ ' : ''}{a.account} · last {fmtDate(a.last_transaction_date)}
              </button>
            );
          })}
        </div>
      )}

      {loadingA && !result ? (
        <div style={{ fontSize:13, color:C.stoneMid, textAlign:'center', padding:'12px 0' }}>Checking {monthStr}...</div>
      ) : isClean ? (
        <div style={{ fontSize:13, color:C.mint, fontWeight:600 }}>{monthStr} looks clean.</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {['import', 'categorization', 'spending'].map(group => {
            const rows = listFindings.filter(f => f.group === group);
            const showSuppression = group === 'spending' && spendingSuppressed;
            if (!rows.length && !showSuppression) return null;
            return (
              <div key={group}>
                <div style={{ fontSize:11, fontWeight:700, color:C.stoneMid, textTransform:'uppercase',
                              letterSpacing:'0.06em', marginBottom:8 }}>
                  {GROUP_LABELS[group]}
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {showSuppression && (
                    <div style={{ ...S.insCard, cursor:'default' }}>
                      <div style={{ width:8, height:8, borderRadius:'50%', background:C.stoneMid, marginTop:4, flexShrink:0 }} />
                      <div style={{ fontSize:12, color:C.stone, lineHeight:1.5 }}>{spendingSuppressed.message}</div>
                    </div>
                  )}
                  {rows.map(f => (
                    <div key={f.key} style={{ ...S.insCard, cursor:'pointer' }} onClick={() => onOpenTxs(f.action)}>
                      <div style={{ width:8, height:8, borderRadius:'50%', background:C.amber, marginTop:4, flexShrink:0 }} />
                      <div>
                        <div style={{ fontSize:13, fontWeight:600, marginBottom:2 }}>{f.title}</div>
                        <div style={{ fontSize:12, color:C.stone, lineHeight:1.5 }}>{f.body}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop:14, borderTop:`1px solid ${C.warmDark}`, paddingTop:10 }}>
        <button onClick={() => setShowHistory(s => !s)}
          style={{ background:'none', border:'none', cursor:'pointer', color:C.stoneMid, fontSize:12, padding:0 }}>
          {showHistory ? '▾' : '▸'} History
        </button>
        {showHistory && (
          <div style={{ marginTop:10 }}>
            {history.rows.length === 0 ? (
              <div style={{ fontSize:12, color:C.stoneMid }}>No months closed out yet.</div>
            ) : (
              <>
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  {history.rows.map(r => (
                    <div key={`${r.year}-${r.month}`}
                      style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                               padding:'6px 8px', borderRadius:8, cursor:'pointer' }}
                      onClick={() => onSelectMonth && onSelectMonth({ y: r.year, m: r.month - 1 })}>
                      <span style={{ fontSize:12 }}>{monthLabel(r.year, r.month - 1)}</span>
                      <span style={{ fontSize:12, color: r.finding_count > 0 ? C.amber : C.mint }}>
                        {r.finding_count === 0 ? 'Clean' : `${r.finding_count} finding${r.finding_count===1?'':'s'}`}
                        <span style={{ color:C.stoneMid, marginLeft:8 }}>{fmtDate(r.generated_at)}</span>
                      </span>
                    </div>
                  ))}
                </div>
                {history.pages > 1 && (
                  <div style={{ display:'flex', gap:8, marginTop:8, fontSize:11 }}>
                    <button disabled={history.page<=1} onClick={()=>loadHistory(history.page-1)} style={{ ...S.btnGhost, padding:'3px 8px', fontSize:11 }}>Prev</button>
                    <span style={{ color:C.stoneMid }}>Page {history.page} of {history.pages}</span>
                    <button disabled={history.page>=history.pages} onClick={()=>loadHistory(history.page+1)} style={{ ...S.btnGhost, padding:'3px 8px', fontSize:11 }}>Next</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── TX LIST PANEL (shared) ───────────────────────────────────────────────────
function TxListPanel({ txs, hiddenTxs, mode, customCats, loading, canEdit, filterCat, filterAccount, filterStatus, filterBucket, filterEnvelope, filterKpi, filterIds, envelopes = [], monthLabelStr, search, showHidden, pendingCount, openTxId, page, perPage, onSetPage, onSetPerPage, onSelect, onConfirm, onConfirmAll, onSetFilter, onSetAccount, onSetStatus, onSetSearch, onToggleHidden, sortedCats, incomeCats, isMobile, sortBy, sortDir, onSetSort, onClearBucket, onClearEnvelope, onClearKpi, onClearIds }) {
  const isBiz = mode === 'business';
  const accent = isBiz ? C.navy : C.sage;

  // Every account with activity this month, taken from the unfiltered list so the
  // row doesn't shrink as you narrow by status or category.
  const baseTxs = showHidden ? [...txs, ...hiddenTxs] : txs;
  const accounts = [...new Set(baseTxs.map(t => t.account))].sort();

  // Capped at 8 to match the dashboard's own slice(0,8) — a category clicked
  // there must have a pill here to highlight, or the filter looks like it failed.
  const activePills = sortedCats.map(c => c[0]).slice(0, 8);
  // Income categories with activity join the overflow rather than taking a pill
  // slot, so adding income never widens the pill row.
  const otherActive = [...sortedCats.map(c => c[0]).slice(8), ...(incomeCats||[]).map(c => c[0])];
  // Everything selectable, hidden categories included. Built from the full maps
  // rather than from this month's rows so a category with no activity is still
  // reachable, and de-duped against the custom list since a custom name can
  // shadow a built-in one.
  const allCats = (() => {
    const builtIn = mode === 'business' ? ALL_BIZ_CATS : ALL_CATS;
    const custom = visibleCustomCats(customCats, mode).map(c => c.name);
    return [...new Set([...builtIn, ...custom])].sort();
  })();

  const filteredTxs = (() => {
    let list = baseTxs;
    if (filterStatus==='pending') list=list.filter(t=>t.status==='pending');
    if (filterStatus==='confirmed') list=list.filter(t=>t.status==='confirmed');
    // Keep the row whose modal is open even once re-categorizing makes it stop
    // matching, so it doesn't vanish from under the dialog mid-edit.
    if (filterCat!=='All') list=list.filter(t=>t.category===filterCat || t.id===openTxId);
    if (filterBucket) list=list.filter(t => {
      const b = t.bucket_override || catBucket(t.category, mode, customCats);
      return b === filterBucket;
    });
    if (filterEnvelope) list=list.filter(t => t.envelope_id===filterEnvelope || t.id===openTxId);
    // Same predicates the KPI cards sum (isKpiIncomeRow/isKpiSpendingRow), so
    // this list's total can never drift from the number it's auditing. Each
    // predicate checks !transfer_hide itself, so toggling "Show hidden" while
    // a KPI filter is active has no effect — hidden rows never counted toward
    // the KPI in the first place, so they must not appear here either.
    if (filterKpi==='income') list=list.filter(t => isKpiIncomeRow(t, mode, customCats) || t.id===openTxId);
    if (filterKpi==='spending') list=list.filter(t => isKpiSpendingRow(t, mode, customCats) || t.id===openTxId);
    if (filterAccount!=='All') list=list.filter(t=>t.account===filterAccount);
    // Points a finding at exact rows (a near-duplicate pair, a specific sign-bug
    // row) rather than the nearest broad category — a finding Cory can't click
    // into precisely is one he won't act on.
    if (filterIds) list=list.filter(t=>filterIds.has(t.id) || t.id===openTxId);
    if (search) list=list.filter(t=>
      t.description.toLowerCase().includes(search.toLowerCase())||
      t.category.toLowerCase().includes(search.toLowerCase())||
      t.account.toLowerCase().includes(search.toLowerCase())
    );
    // Sort here, inside filteredTxs, so pagination below slices an already-sorted
    // list. Sorting after the slice would reorder only the visible page and leave
    // page 2 holding entirely the wrong rows.
    // amount arrives as a NUMERIC string from Postgres, so compare parsed.
    const dir = sortDir === 'asc' ? 1 : -1;
    const cmp =
      sortBy === 'description' ? (a,b) => dir * (a.description||'').localeCompare(b.description||'')
    : sortBy === 'amount'      ? (a,b) => dir * (parseFloat(a.amount) - parseFloat(b.amount))
    : sortBy === 'amountAbs'   ? (a,b) => dir * (Math.abs(parseFloat(a.amount)) - Math.abs(parseFloat(b.amount)))
    :                            (a,b) => dir * a.date.localeCompare(b.date);
    return [...list].sort(cmp);
  })();

  // Paginate the *filtered* result — slicing first would leave empty pages
  // whenever a filter excludes a whole page's worth.
  const totalPages = Math.max(1, Math.ceil(filteredTxs.length / perPage));
  // Guards the render between a filter narrowing the list and the reset effect
  // firing, when `page` can still point past the end.
  const safePage = Math.min(page, totalPages);
  const pageTxs = (() => {
    const slice = filteredTxs.slice((safePage - 1) * perPage, safePage * perPage);
    if (!openTxId || slice.some(t => t.id === openTxId)) return slice;
    // D1: the open row survived filtering but fell off this page. Append it, or
    // the modal sits over a list where its row has vanished — the exact problem
    // the filter exemption in e2af4af was added to prevent.
    const open = filteredTxs.find(t => t.id === openTxId);
    return open ? [...slice, open] : slice;
  })();

  const listRef = useRef(null);
  const goPage = p => {
    onSetPage(p);
    // Only on explicit navigation — an effect on `page` would also fire on mount.
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <div style={{ display:'flex', gap:6, marginBottom:10, flexWrap:'wrap' }}>
        {[['all','All'],['pending',`⏳ Pending${pendingCount>0?` (${pendingCount})`:''}`],['confirmed','✅ Confirmed']].map(([v,l]) => (
          <button key={v} onClick={()=>onSetStatus(v)}
            style={{ ...S.pill, background:filterStatus===v?accent:'white', color:filterStatus===v?'white':C.stone, borderColor:filterStatus===v?accent:C.warmDeep }}>{l}</button>
        ))}
      </div>

      {filterBucket && (
        <div style={{ display:'flex', gap:6, marginBottom:10, alignItems:'center' }}>
          <span style={{ fontSize:11, color:C.stoneMid }}>Bucket filter:</span>
          <span style={{ ...S.pill, fontSize:11, padding:'3px 10px', background:C.warm,
                         color:C.ink, borderColor:C.warmDeep, cursor:'default' }}>
            {BUCKET_LABELS[filterBucket]}
          </span>
          <button onClick={onClearBucket}
            style={{ background:'none', border:'none', cursor:'pointer', color:C.stoneMid,
                     fontSize:18, lineHeight:1, padding:'0 4px', fontFamily:'inherit' }}>×</button>
        </div>
      )}

      {filterKpi && (
        <div style={{ display:'flex', gap:6, marginBottom:10, alignItems:'center' }}>
          <span style={{ fontSize:11, color:C.stoneMid }}>KPI filter:</span>
          <span style={{ ...S.pill, fontSize:11, padding:'3px 10px', background:C.warm,
                         color:C.ink, borderColor:C.warmDeep, cursor:'default' }}>
            {filterKpi==='income' ? 'Income' : 'Spending'} · {monthLabelStr || 'this month'}
          </span>
          <button onClick={onClearKpi}
            style={{ background:'none', border:'none', cursor:'pointer', color:C.stoneMid,
                     fontSize:18, lineHeight:1, padding:'0 4px', fontFamily:'inherit' }}>×</button>
        </div>
      )}

      {filterEnvelope && (
        <div style={{ display:'flex', gap:6, marginBottom:10, alignItems:'center', flexWrap:'wrap' }}>
          <span style={{ fontSize:11, color:C.stoneMid }}>Envelope filter:</span>
          <span style={{ ...S.pill, fontSize:11, padding:'3px 10px', background:C.warm,
                         color:C.ink, borderColor:C.warmDeep, cursor:'default' }}>
            ✉️ {(envelopes.find(e => e.id===filterEnvelope)||{}).name || 'Envelope'} · all months
          </span>
          <button onClick={onClearEnvelope}
            style={{ background:'none', border:'none', cursor:'pointer', color:C.stoneMid,
                     fontSize:18, lineHeight:1, padding:'0 4px', fontFamily:'inherit' }}>×</button>
          {/* The list spans all months, but the pills + pending count below still
              reflect the selected month (list-only scope). Say so, or the mix reads
              as a bug. */}
          <span style={{ fontSize:11, color:C.stoneMid }}>
            · category pills &amp; pending still reflect {monthLabelStr || 'the selected month'}
          </span>
        </div>
      )}

      {filterIds && (
        <div style={{ display:'flex', gap:6, marginBottom:10, alignItems:'center' }}>
          <span style={{ fontSize:11, color:C.stoneMid }}>Flagged rows:</span>
          <span style={{ ...S.pill, fontSize:11, padding:'3px 10px', background:C.amberLight,
                         color:C.amber, borderColor:C.amber, cursor:'default' }}>
            {filterIds.size} row{filterIds.size===1?'':'s'} from Monthly Analysis
          </span>
          <button onClick={onClearIds}
            style={{ background:'none', border:'none', cursor:'pointer', color:C.stoneMid,
                     fontSize:18, lineHeight:1, padding:'0 4px', fontFamily:'inherit' }}>×</button>
        </div>
      )}

      {/* Pills cover the categories with activity — the fast path, and the same
          top-8 the dashboard shows, so a category clicked there always has a pill
          to highlight. The dropdown beside them is the only way to reach the rest:
          sortedCats is built from NEGATIVE monthly totals only, so it silently
          omits every hidden category, everything with no activity this month, and
          anything past the cap.
          Income still gets no PILL for that same reason — a positive total never
          enters sortedCats — so it is reachable only from the full list below.
          Making income a first-class concept rather than "a category that happens
          to be positive" is Stage 6 work, alongside the category type field. */}
      <div style={{ display:'flex', gap:6, marginBottom:10, flexWrap:'wrap', alignItems:'center' }}>
        {['All',...activePills].map(cat => (
          <button key={cat} onClick={()=>onSetFilter(cat)}
            style={{ ...S.pill, background:filterCat===cat?C.ink:'white', color:filterCat===cat?'white':C.stone, borderColor:filterCat===cat?C.ink:C.warmDeep, fontSize:12 }}>
            {cat==='All'?'All categories':(getCat(cat,mode,customCats).icon+' '+cat)}
          </button>
        ))}
        <select value={filterCat==='All' || activePills.includes(filterCat) ? '' : filterCat}
          onChange={e=>{
            const cat = e.target.value;
            if (!cat) return;
            onSetFilter(cat);
            // A hidden category has no rows in the default list, so picking one
            // would show an empty list and read as broken. Flip the visible
            // checkbox rather than silently widening the filter, so it stays
            // obvious why hidden rows appeared.
            if (cat!=='All' && getCat(cat,mode,customCats).hidden && !showHidden) onToggleHidden(true);
          }}
          style={{ ...S.pill, fontSize:12, width:'auto', padding:'5px 8px', cursor:'pointer',
                   background:activePills.includes(filterCat)||filterCat==='All'?'white':C.ink,
                   color:activePills.includes(filterCat)||filterCat==='All'?C.stone:'white',
                   borderColor:activePills.includes(filterCat)||filterCat==='All'?C.warmDeep:C.ink }}>
          <option value="">More…</option>
          <option value="All">All categories</option>
          {otherActive.length > 0 && (
            <optgroup label="With activity">
              {otherActive.map(c => <option key={c} value={c}>{getCat(c,mode,customCats).icon+' '+c}</option>)}
            </optgroup>
          )}
          <optgroup label="All categories">
            {allCats.map(c => <option key={c} value={c}>{getCat(c,mode,customCats).icon+' '+c}</option>)}
          </optgroup>
        </select>
      </div>

      {accounts.length > 1 && (
        <div style={{ display:'flex', gap:6, marginBottom:10, flexWrap:'wrap' }}>
          {['All', ...accounts].map(acct => (
            <button key={acct} onClick={()=>onSetAccount(acct)}
              style={{ ...S.pill, background:filterAccount===acct?C.ink:'white', color:filterAccount===acct?'white':C.stone, borderColor:filterAccount===acct?C.ink:C.warmDeep, fontSize:12 }}>
              {acct==='All'?'All accounts':acct}
            </button>
          ))}
        </div>
      )}

      <div style={{ position:'relative', marginBottom:10 }}>
        <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:C.stoneMid, fontSize:13 }}>🔍</span>
        <input style={{ ...S.input, paddingLeft:36 }} placeholder="Search transactions..." value={search} onChange={e=>onSetSearch(e.target.value)} />
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10, gap:8, flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ fontSize:12, color:C.stoneMid }}>
            {filteredTxs.length} transaction{filteredTxs.length===1?'':'s'}
            {/* Shown for every filter, not just filterKpi — a filtered subtotal
                (this account, this category, this search) is generically useful,
                and for filterKpi specifically it's what makes the number
                verifiable at a glance: it must equal the KPI card exactly, or
                the audit this feature exists for is broken. Sign convention
                mirrors whichever KPI is being audited (Income: plain positive;
                Spending: Math.abs, since that's how the card itself sums it);
                for every other filter it follows the same signed/credit
                convention as the category breakdown above. */}
            {filteredTxs.length > 0 && (() => {
              const sum = filteredTxs.reduce((s,t)=>s+parseFloat(t.amount),0);
              const text = filterKpi==='income' ? fmt(sum)
                : filterKpi==='spending' ? fmt(Math.abs(sum))
                : sum > 0 ? `+${fmt(sum)}` : fmt(Math.abs(sum));
              return <> · <strong style={{ color:C.ink }}>{text}</strong></>;
            })()}
          </div>
          <select value={sortBy} onChange={e=>onSetSort(e.target.value, sortDir)}
            style={{ ...S.input, width:'auto', fontSize:11, padding:'3px 6px' }}>
            <option value="date">Date</option>
            <option value="description">Description</option>
            <option value="amount">Amount</option>
            <option value="amountAbs">Amount (size)</option>
          </select>
          <button onClick={()=>onSetSort(sortBy, sortDir==='asc'?'desc':'asc')}
            title={sortDir==='asc'?'Ascending':'Descending'}
            style={{ ...S.btnSm, padding:'3px 8px', fontSize:11 }}>{sortDir==='asc'?'↑':'↓'}</button>
          {filteredTxs.length > 25 && (
            <select value={perPage} onChange={e=>{ onSetPerPage(Number(e.target.value)); onSetPage(1); }}
              style={{ ...S.input, width:'auto', fontSize:11, padding:'3px 6px' }}>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
            </select>
          )}
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {canEdit && filterStatus==='pending' && pendingCount>0 && (
            <button style={{ ...S.btnSm, background:accent, color:'white', border:'none' }} onClick={onConfirmAll}>Confirm all {pendingCount}</button>
          )}
          <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:C.stone, cursor:'pointer' }}>
            <input type="checkbox" checked={showHidden} onChange={e=>onToggleHidden(e.target.checked)} /> Show hidden
          </label>
        </div>
      </div>

      <div ref={listRef} style={{ ...S.card, padding:'6px 14px', scrollMarginTop: isMobile ? 100 : 70 }}>
        {loading
          ? <div style={{ textAlign:'center', padding:32, color:C.stoneMid }}>Loading...</div>
          : filteredTxs.length===0
          ? <div style={{ textAlign:'center', padding:32, color:C.stoneMid, fontSize:13 }}>No transactions match your filters</div>
          : pageTxs.map((tx, i, arr) => {
              const cfg = getCat(tx.category, mode, customCats);
              // Brand palette pills render unchanged; darkening is only for a
              // user's own color pick, which can land pale enough to need it.
              const isCustomCat = customCats.some(c => c.name === tx.category);
              const pillColor = isCustomCat ? pillTextColor(cfg.color) : cfg.color;
              const amt = parseFloat(tx.amount);
              // Only flag rows in the Pending view -- in All/Confirmed the border
              // would be noise rather than a signal.
              const flagPending = filterStatus==='pending' && tx.status==='pending';
              return (
                <div key={tx.id} onClick={()=>onSelect(tx)}
                  style={{ ...S.txRow, opacity:tx.transfer_hide?.5:1,
                    ...(isMobile ? { padding:'12px 6px' } : {}),
                    ...(i===arr.length-1 ? { borderBottom:'none' } : {}),
                    ...(flagPending ? { borderLeft:`3px solid ${accent}`, paddingLeft:3 } : {}) }}>
                  <div style={{ ...S.txIcon, background:cfg.light }}>{cfg.icon}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:5, minWidth:0 }}>
                      <div style={{ ...S.txName, ...(isMobile ? { maxWidth:'60vw' } : {}) }}>{tx.description}</div>
                      {Math.abs(parseFloat(tx.amount)) >= 2500 && <span title="Large amount" style={{ fontSize:10 }}>⚠️</span>}
                      {tx.tax_tag==='medical' && <span style={{ fontSize:10, background:C.mintLight, padding:'1px 4px', borderRadius:6 }}>🏥</span>}
                      {tx.tax_tag==='deductible' && <span style={{ fontSize:10, background:C.goldLight, padding:'1px 4px', borderRadius:6 }}>💼</span>}
                      {tx.tax_tag==='50pct' && <span style={{ fontSize:10, background:C.blushLight, padding:'1px 4px', borderRadius:6 }}>50%</span>}
                      {tx.transfer_hide && <span style={{ fontSize:9, color:C.stoneMid, background:C.warmDark, padding:'1px 5px', borderRadius:8 }}>hidden</span>}
                      {tx.trend_exclude && <span title="Excluded from the trend chart — still counted in this month's totals and buckets" style={{ fontSize:9, color:C.gold, background:C.goldLight, padding:'1px 5px', borderRadius:8 }}>off-trend</span>}
                    </div>
                    <div style={S.txMeta}>{fmtDate(tx.date)} · {tx.account}</div>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:3 }}>
                    <div style={{ fontSize:15, fontWeight:700, color:amt>0?accent:C.ink }}>{amt>0?'+':''}{fmt(amt)}</div>
                    <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                      <span style={{ fontWeight:700, background:cfg.light, color:pillColor, borderRadius:12, ...(isMobile ? { fontSize:12, padding:'5px 10px' } : { fontSize:11, padding:'3px 8px' }) }}>{tx.category}</span>
                      {canEdit && tx.status==='pending' && (() => {
                        // Not disabled — a dead button is a worse answer than one
                        // that opens the row so the category can be picked.
                        const needsCat = isUncategorized(tx.category);
                        return (
                          <button
                            title={needsCat ? 'Categorize first — opens the transaction' : 'Mark as confirmed'}
                            style={{ ...S.cfBtn, background:needsCat?C.warmDeep:accent, color:needsCat?C.stone:'white',
                                     ...(isMobile ? { padding:'8px 14px', fontSize:13 } : {}) }}
                            onClick={e=>{ e.stopPropagation(); needsCat ? onSelect(tx) : onConfirm(tx.id); }}>
                            {needsCat ? 'Categorize' : '✓ Confirm'}
                          </button>
                        );
                      })()}
                      {tx.status==='confirmed'&&!tx.transfer_hide && <span style={{ fontSize:10, color:accent }}>✓</span>}
                    </div>
                  </div>
                </div>
              );
            })}
        {!loading && totalPages > 1 && (
          <div style={{ display:'flex', alignItems:'center', gap:10, paddingTop:10, marginTop:4,
                        borderTop:`1px solid ${C.warmDark}`,
                        justifyContent: isMobile ? 'center' : 'flex-end' }}>
            <button disabled={safePage<=1} onClick={()=>goPage(safePage-1)}
              style={{ ...S.btnGhost, opacity:safePage<=1?0.5:1, cursor:safePage<=1?'not-allowed':'pointer',
                       ...(isMobile ? { padding:'12px 20px', fontSize:13 } : {}) }}>Prev</button>
            <div style={{ fontSize:12, color:C.stone }}>Page {safePage} of {totalPages}</div>
            <button disabled={safePage>=totalPages} onClick={()=>goPage(safePage+1)}
              style={{ ...S.btnGhost, opacity:safePage>=totalPages?0.5:1, cursor:safePage>=totalPages?'not-allowed':'pointer',
                       ...(isMobile ? { padding:'12px 20px', fontSize:13 } : {}) }}>Next</button>
          </div>
        )}
      </div>

      {hiddenTxs.length>0 && (
        <div style={{ marginTop:16 }}>
          <div style={{ fontSize:11, color:C.stoneMid, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>
            Stored transfers & payments - hidden from spending ({hiddenTxs.length})
          </div>
          <div style={{ ...S.card, padding:'4px 14px', opacity:.65 }}>
            {(showHidden?hiddenTxs:hiddenTxs.slice(0,4)).map((tx, i, arr) => (
              <div key={tx.id} style={{ ...S.txRow, ...(isMobile ? { padding:'12px 6px' } : {}), ...(i===arr.length-1 ? { borderBottom:'none' } : {}) }} onClick={()=>onSelect(tx)}>
                <div style={{ ...S.txIcon, background:C.warmDark }}>↔️</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, color:C.stone, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', ...(isMobile ? {} : { maxWidth:260 }) }}>{tx.description}</div>
                  <div style={S.txMeta}>{fmtDate(tx.date)} · {tx.account}</div>
                </div>
                <div style={{ fontSize:13, fontWeight:500, color:C.stoneMid }}>{fmt(parseFloat(tx.amount))}</div>
              </div>
            ))}
            {!showHidden&&hiddenTxs.length>4 && (
              <div style={{ fontSize:12, color:C.stoneMid, padding:'8px 0', textAlign:'center', cursor:'pointer' }} onClick={()=>onToggleHidden(true)}>
                + {hiddenTxs.length-4} more
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
// ─── UPLOAD PREVIEW ───────────────────────────────────────────────────────────
function UploadPreviewModal({ preview, accounts, onCancel, onImport, busy }) {
  const [overrides, setOverrides] = useState(() => preview.files.map(() => ({})));
  // Rows unchecked in the review-matches section below, keyed "fi:id" — a Set of
  // exclusions rather than inclusions, so a file with no matches never touches this.
  const [excluded, setExcluded] = useState(() => new Set());
  // Gate on the review section itself, not a global "I agree" — ticking it is
  // only possible once every flagged row's checkbox has been looked at, matching
  // the "deliberate click, not a blanket refusal" ask.
  const [reviewed, setReviewed] = useState(false);
  // Which file indices the operator has explicitly clicked "Import anyway" for,
  // after seeing a source-overlap conflict below — never set speculatively.
  const [forcedFiles, setForcedFiles] = useState(() => new Set());
  const BADGE = { high:{bg:C.mintLight,fg:C.mint,l:'High'},
                  low:{bg:C.amberLight,fg:C.amber,l:'Low'},
                  unknown:{bg:C.coralLight,fg:C.coral,l:'Unknown'} };

  const chosen = (fi, det) => overrides[fi][det] || det;
  const setAcct = (fi, det, val) => setOverrides(p => p.map((m,i) => i===fi ? {...m,[det]:val} : m));
  // Every unknown row must be resolved explicitly before Import unlocks.
  const acctBlocked = preview.files.some((f,fi) =>
    f.accounts.some(a => a.confidence==='unknown' && !overrides[fi][a.detected]));
  const total = preview.files.reduce((s,f) => s + f.transaction_count, 0);

  // Flattened across files — one list to review, not one warning per file to
  // notice separately. "unconfirmed match": same date/amount/account as a row
  // already on file. Deliberately NOT "duplicate" — that word is reserved for
  // the ON CONFLICT case the system already handles on its own.
  //
  // Two different things can produce that tuple match, and only one of them
  // needs a human: `same_wording` (the server compared the row's would-be id,
  // not raw text — see server/index.js) means ON CONFLICT will silently skip
  // this row on import regardless of what the operator does here, so there is
  // nothing to decide. A file where every match is same_wording carries
  // `already_imported` naming the prior upload instead of listing each row.
  const matches = preview.files.flatMap((f, fi) =>
    (f.possible_duplicates || []).map(d => ({ ...d, fi, filename: f.filename })));
  // Rows still needing a judgment call: genuinely different wording, and not
  // already covered by a file-level "already imported" banner.
  const needsReview = matches.filter(m => !m.same_wording);
  // Same charge, identical wording — informational, listed unless the whole
  // file already got the single combined banner treatment.
  const autoSkip = matches.filter(m => m.same_wording && !preview.files[m.fi].already_imported);
  const alreadyImportedFiles = preview.files
    .map((f, fi) => f.already_imported ? { fi, filename: f.filename, ...f.already_imported } : null)
    .filter(Boolean);
  const toggleRow = (fi, id) => setExcluded(prev => {
    const next = new Set(prev);
    const key = `${fi}:${id}`;
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const matchBlocked = needsReview.length > 0 && !reviewed;

  // Source-overlap refusal — a fact, not a judgment call, so it's a hard block
  // per file rather than a per-row review list.
  const conflictsByFile = preview.files.map(f =>
    f.accounts.map(a => a.source_conflict).filter(Boolean));
  const conflictBlocked = conflictsByFile.some((cs, fi) => cs.length > 0 && !forcedFiles.has(fi));

  const blocked = acctBlocked || matchBlocked || conflictBlocked;
  const importCount = total - excluded.size;

  const handleImport = () => {
    const excludedIds = preview.files.map((f, fi) =>
      (f.possible_duplicates || []).filter(d => excluded.has(`${fi}:${d.id}`)).map(d => d.id));
    const forceImport = preview.files.map((f, fi) => forcedFiles.has(fi));
    onImport(overrides, excludedIds, forceImport);
  };

  return (
    <div style={S.backdrop} onClick={busy?undefined:onCancel}>
      <div style={{ ...S.modal, maxWidth:680 }} onClick={e=>e.stopPropagation()}>
        <div style={{ fontSize:17, fontWeight:700, marginBottom:4 }}>Confirm import</div>
        <div style={{ fontSize:12, color:C.stone, marginBottom:16 }}>
          {preview.files.length} file{preview.files.length===1?'':'s'} · {total} transactions found.
          Check the account for each before importing.
        </div>

        {preview.files.map((f, fi) => {
          const b = BADGE[f.confidence];
          return (
            <div key={f.filename+fi} style={{ border:`1px solid ${f.confidence==='unknown'?C.coral:C.warmDark}`,
                       background:f.confidence==='unknown'?C.coralLight:'white',
                       borderRadius:12, padding:'12px 14px', marginBottom:10 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                <div style={{ flex:1, fontSize:13, fontWeight:600, wordBreak:'break-all' }}>{f.filename}</div>
                <span style={{ fontSize:10, fontWeight:700, background:b.bg, color:b.fg, padding:'3px 8px', borderRadius:10 }}>{b.l}</span>
              </div>
              <div style={{ fontSize:11, color:C.stoneMid, marginBottom:10 }}>
                {f.detected_format==='unknown' ? 'Format not recognized' : f.detected_format}
                {f.detected_period ? ` · ${f.detected_period}` : ''} · {f.transaction_count} transactions
                {f.error ? ` · ${f.error}` : ''}
              </div>

              {f.accounts.length===0
                ? <div style={{ fontSize:12, color:C.coral }}>No transactions found — this file will be skipped.</div>
                : f.accounts.map(a => {
                    const val = chosen(fi, a.detected);
                    const biz = isBusinessAccount(val, accounts);
                    return (
                      <div key={a.detected} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
                        <div style={{ fontSize:11, color:C.stoneMid, flex:'1 1 110px' }}>{a.detected}</div>
                        <select value={overrides[fi][a.detected] || ''} onChange={e=>setAcct(fi, a.detected, e.target.value)}
                          style={{ ...S.input, width:'auto', flex:'2 1 180px', fontSize:12, padding:'6px 9px',
                                   borderColor: a.confidence==='unknown' && !overrides[fi][a.detected] ? C.coral : C.warmDeep }}>
                          <option value="">{a.confidence==='unknown' ? '— select an account —' : `Keep "${a.detected}"`}</option>
                          {preview.registry.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                        <span style={{ fontSize:11, color:C.stoneMid, minWidth:92 }}>{biz?'💼 Business':'🏠 Household'}</span>
                      </div>
                    );
                  })}

              {/* Closing balances this file carries. Shown before any write, on
                  the same principle as the parsed period and count: a number
                  about to be stored is one the operator should see first. Only
                  the Vancity chequing PDF and CSV report any — a card statement
                  prints no running balance, so this block simply won't appear. */}
              {(f.balances || []).length > 0 && (
                <div style={{ marginTop:10, paddingTop:10, borderTop:`1px solid ${C.warmDark}` }}>
                  <div style={{ fontSize:10, fontWeight:700, color:C.stoneMid, textTransform:'uppercase',
                                letterSpacing:'.05em', marginBottom:6 }}>
                    Closing balance{f.balances.length===1?'':'s'}
                  </div>
                  {f.balances.map(bal => (
                    <div key={bal.account} style={{ display:'flex', justifyContent:'space-between', gap:8,
                                                    fontSize:11, marginBottom:3 }}>
                      {/* The name the parser detected. If it's reassigned above,
                          the balance follows the reassignment on import. */}
                      <span style={{ color:C.stoneMid }}>{chosen(fi, bal.account)}</span>
                      <span style={{ fontWeight:600 }}>
                        {fmt(Number(bal.balance))} <span style={{ color:C.stoneMid, fontWeight:400 }}>as of {bal.as_of}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {preview.files.map((f, fi) => conflictsByFile[fi].length > 0 && (
          <div key={'conflict'+fi} style={{ border:`2px solid ${C.coral}`, background:C.coralLight, borderRadius:12,
                        padding:'14px 16px', marginTop:4, marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.coral, marginBottom:4 }}>
              🚫 Import blocked — {f.filename}
            </div>
            {conflictsByFile[fi].map((c, ci) => (
              <div key={ci} style={{ fontSize:11, color:C.stone, marginBottom:8 }}>
                <strong>{c.account}</strong>: this file covers {c.incoming_period.start} to {c.incoming_period.end}
                ({c.incoming_statement_type}). That overlaps {c.overlap_start} to {c.overlap_end}, already covered by{' '}
                <strong>{c.conflicting_filename}</strong> ({c.conflicting_statement_type}), imported{' '}
                {new Date(c.conflicting_uploaded_at).toLocaleDateString()}. {c.account} is set to treat{' '}
                {c.incoming_statement_type === c.conflicting_statement_type ? 'this source' : 'a different source'} as
                canonical for this period — importing both risks the same transactions counted twice.
              </div>
            ))}
            <div style={{ fontSize:11, color:C.stone, marginBottom:10 }}>
              Undo the conflicting import first if this is a correction, or confirm below if this is
              deliberate (e.g. the earlier import was wrong and this one should stand alongside it).
            </div>
            <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, fontWeight:600, cursor:'pointer' }}>
              <input type="checkbox" checked={forcedFiles.has(fi)}
                onChange={e => setForcedFiles(prev => {
                  const next = new Set(prev);
                  if (e.target.checked) next.add(fi); else next.delete(fi);
                  return next;
                })} />
              Import anyway — I understand the periods overlap
            </label>
          </div>
        ))}

        {alreadyImportedFiles.map(a => (
          <div key={'already'+a.fi} style={{ border:`1px solid ${C.mint}`, background:C.mintLight, borderRadius:12,
                        padding:'12px 14px', marginTop:4, marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.mint, marginBottom:4 }}>
              ℹ️ {a.filename}: already imported
            </div>
            <div style={{ fontSize:11, color:C.stone }}>
              This file appears to have been imported already on {new Date(a.uploaded_at).toLocaleDateString()} as{' '}
              <strong>{a.filename}</strong>. Every matching row is the same charge, worded identically — the
              routine duplicate check will skip all of them automatically. Nothing to review here.
            </div>
          </div>
        ))}

        {(needsReview.length > 0 || autoSkip.length > 0) && (
          <div style={{ border:`2px solid ${C.amber}`, background:C.amberLight, borderRadius:12,
                        padding:'14px 16px', marginTop:4, marginBottom:16 }}>
            {needsReview.length > 0 && (
              <>
                <div style={{ fontSize:13, fontWeight:700, color:C.amberDark || C.amber, marginBottom:4 }}>
                  ⚠️ {needsReview.length} unconfirmed match{needsReview.length===1?'':'es'} — review before importing
                </div>
                <div style={{ fontSize:11, color:C.stone, marginBottom:10 }}>
                  These rows share a date, amount, and account with a transaction already on file, and the
                  wording is different — could be the same charge described differently by two sources (a PDF
                  vs. this file), or a genuine same-day repeat purchase. This is not the system's routine
                  duplicate check — that already ran and is not shown here. Decide each row below.
                </div>
              </>
            )}
            {needsReview.map(m => {
              const key = `${m.fi}:${m.id}`;
              const isExcluded = excluded.has(key);
              // The duplicate check ran once, at file-selection time, against
              // whatever account the parser guessed — it never re-runs when the
              // operator overrides that account below. `m.account` is that
              // stale, pre-override value, so a mismatch here means this
              // comparison may no longer reflect where the row is actually headed.
              const confirmedAccount = chosen(m.fi, m.account);
              const overridden = confirmedAccount !== m.account;
              return (
                <div key={key} style={{ background:'white', borderRadius:8, padding:'8px 10px', marginBottom:8 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                    <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, fontWeight:600, cursor:'pointer' }}>
                      <input type="checkbox" checked={!isExcluded} onChange={()=>toggleRow(m.fi, m.id)} />
                      {isExcluded ? 'Excluded from import' : 'Include in import'}
                    </label>
                    <span style={{ fontSize:11, color:C.stoneMid, marginLeft:'auto' }}>
                      {m.date} · ${Number(m.amount).toFixed(2)} · {overridden ? `${m.account} → ${confirmedAccount}` : m.account}
                    </span>
                  </div>
                  {overridden && (
                    <div style={{ fontSize:11, color:C.coral, marginBottom:6 }}>
                      ⚠️ This check compared against <strong>{m.account}</strong> — the account detected before
                      you changed it to <strong>{confirmedAccount}</strong> above. It may not reflect the
                      corrected account.
                    </div>
                  )}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, fontSize:11 }}>
                    <div>
                      <div style={{ color:C.stoneMid, marginBottom:2 }}>Incoming (this file)</div>
                      <div style={{ fontWeight:600 }}>{m.incoming_description}</div>
                    </div>
                    <div>
                      <div style={{ color:C.stoneMid, marginBottom:2 }}>Already on file</div>
                      <div style={{ fontWeight:600 }}>{m.existing_description}</div>
                    </div>
                  </div>
                </div>
              );
            })}
            {needsReview.length > 0 && (
              <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, fontWeight:600, marginTop:4, cursor:'pointer' }}>
                <input type="checkbox" checked={reviewed} onChange={e=>setReviewed(e.target.checked)} />
                I've reviewed {needsReview.length===1?'this row':'these rows'}
              </label>
            )}
            {autoSkip.length > 0 && (
              <div style={{ marginTop: needsReview.length > 0 ? 12 : 0 }}>
                <div style={{ fontSize:11, color:C.stoneMid, marginBottom:8 }}>
                  {autoSkip.length} more row{autoSkip.length===1?'':'s'} matched a transaction already on file with
                  identical wording — the routine duplicate check will skip {autoSkip.length===1?'it':'them'}
                  automatically, no review needed.
                </div>
                {autoSkip.map(m => {
                  const confirmedAccount = chosen(m.fi, m.account);
                  const overridden = confirmedAccount !== m.account;
                  return (
                    <div key={`${m.fi}:${m.id}`} style={{ background:'white', borderRadius:8, padding:'8px 10px',
                                marginBottom:8, opacity:0.75 }}>
                      <div style={{ fontSize:11, color:C.stoneMid }}>
                        {m.date} · ${Number(m.amount).toFixed(2)} · {overridden ? `${m.account} → ${confirmedAccount}` : m.account} ·{' '}
                        {m.incoming_description} — already on file
                      </div>
                      {overridden && (
                        <div style={{ fontSize:11, color:C.coral, marginTop:4 }}>
                          ⚠️ Checked against {m.account} — the account detected before you changed it to{' '}
                          {confirmedAccount} above. May not reflect the corrected account.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
          <button style={S.btnGhost} onClick={onCancel} disabled={busy}>Cancel</button>
          <button style={{ ...S.btnPrimary(false), opacity:(blocked||busy)?0.5:1,
                           cursor:(blocked||busy)?'not-allowed':'pointer' }}
                  disabled={blocked||busy}
                  onClick={handleImport}>
            {busy ? 'Importing...' : `Import ${importCount} transaction${importCount===1?'':'s'}`}
          </button>
        </div>
        {acctBlocked && <div style={{ fontSize:11, color:C.coral, textAlign:'right', marginTop:8 }}>
          Select an account for each unrecognized row to continue.</div>}
        {!acctBlocked && conflictBlocked && <div style={{ fontSize:11, color:C.coral, textAlign:'right', marginTop:8 }}>
          Resolve the source conflict above to continue.</div>}
        {!acctBlocked && !conflictBlocked && matchBlocked && <div style={{ fontSize:11, color:C.coral, textAlign:'right', marginTop:8 }}>
          Check "I've reviewed these" to continue.</div>}
      </div>
    </div>
  );
}

// The snapshot date is the one thing in this feature that cannot come from the
// file and has to be right, so it is what this modal is built around. The
// statement modal asks "which account is this?"; this one asks "what date is
// this, and what will it replace?".
function InvestmentPreviewModal({ preview, onCancel, onImport, busy }) {
  const [date, setDate] = useState(preview.suggested_date || '');

  const problems = validateSnapshotDate(preview.months, date);
  // Which stored snapshots this date would overwrite. Recomputed as the operator
  // types rather than fetched, so the warning tracks the field instead of lagging it.
  const clashes = (preview.existing || []).filter(e => e.snapshot_date === date);
  const incoming = new Set(preview.files.map(f => f.source));
  const replacing = clashes.filter(c => incoming.has(c.source));

  const readable = preview.files.filter(f => f.source === 'summary' || f.source === 'returns');
  const unknown = [...new Set(preview.files.flatMap(f => f.unknown_numbers || []))];
  const rowCount = readable.reduce((s, f) =>
    s + f.accounts.reduce((n, a) => n + a.positions + a.balances + a.returns + a.monthly, 0), 0);
  const blocked = busy || !!problems.length || !readable.length;

  const SRC = { summary: 'Portfolio summary — balances and positions',
                returns: 'Returns — money-weighted, by period and by month' };

  return (
    <div style={S.backdrop} onClick={busy ? undefined : onCancel}>
      <div style={{ ...S.modal, maxWidth: 680 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>Confirm investment import</div>
        <div style={{ fontSize: 12, color: C.stone, marginBottom: 16 }}>
          {preview.files.length} file{preview.files.length === 1 ? '' : 's'} · {rowCount} rows found.
          Check the snapshot date before importing.
        </div>

        {/* THE SNAPSHOT DATE */}
        <div style={{ border: `1px solid ${problems.length ? C.coral : C.warmDark}`,
                      background: problems.length ? C.coralLight : C.warm,
                      borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Snapshot date</div>
          <div style={{ fontSize: 11, color: C.stoneMid, marginBottom: 8, lineHeight: 1.5 }}>
            The date this export was taken. It is not stored in the file — everything imported is filed under
            whatever you enter here.
          </div>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} disabled={busy}
            style={{ ...S.input, width: 'auto', minWidth: 190, fontSize: 14, fontWeight: 600,
                     borderColor: problems.length ? C.coral : C.warmDeep }} />
          {preview.date_source === 'returns_months' && !problems.length && (
            <div style={{ fontSize: 11, color: C.stoneMid, marginTop: 6 }}>
              Suggested from this export's last month column.
            </div>
          )}
          {preview.date_source === 'existing_snapshot' && !problems.length && (
            <div style={{ fontSize: 11, color: C.stoneMid, marginTop: 6 }}>
              Suggested from your most recent snapshot — this file carries no month columns to date itself with.
            </div>
          )}
          {problems.map((p, i) => (
            <div key={i} style={{ fontSize: 12, color: C.coral, marginTop: 8, lineHeight: 1.5 }}>{p}</div>
          ))}
        </div>

        {/* WILL REPLACE */}
        {replacing.length > 0 && (
          <div style={{ border: `1px solid ${C.amber}`, background: C.amberLight, borderRadius: 12,
                        padding: '12px 14px', marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
              A snapshot for {date} already exists — importing will replace it.
            </div>
            {replacing.map((c, i) => (
              <div key={i} style={{ fontSize: 11, color: C.stone, lineHeight: 1.6 }}>
                {INV_SOURCE_LABEL[c.source] || c.source} · {c.filename}
                {c.uploaded_at ? ` · imported ${new Date(c.uploaded_at).toLocaleDateString()}` : ''}
              </div>
            ))}
            <div style={{ fontSize: 11, color: C.stone, marginTop: 6, lineHeight: 1.5 }}>
              The old rows are deleted and the new ones written in a single step, so a holding you have
              since sold will not linger.
            </div>
          </div>
        )}

        {/* FILES */}
        {preview.files.map((f, fi) => (
          <div key={f.filename + fi}
               style={{ border: `1px solid ${f.source === 'error' || f.source === 'unknown' ? C.coral : C.warmDark}`,
                        background: f.source === 'error' || f.source === 'unknown' ? C.coralLight : 'white',
                        borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all', marginBottom: 4 }}>{f.filename}</div>
            <div style={{ fontSize: 11, color: C.stoneMid, marginBottom: 10 }}>
              {SRC[f.source] || f.error || 'Not a Questrade export Tally recognizes'}
            </div>

            {f.accounts.map(a => (
              <div key={a.number} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 12, flex: '1 1 150px', color: a.registered ? C.ink : C.coral }}>
                  {a.registered ? a.display_name : `Account ${a.number}`}
                  <span style={{ fontSize: 10, color: C.stoneMid }}>
                    {' '}· {a.number}{a.label ? ` · ${a.label}` : ''}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: C.stoneMid, flex: '1 1 150px' }}>
                  {f.source === 'summary'
                    ? `${a.positions} position${a.positions === 1 ? '' : 's'} · ${a.balances} balance${a.balances === 1 ? '' : 's'}`
                    : `${a.returns} period${a.returns === 1 ? '' : 's'} · ${a.monthly} month${a.monthly === 1 ? '' : 's'}`}
                </div>
                <span style={{ fontSize: 11, color: a.registered ? C.stoneMid : C.coral, minWidth: 92 }}>
                  {!a.registered ? '⚠ not registered' : a.view === 'business' ? '💼 Business' : '🏠 Household'}
                </span>
              </div>
            ))}

            {(f.skipped_accounts || []).length > 0 && (
              <div style={{ fontSize: 11, color: C.stoneMid, marginTop: 8, lineHeight: 1.5 }}>
                {f.skipped_accounts.length} account{f.skipped_accounts.length === 1 ? '' : 's'} in Returns had no
                matching balances — skipped ({f.skipped_accounts.join(', ')}). An account with no holdings and no
                balance row carries nothing but zeros.
              </div>
            )}
          </div>
        ))}

        {unknown.length > 0 && (
          <div style={{ border: `1px solid ${C.coral}`, background: C.coralLight, borderRadius: 12,
                        padding: '12px 14px', marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
              {unknown.length} account{unknown.length === 1 ? '' : 's'} not in your registry — {unknown.join(', ')}
            </div>
            <div style={{ fontSize: 11, color: C.stone, lineHeight: 1.5 }}>
              These will be skipped. Tally will not create an account from a number in a spreadsheet — it has no way
              to know whether it belongs to the household or the business. Add it in Admin → Accounts with
              type <strong>investment</strong> and that number as the identifier, then upload again.
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button style={S.btnGhost} onClick={onCancel} disabled={busy}>Cancel</button>
          <button style={{ ...S.btnPrimary(false), opacity: blocked ? 0.5 : 1,
                           cursor: blocked ? 'not-allowed' : 'pointer' }}
                  disabled={blocked} onClick={() => onImport(date)}>
            {busy ? 'Importing...' : replacing.length ? `Replace snapshot for ${date}` : `Import ${rowCount} rows`}
          </button>
        </div>
        {!readable.length && !busy && (
          <div style={{ fontSize: 11, color: C.coral, textAlign: 'right', marginTop: 8 }}>
            Nothing here can be imported.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MANUAL HOLDINGS ──────────────────────────────────────────────────────────
// Holdings with no Questrade export behind them. The value is typed in by hand,
// so the date it was last checked is part of the number — an unmarked stale
// figure is worse than no figure at all.
const STALE_DAYS = 90;

// Whole days between an as-of date and today, parsed by hand: new Date('2026-05-31')
// is UTC midnight and reads as the day before in every timezone west of Greenwich.
function daysSince(iso) {
  if (!iso) return null;
  const parts = String(iso).split('-');
  const then = Date.UTC(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((today - then) / 86400000);
}

// AUM statement lines. Mirrors MANUAL_GROUPS in server/index.js, which is the
// authority — it validates every write. Duplicated here rather than fetched,
// the same way BUSINESS_ACCOUNTS mirrors BUSINESS_ACCOUNTS_FALLBACK. If you add
// a group, add it in both places or the dropdown offers something the server
// will reject.
const MANUAL_GROUPS = {
  asset: ['Real Estate', 'Vehicles', 'Accounts', 'Investments', 'Other'],
  debt:  ['Loans', 'Credit Cards'],
};

// A debt's value is stored POSITIVE and subtracted at display time, which makes
// the event vocabulary read BACKWARDS unless the labels follow the direction:
// a mortgage payment is a 'withdrawal' in the schema, and calling it that on
// screen is precisely the ambiguity the event log exists to prevent. The three
// server-side event types are unchanged — only what the operator reads changes.
const MANUAL_EVENT_LABELS = {
  asset: { opened:'Opened', revaluation:'Revaluation', contribution:'Contribution',
           withdrawal:'Withdrawal', closed:'Closed' },
  debt:  { opened:'Opened', revaluation:'Balance updated', contribution:'New borrowing',
           withdrawal:'Payment', closed:'Paid off' },
};
const eventLabel = (direction, type) =>
  (MANUAL_EVENT_LABELS[direction === 'debt' ? 'debt' : 'asset'] || {})[type] || type;

// The operator picks one of these explicitly. There is no default: a withdrawal
// mislabelled as a revaluation is precisely the mistake this table exists to
// stop, and a pre-selected radio button is how that mistake gets made.
const MANUAL_ACTIONS_BY_DIRECTION = {
  asset: [
    { key:'revaluation',  label:'Update value',        blurb:'The market moved. No money went in or out.' },
    { key:'contribution', label:'Record contribution', blurb:'You added money to this holding.' },
    { key:'withdrawal',   label:'Record withdrawal',   blurb:'You took money out of this holding.' },
  ],
  debt: [
    { key:'revaluation',  label:'Update balance',      blurb:'Interest or a correction changed what is owed. You made no payment.' },
    { key:'contribution', label:'Record new borrowing', blurb:'You borrowed more against this — the balance went up.' },
    { key:'withdrawal',   label:'Record payment',      blurb:'You paid money against this — the balance went down.' },
  ],
};
const manualActions = direction => MANUAL_ACTIONS_BY_DIRECTION[direction === 'debt' ? 'debt' : 'asset'];

function ManualHoldingModal({ holding, action, onCancel, onSave, busy }) {
  const isNew = !holding;
  const [type, setType] = useState(action || '');
  const [form, setForm] = useState(() => ({
    name: holding?.name || '', institution: holding?.institution || '',
    account_type: holding?.account_type || '', owner: holding?.owner || '',
    view: holding?.view || 'household',
    direction: holding?.direction || 'asset', group_name: holding?.group_name || '',
    new_value: '', amount: '', as_of: '', notes: '',
  }));
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const needsAmount = type === 'contribution' || type === 'withdrawal';
  const isDebt = form.direction === 'debt';
  const actions = manualActions(form.direction);

  const problems = [];
  if (isNew && !form.name.trim()) problems.push('Name is required.');
  if (!isNew && !type) problems.push('Choose why the value is changing.');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.as_of)) problems.push('Enter the date this value is accurate as of.');
  if (form.new_value === '' || !Number.isFinite(Number(form.new_value))) {
    problems.push(isNew ? 'Enter the current value.' : "Enter what it is worth now.");
  } else if (isDebt && Number(form.new_value) < 0) {
    // Mirrors the server guard. A debt is stored positive and subtracted at
    // display time; a negative here means it was entered the other way round.
    problems.push('Enter what is owed as a positive number — Tally subtracts it from your assets.');
  }
  if (needsAmount && (form.amount === '' || !(Number(form.amount) > 0))) {
    problems.push(`Enter the ${(actions.find(a => a.key === type)?.label || type).toLowerCase()} amount.`);
  }

  return (
    <div style={S.backdrop} onClick={busy ? undefined : onCancel}>
      <div style={{ ...S.modal, maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize:17, fontWeight:700, marginBottom:4 }}>
          {isNew ? 'Add a manual holding' : holding.name}
        </div>
        <div style={{ fontSize:12, color:C.stone, marginBottom:16, lineHeight:1.5 }}>
          {isNew
            ? 'For anything with no export behind it — an account, a property, a vehicle, or a debt. Its value is maintained by hand, and every change is logged with a reason.'
            : 'Every change is recorded permanently. Pick the reason that actually happened.'}
        </div>

        {isNew && (
          <>
            <div style={S.modalLabel}>Name</div>
            <input style={{ ...S.input, marginBottom:12 }} value={form.name} disabled={busy}
              placeholder="Dynamic Mutual Funds TFSA" onChange={e=>set('name', e.target.value)} />

            {/* Direction first: it changes what every field below means, and the
                group list it feeds is direction-specific. Switching it clears
                the group rather than carrying a now-invalid one into a submit
                the server would reject. */}
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
              <div style={{ flex:'1 1 150px' }}>
                <div style={S.modalLabel}>Is this an asset or a debt?</div>
                <select style={S.input} value={form.direction} disabled={busy}
                  onChange={e=>setForm(f=>({ ...f, direction:e.target.value, group_name:'' }))}>
                  <option value="asset">Asset — something you own</option>
                  <option value="debt">Debt — something you owe</option>
                </select>
              </div>
              <div style={{ flex:'1 1 150px' }}>
                <div style={S.modalLabel}>Group</div>
                <select style={S.input} value={form.group_name} disabled={busy}
                  onChange={e=>set('group_name', e.target.value)}>
                  <option value="">Ungrouped</option>
                  {MANUAL_GROUPS[isDebt ? 'debt' : 'asset'].map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
            </div>
            {isDebt && (
              <div style={{ fontSize:11, color:C.stone, marginBottom:12, lineHeight:1.5 }}>
                Enter the balance <strong>owed</strong> as a positive number — Tally subtracts it from your assets.
                A house and the mortgage against it are two separate entries: the house is an asset under Real
                Estate, the mortgage is a debt under Loans.
              </div>
            )}

            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
              <div style={{ flex:'1 1 150px' }}>
                <div style={S.modalLabel}>Institution</div>
                <input style={S.input} value={form.institution} disabled={busy}
                  placeholder={isDebt ? 'Vancity' : 'Dynamic Funds'} onChange={e=>set('institution', e.target.value)} />
              </div>
              <div style={{ flex:'1 1 110px' }}>
                <div style={S.modalLabel}>Type</div>
                <input style={S.input} value={form.account_type} disabled={busy}
                  placeholder={isDebt ? 'Mortgage' : 'TFSA'} onChange={e=>set('account_type', e.target.value)} />
              </div>
              <div style={{ flex:'1 1 110px' }}>
                <div style={S.modalLabel}>Owner</div>
                <input style={S.input} value={form.owner} disabled={busy}
                  placeholder="Ashley" onChange={e=>set('owner', e.target.value)} />
              </div>
            </div>
            <div style={S.modalLabel}>View</div>
            <select style={{ ...S.input, marginBottom:12 }} value={form.view} disabled={busy}
              onChange={e=>set('view', e.target.value)}>
              <option value="household">🏠 Household</option>
              <option value="business">💼 Business</option>
            </select>
          </>
        )}

        {!isNew && (
          <>
            <div style={S.modalLabel}>What happened</div>
            <div style={{ marginBottom:14 }}>
              {actions.map(a => (
                <label key={a.key} style={{ display:'flex', alignItems:'flex-start', gap:8, padding:'8px 10px',
                          border:`1px solid ${type===a.key?C.sage:C.warmDark}`, borderRadius:10, marginBottom:6,
                          background:type===a.key?C.sageLight:'white', cursor:busy?'not-allowed':'pointer' }}>
                  <input type="radio" name="mh-event" checked={type===a.key} disabled={busy}
                    onChange={()=>setType(a.key)} style={{ marginTop:3 }} />
                  <div>
                    <div style={{ fontSize:13, fontWeight:600 }}>{a.label}</div>
                    <div style={{ fontSize:11, color:C.stone }}>{a.blurb}</div>
                  </div>
                </label>
              ))}
            </div>
          </>
        )}

        {needsAmount && (
          <>
            <div style={S.modalLabel}>
              {isDebt
                ? (type === 'withdrawal' ? 'How much you paid' : 'How much you borrowed')
                : (type === 'withdrawal' ? 'How much came out' : 'How much went in')}
            </div>
            <input style={{ ...S.input, marginBottom:12 }} value={form.amount} disabled={busy}
              inputMode="decimal" placeholder="40000" onChange={e=>set('amount', e.target.value)} />
          </>
        )}

        <div style={S.modalLabel}>
          {isDebt
            ? (isNew ? 'Balance owed' : 'What is owed NOW')
            : (isNew ? 'Current value' : 'What it is worth NOW')}
        </div>
        <input style={{ ...S.input, marginBottom:6 }} value={form.new_value} disabled={busy}
          inputMode="decimal" placeholder="100000" onChange={e=>set('new_value', e.target.value)} />
        {needsAmount && (
          // The whole reason both fields exist. Between the last check and today
          // the market moved (or interest accrued), so old ± amount is a number
          // nobody verified.
          <div style={{ fontSize:11, color:C.stone, marginBottom:12, lineHeight:1.5 }}>
            Look up the real balance — this is <strong>not</strong> calculated from the amount above.
            {isDebt
              ? ' Interest accrued since the last check, so subtracting the payment would bake in a figure nobody checked.'
              : ' The market moved since the last check, so subtracting would bake in a figure nobody checked.'}
          </div>
        )}
        {!needsAmount && <div style={{ marginBottom:12 }} />}

        <div style={S.modalLabel}>Value is accurate as of</div>
        <input type="date" style={{ ...S.input, marginBottom:12 }} value={form.as_of} disabled={busy}
          onChange={e=>set('as_of', e.target.value)} />

        <div style={S.modalLabel}>Note (optional)</div>
        <input style={{ ...S.input, marginBottom:16 }} value={form.notes} disabled={busy}
          placeholder="Withdrawn for house deposit" onChange={e=>set('notes', e.target.value)} />

        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button style={S.btnGhost} onClick={onCancel} disabled={busy}>Cancel</button>
          <button style={{ ...S.btnPrimary(false), opacity:(problems.length||busy)?0.5:1,
                           cursor:(problems.length||busy)?'not-allowed':'pointer' }}
                  disabled={!!problems.length || busy}
                  onClick={()=>onSave({ ...form, event_type: type })}>
            {busy ? 'Saving…' : isNew ? 'Add holding' : 'Record it'}
          </button>
        </div>
        {problems.length > 0 && !busy && (
          <div style={{ fontSize:11, color:C.coral, textAlign:'right', marginTop:8 }}>{problems[0]}</div>
        )}
      </div>
    </div>
  );
}

function ManualCloseModal({ holding, onCancel, onConfirm, busy }) {
  const [closedOn, setClosedOn] = useState('');
  const [notes, setNotes] = useState('');
  const ok = /^\d{4}-\d{2}-\d{2}$/.test(closedOn);
  // A debt closing at zero is a loan PAID OFF, not an account emptied. Same
  // server behaviour, opposite meaning to the operator.
  const isDebt = holding.direction === 'debt';
  return (
    <div style={S.backdrop} onClick={busy ? undefined : onCancel}>
      <div style={{ ...S.modal, maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize:17, fontWeight:700, marginBottom:12 }}>
          {isDebt ? 'Mark' : 'Close'} {holding.name}{isDebt ? ' paid off?' : '?'}
        </div>
        <div style={{ fontSize:13, color:C.ink, marginBottom:14, lineHeight:1.6 }}>
          {isDebt ? (
            <>This sets the balance to $0 and stops it counting against your assets. The record and its full
            history stay on file. If you made a final payment, <strong>record the payment first</strong> — closing
            on its own does not say how the remaining {fmt(Number(holding.current_value))} was cleared.</>
          ) : (
            <>This sets the value to $0 and stops it counting toward any total. The holding and its full history stay
            on file. If money came out, <strong>record the withdrawal first</strong> — closing on its own does not
            say where {fmt(Number(holding.current_value))} went.</>
          )}
        </div>
        <div style={S.modalLabel}>{isDebt ? 'Paid off on' : 'Closed on'}</div>
        <input type="date" style={{ ...S.input, marginBottom:12 }} value={closedOn} disabled={busy}
          onChange={e=>setClosedOn(e.target.value)} />
        <div style={S.modalLabel}>Note (optional)</div>
        <input style={{ ...S.input, marginBottom:16 }} value={notes} disabled={busy}
          placeholder={isDebt ? 'Final payment made' : 'Transferred to Questrade'}
          onChange={e=>setNotes(e.target.value)} />
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button style={S.btnGhost} onClick={onCancel} disabled={busy}>Cancel</button>
          <button style={{ ...S.btnPrimary(false), opacity:(!ok||busy)?0.5:1, cursor:(!ok||busy)?'not-allowed':'pointer' }}
                  disabled={!ok || busy} onClick={()=>onConfirm({ closed_on: closedOn, notes })}>
            {busy ? 'Closing…' : isDebt ? 'Mark paid off' : 'Close holding'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ManualDeleteModal({ holding, onCancel, onConfirm, busy }) {
  return (
    <div style={S.backdrop} onClick={busy ? undefined : onCancel}>
      <div style={{ ...S.modal, maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize:17, fontWeight:700, marginBottom:12 }}>Delete {holding.name}?</div>
        <div style={{ fontSize:13, color:C.ink, marginBottom:20, lineHeight:1.6 }}>
          This permanently destroys the holding and all {holding.event_count} history
          entr{holding.event_count===1?'y':'ies'}, including any recorded withdrawals. This cannot be undone.
          <br /><br />
          If this holding was real and has simply ended, <strong>close it instead</strong> — closing keeps the
          record of what happened to the money.
        </div>
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button style={S.btnGhost} onClick={onCancel} disabled={busy}>Cancel</button>
          <button style={{ ...S.btnPrimary(false), background:C.coral, borderColor:C.coral,
                           opacity:busy?0.6:1, cursor:busy?'not-allowed':'pointer' }}
                  disabled={busy} onClick={onConfirm}>
            {busy ? 'Deleting…' : 'Delete permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ManualHistoryModal({ holding, events, loading, onClose }) {
  const isDebt = holding.direction === 'debt';
  return (
    <div style={S.backdrop} onClick={onClose}>
      <div style={{ ...S.modal, maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize:17, fontWeight:700, marginBottom:4 }}>{holding.name} — history</div>
        <div style={{ fontSize:12, color:C.stone, marginBottom:16 }}>
          Every recorded change, newest first. Read-only.
          {isDebt ? ' A falling balance means the debt is being paid down.' : ''}
        </div>
        {loading ? <div style={{ fontSize:13, color:C.stone }}>Loading…</div>
         : !events.length ? <div style={{ fontSize:13, color:C.stone }}>No events recorded.</div>
         : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:520 }}>
              <thead>
                <tr style={{ textAlign:'left', color:C.stoneMid, fontSize:10, textTransform:'uppercase', letterSpacing:'.05em' }}>
                  <th style={{ padding:'6px 8px 6px 0' }}>Date</th>
                  <th style={{ padding:'6px 8px' }}>Event</th>
                  <th style={{ padding:'6px 8px', textAlign:'right' }}>Amount</th>
                  <th style={{ padding:'6px 8px', textAlign:'right' }}>{isDebt ? 'Balance after' : 'Value after'}</th>
                  <th style={{ padding:'6px 0 6px 8px' }}>Note</th>
                </tr>
              </thead>
              <tbody>
                {events.map(e => {
                  // The ± prefix needs no direction logic: withdrawal always
                  // moves the number DOWN and contribution always moves it UP,
                  // which is true of a portfolio and of a loan balance alike.
                  // The COLOUR does flip — paying down a mortgage is the good
                  // outcome, and rendering it in coral would say the opposite.
                  const good = isDebt ? 'withdrawal' : 'contribution';
                  const bad  = isDebt ? 'contribution' : 'withdrawal';
                  return (
                  <tr key={e.id} style={{ borderTop:`1px solid ${C.warmDark}` }}>
                    <td style={{ padding:'8px 8px 8px 0', whiteSpace:'nowrap' }}>{e.event_date}</td>
                    <td style={{ padding:'8px' }}>
                      <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:10,
                        background: e.event_type===bad ? C.coralLight : e.event_type===good ? C.mintLight : C.warm,
                        color: e.event_type===bad ? C.coral : e.event_type===good ? C.mint : C.stone }}>
                        {eventLabel(holding.direction, e.event_type)}
                      </span>
                    </td>
                    <td style={{ padding:'8px', textAlign:'right' }}>
                      {e.amount === null ? '—'
                        : `${e.event_type==='withdrawal' ? '−' : '+'}${fmt(Math.abs(Number(e.amount)))}`}
                    </td>
                    <td style={{ padding:'8px', textAlign:'right', fontWeight:600 }}>{fmt(Number(e.value_after))}</td>
                    <td style={{ padding:'8px 0 8px 8px', color:C.stone }}>{e.notes || ''}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:16 }}>
          <button style={S.btnGhost} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function ManualHoldingsSection({ holdings, isOwner, accent, title, blurb, emptyText,
                                 onAdd, onAction, onClose, onDelete, onHistory }) {
  const open = holdings.filter(h => h.status !== 'closed');
  const closed = holdings.filter(h => h.status === 'closed');

  return (
    <div style={{ ...S.card, marginBottom:16 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, flexWrap:'wrap' }}>
        <div style={{ ...S.secTitle, marginBottom:0 }}>{title || 'Manually tracked'}</div>
        {isOwner && onAdd && <button style={{ ...S.btnSm, background:accent, border:`1px solid ${accent}`, color:'white' }}
          onClick={onAdd}>+ Add holding</button>}
      </div>
      <div style={{ fontSize:11, color:C.stoneMid, margin:'8px 0 14px', lineHeight:1.5 }}>
        {blurb || <>Accounts with no Questrade export. Values are entered by hand, so each one carries the date it
        was last checked. Every change is logged with a reason — a withdrawal and a loss both look like the number
        going down, and only the record can tell them apart.</>}
      </div>

      {!holdings.length && (
        <div style={{ fontSize:13, color:C.stone }}>
          {emptyText || <>Nothing tracked manually yet.{isOwner ? ' Add a holding for any account Questrade does not export.' : ''}</>}
        </div>
      )}

      {open.map(h => {
        const age = daysSince(h.as_of);
        const stale = age !== null && age > STALE_DAYS;
        return (
          <div key={h.id} style={{ border:`1px solid ${stale?C.amber:C.warmDark}`, background:stale?C.amberLight:'white',
                                   borderRadius:12, padding:'12px 14px', marginBottom:8 }}>
            <div style={{ display:'flex', alignItems:'flex-start', gap:10, flexWrap:'wrap' }}>
              <div style={{ flex:'2 1 200px', minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:700 }}>{h.name}</div>
                <div style={{ fontSize:11, color:C.stoneMid, marginTop:2 }}>
                  {[h.group_name || 'Ungrouped', h.institution, h.account_type, h.owner].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div style={{ flex:'1 1 120px', textAlign:'right' }}>
                {/* A debt renders with a leading minus even though it is STORED
                    positive — this is the "subtracted at display time" half of
                    that decision, and without it the row is indistinguishable
                    from an asset of the same size. */}
                <div style={{ fontSize:18, fontWeight:700, color: h.direction === 'debt' ? C.coral : accent }}>
                  {h.direction === 'debt' ? `−${fmt(Number(h.current_value))}` : fmt(Number(h.current_value))}
                </div>
                <div style={{ fontSize:11, marginTop:2, color: stale ? C.amber : C.stoneMid, fontWeight: stale ? 700 : 400 }}>
                  as of {h.as_of}{stale ? ` · last checked ${age} days ago — time to update` : ''}
                </div>
              </div>
            </div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:10 }}>
              <button style={{ ...S.btnSm, background:'none', border:`1px solid ${C.warmDeep}`, color:C.stone }}
                onClick={()=>onHistory(h)}>History ({h.event_count})</button>
              {isOwner && manualActions(h.direction).map(a => (
                <button key={a.key} style={{ ...S.btnSm, background:'none', border:`1px solid ${C.warmDeep}`, color:C.stone }}
                  onClick={()=>onAction(h, a.key)}>{a.label}</button>
              ))}
              {isOwner && <button style={{ ...S.btnSm, background:'none', border:`1px solid ${C.warmDeep}`, color:C.stone }}
                onClick={()=>onClose(h)}>{h.direction === 'debt' ? 'Mark paid off' : 'Close'}</button>}
              {isOwner && <button style={{ ...S.btnSm, background:'none', border:`1px solid ${C.coral}`, color:C.coral }}
                onClick={()=>onDelete(h)}>Delete</button>}
            </div>
          </div>
        );
      })}

      {closed.map(h => (
        <div key={h.id} style={{ border:`1px solid ${C.warmDark}`, background:C.warm, borderRadius:12,
                                 padding:'10px 14px', marginBottom:8, opacity:.7 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <div style={{ flex:'2 1 200px', fontSize:13, fontWeight:600, color:C.stone }}>{h.name}</div>
            <div style={{ flex:'1 1 120px', textAlign:'right', fontSize:11, color:C.stoneMid }}>
              {h.direction === 'debt' ? 'Paid off' : 'Closed'} {h.closed_on} · excluded from totals
            </div>
            <button style={{ ...S.btnSm, background:'none', border:`1px solid ${C.warmDeep}`, color:C.stone }}
              onClick={()=>onHistory(h)}>History ({h.event_count})</button>
            {isOwner && <button style={{ ...S.btnSm, background:'none', border:`1px solid ${C.coral}`, color:C.coral }}
              onClick={()=>onDelete(h)}>Delete</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── AUM (ASSETS UNDER MANAGEMENT) ────────────────────────────────────────────
// Everything under management, assets minus debt, across bank balances,
// Questrade equity and hand-entered holdings.
//
// This view is deliberately NOT a scoreboard. It leads with composition and the
// trend; the total is a line item at the foot of the breakdown, at body weight,
// with no colour, no arrow and no change-since-last-month badge anywhere. The
// reason is concrete rather than stylistic: a large balance here is house-
// purchase money awaiting closing, so the figure will fall by most of itself in
// a single step. Rendered as a hero number with a delta, a planned house
// purchase would read as a catastrophe. Composition answers "what is this money
// and where is it" — which is the question a drop like that actually raises.
//
// ⚠️ A missing reading is NOT zero. An account Tally has no balance for shows
// "No balance imported" and is excluded from every total; substituting 0 would
// turn a gap in knowledge into a claim about an empty account.

// Two thresholds, because the same amber means two different things. A manual
// line going stale means nobody has checked it; an automatic one means a
// statement has not been imported. The fix differs, so the copy differs.
const AUM_STALE_AUTOMATIC = 45;
const AUM_STALE_MANUAL = STALE_DAYS;   // 90 — quarterly, shared with Investments

// Group render order. Assets in the course's order, then debts. A group with
// nothing in it is not rendered at all.
const AUM_GROUP_ORDER = ['Real Estate', 'Vehicles', 'Accounts', 'Investments', 'Other',
                         'Ungrouped', 'Loans', 'Credit Cards'];

function aumStaleness(line) {
  if (line.balance === null || !line.as_of) return null;
  const age = daysSince(line.as_of);
  if (age === null) return null;
  const limit = line.tracking === 'manual' ? AUM_STALE_MANUAL : AUM_STALE_AUTOMATIC;
  if (age <= limit) return null;
  return {
    age,
    note: line.tracking === 'manual'
      ? `last checked ${age} days ago — time to update`
      : `no statement since ${line.as_of} — upload one`,
  };
}

function AumPanel({ data, loading, error, view, isMobile, onReload }) {
  const isBiz = view === 'business';
  const accent = isBiz ? C.navy : C.sage;

  if (loading && !data) return <div style={{ ...S.card, color:C.stone, fontSize:13 }}>Loading AUM…</div>;
  if (error) {
    return (
      <div style={{ ...S.card, background:C.coralLight, borderColor:C.coral }}>
        <div style={{ fontSize:13, color:C.coral, fontWeight:600, marginBottom:8 }}>Could not load AUM</div>
        <div style={{ fontSize:12, color:C.stone, marginBottom:12 }}>{error}</div>
        <button style={S.btnGhost} onClick={onReload}>Try again</button>
      </div>
    );
  }
  if (!data) return null;

  const lines = data.lines || [];
  const withReading = lines.filter(l => l.balance !== null);

  if (!lines.length) {
    return (
      <div style={{ ...S.card, textAlign:'center', padding:'40px 24px' }}>
        <div style={{ fontSize:32, marginBottom:8 }}>🌿</div>
        <div style={{ fontSize:15, fontWeight:600, marginBottom:6 }}>Nothing under management yet</div>
        <div style={{ fontSize:13, color:C.stone, lineHeight:1.6, maxWidth:440, margin:'0 auto' }}>
          Import a Vancity statement to pick up account balances, or add what you own and owe by hand
          on the Investments tab.
        </div>
      </div>
    );
  }

  // Group, preserving the course's order and dropping empty groups.
  const groups = AUM_GROUP_ORDER
    .map(name => {
      const inGroup = lines.filter(l => l.group === name);
      if (!inGroup.length) return null;
      const readable = inGroup.filter(l => l.balance !== null);
      return {
        name,
        direction: inGroup[0].direction,
        lines: inGroup.slice().sort((a, b) => (b.balance || 0) - (a.balance || 0)),
        total: readable.reduce((s, l) => s + l.balance, 0),
        missing: inGroup.length - readable.length,
      };
    })
    .filter(Boolean);

  // Share of assets, for the composition bars. Debts are shown as their own
  // block and are NOT given a share — a percentage of a subtraction reads as if
  // the debt were part of what you hold.
  const assetTotal = data.assets || 0;
  const cov = data.coverage || { lines_total:0, lines_with_reading:0, missing:[] };

  const series = data.series || [];
  // Coverage rises as statements are imported, and an AUM that climbs because
  // Tally LEARNED about an account is not the same event as one that climbs
  // because money arrived.
  //
  // The first attempt at showing this split the line into "partial coverage"
  // dashed and "full coverage" solid. It rendered the ENTIRE line dashed, since
  // only the newest point ever sits at maximum coverage — technically true and
  // completely useless. What matters is not whether coverage is maximal but
  // whether it CHANGED between two points: a step where coverage held steady is
  // a real move, and a step where it grew is partly Tally catching up. So the
  // line is drawn whole, and the points where coverage changed are marked.
  const chartData = series.map((p, i) => ({
    date: p.as_of.slice(5),
    aum: p.aum,
    assets: p.assets,
    // Plotted as a negative so the chart reads as a stack around zero: debt
    // hangs below the line, assets above, and AUM is visibly the gap between.
    owed: -p.debts,
    coverage: `${p.lines_with_reading}/${p.lines_total}`,
    coverageChanged: i > 0 && p.lines_with_reading !== series[i - 1].lines_with_reading,
  }));
  const coverageSteps = chartData.filter(p => p.coverageChanged).length;

  // A ring only on the points where coverage moved. Recharts calls this for
  // every point, so the common case must return an empty node rather than null
  // (null logs a React warning about invalid return values in some versions).
  const coverageDot = props => {
    const { cx, cy, payload, key } = props;
    if (!payload.coverageChanged) return <g key={key} />;
    return <circle key={key} cx={cx} cy={cy} r={4} fill="white" stroke={C.stoneMid} strokeWidth={2} />;
  };

  const money = v => fmt(Number(v || 0));

  return (
    <>
      {/* ── COMPOSITION ─────────────────────────────────────────────────────
          First on the page, deliberately. The total is at the bottom of this
          card, at body weight — the answer to "what is this and where is it"
          comes before the answer to "how much". */}
      <div style={{ ...S.card, marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', gap:8, flexWrap:'wrap' }}>
          <div style={{ ...S.secTitle, marginBottom:0 }}>
            What's under management · {isBiz ? '💼 Business' : '🏠 Household'}
          </div>
          <div style={{ fontSize:11, color:C.stoneMid }}>as of {data.as_of}</div>
        </div>
        <div style={{ fontSize:12, color:C.stone, margin:'10px 0 16px', lineHeight:1.6 }}>
          Everything being stewarded, and what is owed against it.
        </div>

        {groups.map(g => (
          <div key={g.name} style={{ marginBottom:18 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline',
                          gap:8, marginBottom:8, flexWrap:'wrap' }}>
              <div style={{ fontSize:13, fontWeight:700 }}>
                {g.name}
                {g.direction === 'debt' && (
                  <span style={{ fontSize:10, fontWeight:700, color:C.coral, background:C.coralLight,
                                 padding:'2px 7px', borderRadius:10, marginLeft:8 }}>OWED</span>
                )}
              </div>
              <div style={{ fontSize:13, fontWeight:600, color: g.direction === 'debt' ? C.coral : C.ink }}>
                {g.direction === 'debt' ? `−${money(g.total)}` : money(g.total)}
              </div>
            </div>

            {/* Share of ASSETS only. A debt gets no bar — a percentage of a
                subtraction reads as though it were part of what you hold. */}
            {g.direction === 'asset' && assetTotal > 0 && (
              <div style={{ height:8, background:C.warmDark, borderRadius:4, overflow:'hidden', marginBottom:10 }}>
                <div style={{ width:`${Math.min(100, (g.total / assetTotal) * 100)}%`, height:'100%', background:accent }} />
              </div>
            )}

            {g.lines.map(l => {
              const stale = aumStaleness(l);
              const noReading = l.balance === null;
              return (
                <div key={l.key} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start',
                                          gap:10, padding:'5px 0', flexWrap:'wrap' }}>
                  <div style={{ flex:'1 1 200px', minWidth:0 }}>
                    <div style={{ fontSize:12, color: noReading ? C.stoneMid : C.ink }}>
                      {l.name}
                      {/* Automatic vs manual, on every line that HAS a source. A
                          stale manual entry and a stale import mean different
                          things and have different fixes, so the badge is never
                          decorative — which is exactly why it is omitted when an
                          account has never produced a balance at all. Registry
                          type would call those "automatic", but nothing has ever
                          imported one and for CIBC Chequing nothing can (its CSV
                          has no balance column). Badging them ⟳ IMPORTED would
                          assert something untrue right next to a line that says
                          no balance was imported. */}
                      {!(noReading && l.ever_sourced === false) && (
                        <span style={{ fontSize:9, fontWeight:700, marginLeft:7, padding:'1px 6px', borderRadius:8,
                                       background: l.tracking === 'manual' ? C.warm : C.mintLight,
                                       color: l.tracking === 'manual' ? C.stone : C.mint }}>
                          {l.tracking === 'manual' ? '✎ MANUAL' : '⟳ IMPORTED'}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize:10, color: stale ? C.amber : C.stoneMid, marginTop:2,
                                  fontWeight: stale ? 700 : 400 }}>
                      {noReading
                        ? (l.ever_sourced === false
                            ? 'No balance imported — enter it by hand to include it'
                            : 'No balance on or before this date')
                        : stale ? stale.note : `as of ${l.as_of}`}
                    </div>
                  </div>
                  <div style={{ fontSize:12, textAlign:'right', whiteSpace:'nowrap',
                                fontWeight: noReading ? 400 : 600,
                                color: noReading ? C.stoneMid : l.direction === 'debt' ? C.coral : C.ink }}>
                    {/* Never "$0.00" — absent is a gap in what Tally knows, and
                        a zero here would be a claim that the account is empty. */}
                    {noReading ? 'no reading' : l.direction === 'debt' ? `−${money(l.balance)}` : money(l.balance)}
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {/* ── TOTALS ────────────────────────────────────────────────────────
            Assets and debts broken out above the AUM line, so the composition
            is what the eye lands on. No colour, no arrow, no delta on the AUM
            figure: it is a position, not a score. */}
        <div style={{ borderTop:`1px solid ${C.warmDark}`, marginTop:6, paddingTop:12 }}>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:6 }}>
            <span style={{ color:C.stone }}>Total assets</span>
            <span style={{ fontWeight:600 }}>{money(data.assets)}</span>
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:10 }}>
            <span style={{ color:C.stone }}>Total owed</span>
            <span style={{ fontWeight:600, color: data.debts ? C.coral : C.stone }}>
              {data.debts ? `−${money(data.debts)}` : money(0)}
            </span>
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline',
                        fontSize:14, fontWeight:700, borderTop:`1px solid ${C.warmDark}`, paddingTop:10 }}>
            <span>Assets under management</span>
            <span>{money(data.aum)}</span>
          </div>
        </div>

        {/* Coverage rides with the figure, never in a separate card — a total
            and the caveat about what it omits must not be able to disagree. */}
        {cov.missing && cov.missing.length > 0 && (
          <div style={{ marginTop:12, padding:'10px 12px', background:C.warm, borderRadius:10,
                        fontSize:11, color:C.stone, lineHeight:1.6 }}>
            This covers <strong>{cov.lines_with_reading} of {cov.lines_total}</strong> tracked lines.
            No balance on file for <strong>{cov.missing.join(', ')}</strong>, so {cov.missing.length === 1 ? 'it is' : 'they are'} left
            out entirely rather than counted as zero — the figure above is lower than reality by whatever
            {cov.missing.length === 1 ? ' it holds' : ' they hold'}.
          </div>
        )}
      </div>

      {/* ── TREND ───────────────────────────────────────────────────────────── */}
      <div style={{ ...S.card, marginBottom:16 }}>
        <div style={S.secTitle}>Over time</div>
        <div style={{ fontSize:11, color:C.stoneMid, marginBottom:12, lineHeight:1.6 }}>
          Each step is a statement imported or a value checked — between readings a figure is carried
          forward flat rather than smoothed, so the line steps rather than curves. That is the honest
          shape: nothing here is estimated between two known points.
        </div>
        {series.length < 2 ? (
          <div style={{ fontSize:13, color:C.stone, padding:'8px 0' }}>
            {series.length === 1
              ? 'One reading so far. Import another statement or record another value to start a trend.'
              : 'No readings yet.'}
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
              <LineChart data={chartData} margin={{ top:5, right:10, left:-10, bottom:0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.warmDark} />
                <XAxis dataKey="date" tick={{ fontSize:10, fill:C.stoneMid }} />
                <YAxis tick={{ fontSize:10, fill:C.stoneMid }}
                       tickFormatter={v => `${Math.round(v/1000)}k`} />
                <Tooltip
                  formatter={(v, n) => [fmt(Math.abs(Number(v))), n]}
                  labelFormatter={(l, p) => `${l} · ${p?.[0]?.payload?.coverage || ''} lines with a reading`} />
                <Legend wrapperStyle={{ fontSize:11 }} />
                <ReferenceLine y={0} stroke={C.stoneMid} />
                {/* Assets and owed are plotted alongside AUM deliberately. AUM
                    alone cannot distinguish "the money went" from "a debt was
                    taken on" or "an asset was converted" — all three are just
                    the line falling. With all three visible, a mortgage
                    appearing shows as the owed line dropping away from zero
                    while assets hold, which is what actually happened. */}
                <Line type="stepAfter" dataKey="assets" name="Assets" stroke={accent}
                      strokeWidth={1.5} dot={false} isAnimationActive={false} />
                <Line type="stepAfter" dataKey="owed" name="Owed" stroke={C.coral}
                      strokeWidth={1.5} dot={false} isAnimationActive={false} />
                <Line type="stepAfter" dataKey="aum" name="AUM" stroke={C.ink}
                      strokeWidth={2.5} dot={coverageDot} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
            {coverageSteps > 0 && (
              // The single most misleading thing this chart could do is let a
              // coverage change read as a real move. Said plainly instead.
              <div style={{ fontSize:11, color:C.stone, marginTop:8, padding:'8px 10px',
                            background:C.warm, borderRadius:8, lineHeight:1.6 }}>
                The <strong>ringed points</strong> are where the number of accounts with a reading changed —
                {coverageSteps === 1 ? ' one of them' : ` ${coverageSteps} of them`} here. A rise across one of
                those is partly Tally learning what exists rather than the money growing, so the step either
                side of a ring is not a like-for-like comparison.
              </div>
            )}
          </>
        )}
      </div>

      {/* ── HOW THIS IS TRACKED ──────────────────────────────────────────────── */}
      <div style={{ ...S.card, marginBottom:16 }}>
        <div style={S.secTitle}>How each line is kept current</div>
        <div style={{ fontSize:12, color:C.stone, lineHeight:1.7 }}>
          <div style={{ marginBottom:8 }}>
            <span style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:8,
                           background:C.mintLight, color:C.mint, marginRight:8 }}>⟳ IMPORTED</span>
            Comes from an uploaded statement — {withReading.filter(l => l.tracking === 'automatic').length} line
            {withReading.filter(l => l.tracking === 'automatic').length === 1 ? '' : 's'} today. Goes amber
            after {AUM_STALE_AUTOMATIC} days, which means a statement is waiting to be uploaded.
          </div>
          <div>
            <span style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:8,
                           background:C.warm, color:C.stone, marginRight:8 }}>✎ MANUAL</span>
            Typed in by hand and maintained on the Investments tab — {withReading.filter(l => l.tracking === 'manual').length} line
            {withReading.filter(l => l.tracking === 'manual').length === 1 ? '' : 's'} today. Goes amber
            after {AUM_STALE_MANUAL} days, which means it is due a check. Credit cards and CIBC Chequing
            live here: their statements carry no balance to import.
          </div>
        </div>
      </div>
    </>
  );
}

// ─── INVESTMENTS ──────────────────────────────────────────────────────────────
// Read-only. Everything here comes from an uploaded Questrade snapshot except
// "Contributed this year", which comes from the bank transactions — the exports
// carry holdings and returns, never deposits.
const INV_SUB_TABS = [['overview','Overview'], ['performance','Performance']];

// Questrade's asset-class codes. The CASH *symbol* is a high-interest savings
// ETF and belongs under ETFs; the Cash bucket comes from the balance sheet's
// cash columns instead. Backwards, the allocation stops reconciling.
const ASSET_LABELS = { ETF:'ETFs', STK:'Stocks', OPT:'Options', BND:'Bonds' };

const PERIOD_ORDER = [
  ['1m','1 month'], ['3m','3 months'], ['6m','6 months'], ['ytd','Year to date'],
  ['1y','1 year'], ['3y','3 years'], ['5y','5 years'], ['10y','10 years'],
  ['inception','Since inception'],
];

// NULL is not 0. An empty 3/5/10-year cell means the account is younger than the
// window — the FHSA opened Nov 2023, so its 5- and 10-year cells are permanently
// empty. Rendering 0.00% would make that indistinguishable from a flat year.
const pct = v => (v === null || v === undefined) ? '—' : `${Number(v) >= 0 ? '+' : ''}${Number(v).toFixed(2)}%`;
const pctColor = v => (v === null || v === undefined) ? C.stoneMid : Number(v) >= 0 ? C.sage : C.coral;

function allocationOf(positions, balances, manual = []) {
  const buckets = new Map();
  positions.forEach(p => {
    const label = ASSET_LABELS[p.asset_class] || p.asset_class || 'Other';
    buckets.set(label, (buckets.get(label) || 0) + Number(p.market_value || 0));
  });
  const cash = balances.reduce((s, b) => s + Number(b.cash_cad_combined || 0), 0);
  if (cash) buckets.set('Cash', (buckets.get('Cash') || 0) + cash);
  // Manual holdings get their own bucket and are NEVER distributed into
  // ETFs/Stocks/Cash. We do not know what is inside them, and guessing would
  // corrupt the Questrade reconciliation that currently lands exactly on its
  // total equity to the cent.
  //
  // direction !== 'debt' is load-bearing, not defensive: manual_holdings now
  // carries mortgages and loans for AUM, and an allocation is a breakdown of
  // what you OWN. A debt in here would be added to the portfolio as if it were
  // an asset and every share percentage below it would be wrong.
  const manualValue = manual.filter(h => h.status !== 'closed' && h.direction !== 'debt')
    .reduce((s2, h) => s2 + Number(h.current_value || 0), 0);
  if (manualValue) buckets.set('Manually tracked', manualValue);
  const total = [...buckets.values()].reduce((s, v) => s + v, 0);
  return [...buckets.entries()]
    .map(([label, value]) => ({ label, value, share: total ? (value / total) * 100 : 0 }))
    .sort((a, b) => b.value - a.value);
}

function InvestmentsPanel({ data, view, isOwner, isMobile, subTab, onSetSubTab, onGoUpload, loading, manualProps }) {
  const isBiz = view === 'business';
  const accent = isBiz ? C.navy : C.sage;

  if (loading && !data) return <div style={{ ...S.card, color:C.stone, fontSize:13 }}>Loading investments…</div>;

  const all = data || { balances:[], positions:[], periods:[], monthly:[], equity_series:[], contributions:[], snapshots:[], snapshot_dates:[], manual_holdings:[] };
  const inView = rows => (rows || []).filter(r => (r.view === 'business') === isBiz);

  const balances = inView(all.balances);
  const positions = inView(all.positions);
  const periods = inView(all.periods);
  const monthly = inView(all.monthly);
  const snapshotCount = (all.snapshot_dates || []).length;
  const manualAll = all.manual_holdings || [];
  // The Investments tab is about what is HELD. manual_holdings also carries the
  // debts behind AUM now (mortgage, loans, cards), and adding those to a
  // portfolio total would be silently, badly wrong — a $400k mortgage would
  // read as $400k of holdings. Split at the door, once, so nothing downstream
  // has to remember: everything in this component works from assets only, and
  // the debts are rendered in their own clearly-excluded block below.
  const isAsset = h => h.direction !== 'debt';
  const manualAssetsAll = manualAll.filter(isAsset);
  const manualDebts = inView(manualAll.filter(h => !isAsset(h)));
  const manual = inView(manualAssetsAll);
  // Closed holdings are worth nothing and count toward nothing; they stay
  // listed so the record of what happened to the money survives.
  const manualOpen = manual.filter(h => h.status !== 'closed');
  const manualValue = manualOpen.reduce((s2, h) => s2 + Number(h.current_value || 0), 0);
  const manualCombined = manualAssetsAll.filter(h => h.status !== 'closed')
    .reduce((s2, h) => s2 + Number(h.current_value || 0), 0);

  // Nothing uploaded at all — a different message from "this view is empty".
  // manualAll, not manualAssetsAll: a household with only a mortgage entered
  // has genuinely put something in, and telling it "no snapshots yet" while
  // hiding the row it just created would read as data loss.
  if (!all.balances.length && !all.periods.length && !manualAll.length) {
    return (
      <div style={{ ...S.card, textAlign:'center', padding:'40px 24px' }}>
        <div style={{ fontSize:32, marginBottom:8 }}>📈</div>
        <div style={{ fontSize:15, fontWeight:600, marginBottom:6 }}>No snapshots yet</div>
        <div style={{ fontSize:13, color:C.stone, lineHeight:1.6, maxWidth:420, margin:'0 auto 16px' }}>
          Upload <strong>InvestmentSummary.xlsx</strong> and <strong>InvestmentReturns.xlsx</strong> from
          Questrade → Reports. Holdings appear from the first upload; the value-over-time chart needs two.
        </div>
        {isOwner && <button style={S.btnPrimary(isBiz)} onClick={onGoUpload}>Go to Upload</button>}
      </div>
    );
  }

  // Data exists, but not for this view. Business is expected to be empty today.
  // manualDebts is in the test so a view holding only debts still renders — it
  // falls through to the panel below, where the debts block is the only thing
  // with anything to show. Without it those rows would be unreachable in the UI.
  if (!balances.length && !periods.length && !manual.length && !manualDebts.length) {
    return (
      <div style={{ ...S.card, textAlign:'center', padding:'36px 24px' }}>
        <div style={{ fontSize:28, marginBottom:8 }}>{isBiz ? '💼' : '🏠'}</div>
        <div style={{ fontSize:15, fontWeight:600, marginBottom:6 }}>
          No investment accounts in the {isBiz ? 'business' : 'household'} view
        </div>
        <div style={{ fontSize:13, color:C.stone, lineHeight:1.6, maxWidth:460, margin:'0 auto' }}>
          {isBiz
            ? <>This is expected, not a problem. The Simpler Times corporate account is not registered yet —
               once it is added in Admin → Accounts as an <strong>investment</strong> account and a Questrade
               export containing it is uploaded, it will appear here.</>
            : <>Your investment accounts are registered to the business view.</>}
        </div>
      </div>
    );
  }

  const sum = (rows, k) => rows.reduce((s, r) => s + Number(r[k] || 0), 0);
  const questradeEquity = sum(balances, 'total_equity_cad_combined');
  const totalEquity = questradeEquity + manualValue;
  const combinedEquity = sum(all.balances, 'total_equity_cad_combined') + manualCombined;
  const thisYear = new Date().getFullYear();
  const contribution = (all.contributions || []).find(c => c.year === thisYear && (c.view === 'business') === isBiz);

  return (
    <>
      {/* HEADER */}
      <div style={{ ...S.card, marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
          <div>
            <div style={{ fontSize:11, fontWeight:600, color:C.stoneMid, textTransform:'uppercase', letterSpacing:'.06em' }}>
              Total holdings · {isBiz ? '💼 Business' : '🏠 Household'}
            </div>
            <div style={{ fontSize:32, fontWeight:700, color:accent, marginTop:4 }}>{fmt(totalEquity)}</div>
            <div style={{ fontSize:11, color:C.stoneMid, marginTop:2 }}>
              {/* Spelled out so the figure is never read as Questrade alone. */}
              Questrade {fmt(questradeEquity)} as of {all.latest || '—'}
              {manualValue ? ` + manually tracked ${fmt(manualValue)}` : ''}
            </div>
            {/* The snapshot date carried no staleness cue at all, while the
                manual holdings below it have had one since Stage 4 — so the
                hand-maintained figures were held to a standard the imported
                one was not. Same 45-day automatic threshold the AUM tab uses,
                and the same reason for it being shorter than the manual 90:
                this going stale means an export is waiting to be uploaded,
                which is a different job from checking a value by hand. */}
            {(() => {
              const age = daysSince(all.latest);
              if (age === null || age <= AUM_STALE_AUTOMATIC) return null;
              return (
                <div style={{ fontSize:11, color:C.amber, fontWeight:700, marginTop:3 }}>
                  {age} days since the last Questrade export — upload one to refresh this
                </div>
              );
            })()}
          </div>
          {isOwner && (
            // Always visible, both views. The toggle governs the body; this line
            // surfaces the combined figure without pretending two legal entities
            // are one portfolio.
            <div style={{ background:C.warm, borderRadius:12, padding:'10px 14px', minWidth:190 }}>
              <div style={{ fontSize:10, fontWeight:700, color:C.stoneMid, textTransform:'uppercase', letterSpacing:'.06em' }}>
                Total across both views
              </div>
              <div style={{ fontSize:20, fontWeight:700, marginTop:3 }}>{fmt(combinedEquity)}</div>
              <div style={{ fontSize:10, color:C.stoneMid, marginTop:2 }}>
                Household + business, Questrade + manually tracked
              </div>
            </div>
          )}
        </div>
      </div>

      {/* SUB-TABS */}
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:16 }}>
        {INV_SUB_TABS.map(([key,label]) => (
          <button key={key} onClick={()=>onSetSubTab(key)}
            style={{ ...S.pill, background:subTab===key?accent:'white',
                     color:subTab===key?'white':C.stone,
                     borderColor:subTab===key?accent:C.warmDeep }}>{label}</button>
        ))}
      </div>

      {subTab === 'overview'
        ? <InvestmentsOverview balances={balances} positions={positions} contribution={contribution}
            thisYear={thisYear} accent={accent} isBiz={isBiz} isMobile={isMobile}
            manual={manual} manualDebts={manualDebts} isOwner={isOwner} manualProps={manualProps} />
        : <InvestmentsPerformance periods={periods} monthly={monthly} equitySeries={inView(all.equity_series)}
            snapshotCount={snapshotCount} accent={accent} isBiz={isBiz} isMobile={isMobile} />}
    </>
  );
}

function InvestmentsOverview({ balances, positions, contribution, thisYear, accent, isBiz, isMobile,
                               manual, manualDebts, isOwner, manualProps }) {
  const alloc = allocationOf(positions, balances, manual);
  const allocTotal = alloc.reduce((s, a) => s + a.value, 0);

  // Same symbol can sit in several accounts. Quantities add; cost per share and
  // percentage return do NOT, so gain on cost is recomputed from the summed
  // cost and value rather than averaged.
  const bySymbol = [];
  positions.forEach(p => {
    let row = bySymbol.find(r => r.symbol === p.symbol);
    if (!row) { row = { symbol:p.symbol, description:p.description, value:0, cost:0 }; bySymbol.push(row); }
    row.value += Number(p.market_value || 0);
    row.cost += Number(p.position_cost || 0);
  });
  bySymbol.sort((a, b) => b.value - a.value);

  return (
    <>
      {/* ACCOUNT CARDS */}
      <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'repeat(auto-fit,minmax(210px,1fr))', gap:12, marginBottom:16 }}>
        {balances.map(b => (
          <div key={b.account_id} style={{ ...S.card, margin:0 }}>
            <div style={{ fontSize:13, fontWeight:700, marginBottom:8 }}>{b.display_name}</div>
            <div style={{ fontSize:22, fontWeight:700, color:accent }}>{fmt(Number(b.total_equity_cad_combined))}</div>
            <div style={{ fontSize:11, color:C.stoneMid, marginTop:8, lineHeight:1.7 }}>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span>Market value</span><span style={{ color:C.ink }}>{fmt(Number(b.market_value_cad_combined))}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span>Cash</span><span style={{ color:C.ink }}>{fmt(Number(b.cash_cad_combined))}</span>
              </div>
            </div>
          </div>
        ))}
        <div style={{ ...S.card, margin:0, background:C.warm }}>
          <div style={{ fontSize:13, fontWeight:700, marginBottom:8 }}>Contributed in {thisYear}</div>
          <div style={{ fontSize:22, fontWeight:700, color:accent }}>{contribution ? fmt(contribution.net) : '—'}</div>
          <div style={{ fontSize:11, color:C.stoneMid, marginTop:8, lineHeight:1.5 }}>
            {contribution
              ? <>From {contribution.count} bank transfer{contribution.count===1?'':'s'} to Questrade. Deposits are
                 not in the export — this comes from your transactions.</>
              : <>No transfers to Questrade recorded in {thisYear} yet.</>}
          </div>
        </div>
      </div>

      {/* MANUAL HOLDINGS */}
      <ManualHoldingsSection holdings={manual} isOwner={isOwner} accent={accent}
        onAdd={manualProps.onAdd} onAction={manualProps.onAction} onClose={manualProps.onClose}
        onDelete={manualProps.onDelete} onHistory={manualProps.onHistory} />

      {/* DEBTS — tracked in the same table, deliberately NOT in any total on
          this tab. Rendered here only so the rows stay reachable until the AUM
          tab exists; nothing above or below this block includes them. */}
      {manualDebts.length > 0 && (
        <ManualHoldingsSection holdings={manualDebts} isOwner={isOwner} accent={C.coral}
          title="Debts"
          blurb={<>Not part of any figure on this tab — the totals above are what you <strong>hold</strong>.
            Balances are stored as what is owed and subtracted from your assets in the AUM view. Listed here
            so they stay reachable and their staleness stays visible.</>}
          onAction={manualProps.onAction} onClose={manualProps.onClose}
          onDelete={manualProps.onDelete} onHistory={manualProps.onHistory} />
      )}

      {/* ALLOCATION */}
      <div style={{ ...S.card, marginBottom:16 }}>
        <div style={S.secTitle}>Allocation</div>
        <div style={{ fontSize:11, color:C.stoneMid, marginBottom:12 }}>
          Computed from this snapshot, not stored. Cash is the accounts' cash balance; the CASH holding is a
          high-interest savings ETF and counts under ETFs. Manually tracked holdings sit in their own bucket —
          what is inside them is unknown, so they are not split across the others.
        </div>
        {alloc.map(a => (
          <div key={a.label} style={{ marginBottom:10 }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
              <span style={{ fontWeight:600 }}>{a.label}</span>
              <span style={{ color:C.stone }}>{fmt(a.value)} · {a.share.toFixed(1)}%</span>
            </div>
            <div style={{ height:8, background:C.warmDark, borderRadius:4, overflow:'hidden' }}>
              <div style={{ width:`${a.share}%`, height:'100%', background:accent }} />
            </div>
          </div>
        ))}
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, fontWeight:700,
                      borderTop:`1px solid ${C.warmDark}`, marginTop:12, paddingTop:10 }}>
          <span>Total</span><span>{fmt(allocTotal)}</span>
        </div>

        <div style={{ ...S.secTitle, marginTop:20 }}>By holding</div>
        <div style={{ fontSize:11, color:C.stoneMid, marginBottom:12 }}>
          Questrade positions only — a manually tracked holding has no position breakdown.
        </div>
        {bySymbol.map(r => {
          const share = allocTotal ? (r.value / allocTotal) * 100 : 0;
          return (
            <div key={r.symbol} style={{ marginBottom:10 }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4, gap:8 }}>
                <span style={{ fontWeight:600 }}>{r.symbol}</span>
                <span style={{ color:C.stone }}>{fmt(r.value)} · {share.toFixed(1)}%</span>
              </div>
              <div style={{ height:6, background:C.warmDark, borderRadius:3, overflow:'hidden' }}>
                <div style={{ width:`${share}%`, height:'100%', background:accent, opacity:.7 }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* HOLDINGS */}
      <div style={S.card}>
        <div style={S.secTitle}>Holdings</div>
        <div style={{ fontSize:11, color:C.stoneMid, marginBottom:12 }}>
          "Gain on cost" is this position's profit over what was paid for it. It is a different denominator from
          the account return on the Performance tab, which divides by total equity including idle cash. Both are
          correct; they are not the same number.
        </div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:640 }}>
            <thead>
              <tr style={{ textAlign:'left', color:C.stoneMid, fontSize:10, textTransform:'uppercase', letterSpacing:'.05em' }}>
                <th style={{ padding:'6px 8px 6px 0' }}>Symbol</th>
                <th style={{ padding:'6px 8px' }}>Description</th>
                <th style={{ padding:'6px 8px' }}>Account</th>
                <th style={{ padding:'6px 8px', textAlign:'right' }}>Quantity</th>
                <th style={{ padding:'6px 8px', textAlign:'right' }}>Market value</th>
                <th style={{ padding:'6px 0 6px 8px', textAlign:'right' }}>Gain on cost</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((p, i) => (
                <tr key={`${p.account_id}-${p.symbol}-${i}`} style={{ borderTop:`1px solid ${C.warmDark}` }}>
                  <td style={{ padding:'8px 8px 8px 0', fontWeight:700 }}>{p.symbol}</td>
                  <td style={{ padding:'8px', color:C.stone }}>{p.description}</td>
                  <td style={{ padding:'8px', color:C.stone, whiteSpace:'nowrap' }}>{p.display_name}</td>
                  <td style={{ padding:'8px', textAlign:'right' }}>{Number(p.quantity).toLocaleString('en-CA')}</td>
                  <td style={{ padding:'8px', textAlign:'right', fontWeight:600 }}>{fmt(Number(p.market_value))}</td>
                  <td style={{ padding:'8px 0 8px 8px', textAlign:'right', fontWeight:600, color:pctColor(p.pct_return) }}>
                    {pct(p.pct_return)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function InvestmentsPerformance({ periods, monthly, equitySeries, snapshotCount, accent, isBiz, isMobile }) {
  // One line per account across the 12 months, newest snapshot winning per month.
  const accounts = [...new Set(monthly.map(m => m.display_name))].sort();
  const months = [...new Set(monthly.map(m => m.month))].sort();
  const chartData = months.map(mo => {
    const row = { month: mo.slice(0, 7) };
    accounts.forEach(name => {
      const hit = monthly.find(m => m.month === mo && m.display_name === name);
      row[name] = hit && hit.pct !== null ? Number(hit.pct) : null;
    });
    return row;
  });

  const periodAccounts = [...new Set(periods.map(p => p.display_name))].sort();
  const equityByDate = [...new Set(equitySeries.map(e => e.snapshot_date))].sort().map(d => ({
    date: d,
    equity: equitySeries.filter(e => e.snapshot_date === d).reduce((s, e) => s + Number(e.equity || 0), 0),
  }));

  return (
    <>
      {/* Manually tracked holdings are absent from everything below, and that is
          deliberate — there is no return data behind them. Said out loud so the
          gap reads as a decision rather than a bug. */}
      <div style={{ ...S.card, marginBottom:16, background:C.warm }}>
        <div style={{ fontSize:12, color:C.stone, lineHeight:1.6 }}>
          <strong>Performance covers Questrade accounts only.</strong> Manually tracked holdings have no return
          history or snapshot series behind them, so they are excluded from the chart, the table and the value
          history below. They still count toward the totals on the Overview tab.
        </div>
      </div>

      {/* 12-MONTH RETURN */}
      <div style={{ ...S.card, marginBottom:16 }}>
        <div style={S.secTitle}>Monthly return, last 12 months</div>
        <div style={{ fontSize:11, color:C.stoneMid, marginBottom:12 }}>
          Money-weighted, per account. Where two snapshots report the same month the newer one is shown — a
          month's return is recalculated as later deposits land, and both are kept.
        </div>
        {chartData.length === 0 ? (
          <div style={{ fontSize:13, color:C.stone }}>No monthly returns yet — upload InvestmentReturns.xlsx.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.warmDark} />
              <XAxis dataKey="month" tick={{ fontSize:10, fill:C.stoneMid }} />
              <YAxis tick={{ fontSize:10, fill:C.stoneMid }} tickFormatter={v=>`${v}%`} />
              <Tooltip formatter={(v, n) => [v === null ? '—' : `${Number(v).toFixed(2)}%`, n]} />
              <Legend wrapperStyle={{ fontSize:11 }} />
              <ReferenceLine y={0} stroke={C.stoneMid} />
              {accounts.map((name, i) => (
                <Line key={name} type="monotone" dataKey={name} stroke={INV_LINE_COLORS[i % INV_LINE_COLORS.length]}
                      strokeWidth={2} dot={false} connectNulls={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* PERIOD RETURNS */}
      <div style={{ ...S.card, marginBottom:16 }}>
        <div style={S.secTitle}>Account return (money-weighted)</div>
        <div style={{ fontSize:11, color:C.stoneMid, marginBottom:12 }}>
          Divided by total equity including idle cash — not the same as "Gain on cost" in the holdings table.
          A dash means the account is younger than that window, which is not a zero return.
        </div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, minWidth:640 }}>
            <thead>
              <tr style={{ textAlign:'right', color:C.stoneMid, fontSize:10, textTransform:'uppercase', letterSpacing:'.05em' }}>
                <th style={{ padding:'6px 8px 6px 0', textAlign:'left' }}>Account</th>
                {PERIOD_ORDER.map(([k, label]) => <th key={k} style={{ padding:'6px 8px', whiteSpace:'nowrap' }}>{label}</th>)}
              </tr>
            </thead>
            <tbody>
              {periodAccounts.map(name => (
                <tr key={name} style={{ borderTop:`1px solid ${C.warmDark}` }}>
                  <td style={{ padding:'8px 8px 8px 0', fontWeight:600, whiteSpace:'nowrap' }}>{name}</td>
                  {PERIOD_ORDER.map(([k]) => {
                    const hit = periods.find(p => p.display_name === name && p.period === k);
                    const v = hit ? hit.pct : null;
                    return (
                      <td key={k} style={{ padding:'8px', textAlign:'right', fontWeight:600, color:pctColor(v) }}>
                        {pct(v)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* VALUE OVER TIME */}
      <div style={S.card}>
        <div style={S.secTitle}>Total equity over time</div>
        {snapshotCount < 2 ? (
          // A single point drawn as a line reads as a flat portfolio. Say what is
          // actually true instead: there is nothing to compare to yet.
          <div style={{ background:C.warm, borderRadius:12, padding:'28px 20px', textAlign:'center' }}>
            <div style={{ fontSize:24, marginBottom:8 }}>📅</div>
            <div style={{ fontSize:14, fontWeight:600, marginBottom:6 }}>Upload monthly to build history</div>
            <div style={{ fontSize:12, color:C.stone, lineHeight:1.6, maxWidth:400, margin:'0 auto' }}>
              {snapshotCount === 1
                ? <>One snapshot so far ({equityByDate[0]?.date}, {fmt(equityByDate[0]?.equity || 0)}). A second one
                   gives this chart a line and turns the change figures below into real numbers.</>
                : <>No snapshots yet.</>}
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={equityByDate} margin={{ top:5, right:10, left:0, bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.warmDark} />
              <XAxis dataKey="date" tick={{ fontSize:10, fill:C.stoneMid }} />
              <YAxis tick={{ fontSize:10, fill:C.stoneMid }} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={v=>fmt(Number(v))} />
              <Line type="monotone" dataKey="equity" stroke={accent} strokeWidth={2} dot={{ r:3 }} />
            </LineChart>
          </ResponsiveContainer>
        )}

        {/* DELTAS */}
        <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'repeat(3,1fr)', gap:12, marginTop:16 }}>
          {[['Past week',7], ['Past month',30], ['Past year',365]].map(([label, days]) => {
            const d = equityDelta(equityByDate, days);
            return (
              <div key={label} style={{ background:C.warm, borderRadius:12, padding:'12px 14px' }}>
                <div style={{ fontSize:10, fontWeight:700, color:C.stoneMid, textTransform:'uppercase', letterSpacing:'.06em' }}>{label}</div>
                <div style={{ fontSize:18, fontWeight:700, marginTop:4, color: d === null ? C.stoneMid : d >= 0 ? C.sage : C.coral }}>
                  {d === null ? '—' : `${d >= 0 ? '+' : '-'}${fmt(Math.abs(d)).replace('-','')}`}
                </div>
                <div style={{ fontSize:10, color:C.stoneMid, marginTop:2 }}>
                  {d === null ? 'Needs a second snapshot' : 'Change in total equity'}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

// Null until there are genuinely two snapshots spanning the window. A delta
// invented from one point is worse than no delta.
function equityDelta(series, days) {
  if (!series || series.length < 2) return null;
  const latest = series[series.length - 1];
  const cutoff = new Date(latest.date);
  cutoff.setDate(cutoff.getDate() - days);
  const iso = cutoff.toISOString().slice(0, 10);
  const prior = [...series].reverse().find(p => p.date <= iso);
  if (!prior) return null;
  return latest.equity - prior.equity;
}

function BudgetTargets({ settings, onSave }) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const active = draft ? { ...settings, ...draft } : settings;
  const n = parseInt(active.needs_pct) || 0;
  const w = parseInt(active.wants_pct) || 0;
  const s = parseInt(active.savings_pct) || 0;
  const sum = n + w + s;
  const valid = sum === 100;
  return (
    <>
      <div style={{ fontSize:12, color:C.stone, marginBottom:14 }}>
        How much of monthly spending should go to each bucket. Must total 100%.
      </div>
      <div style={{ display:'flex', gap:12, marginBottom:10, flexWrap:'wrap' }}>
        {BUCKETS.map(b => (
          <div key={b} style={{ flex:1, minWidth:80 }}>
            <div style={S.modalLabel}>{BUCKET_LABELS[b]} %</div>
            <input type="number" min="0" max="100"
              value={active[b + '_pct'] ?? 0}
              onChange={e => { setSaveErr(''); setDraft(p => ({ ...(p || settings), [b + '_pct']: e.target.value })); }}
              style={{ ...S.input, width:'100%' }} />
          </div>
        ))}
      </div>
      <div style={{ fontSize:12, marginBottom:10, fontWeight:600,
                    color: valid ? C.sage : C.coral }}>
        {sum} / 100{valid ? ' ✓' : sum > 100 ? ` — over by ${sum - 100}` : ` — ${100 - sum} remaining`}
      </div>
      {saveErr && <div style={{ fontSize:12, color:C.coral, marginBottom:8 }}>{saveErr}</div>}
      <button
        style={{ ...S.btnPrimary(false), opacity: valid && !saving ? 1 : 0.5,
                 cursor: valid && !saving ? 'pointer' : 'not-allowed' }}
        disabled={!valid || saving}
        onClick={async () => {
          setSaving(true); setSaveErr('');
          try {
            await onSave({ needs_pct: n, wants_pct: w, savings_pct: s });
            setDraft(null);
          } catch (e) { setSaveErr(e.message); }
          setSaving(false);
        }}>
        {saving ? 'Saving…' : valid ? 'Save targets' : `Save (${sum} / 100)`}
      </button>
    </>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  // 'household' | 'business'
  const [view, setView] = useState('household');
  const [tab, setTab] = useState('dashboard');
  const [month, setMonth] = useState(() => { const n=new Date(); return { y:n.getFullYear(), m:n.getMonth() }; });

  // Data
  const [allTxs, setAllTxs] = useState([]);
  const [rules, setRules] = useState([]);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [importLog, setImportLog] = useState({ rows:[], total:0, page:1, pages:1 });
  const [confirmDeleteBatch, setConfirmDeleteBatch] = useState(null);
  const [deletingBatch, setDeletingBatch] = useState(false);
  const [customCats, setCustomCats] = useState([]);
  const [medTotal, setMedTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // UI state
  const [selected, setSelected] = useState(null);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [ruleInitial, setRuleInitial] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('All');
  const [filterAccount, setFilterAccount] = useState('All');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBucket, setFilterBucket] = useState(null);
  const [filterEnvelope, setFilterEnvelope] = useState(null);
  // 'income' | 'spending' | null — routes the transaction list through
  // isKpiIncomeRow/isKpiSpendingRow, the same predicate the KPI cards sum.
  const [filterKpi, setFilterKpi] = useState(null);
  // Set of transaction ids, or null — how a Monthly Analysis finding points at
  // its exact rows rather than the nearest broad category/status.
  const [filterIds, setFilterIds] = useState(null);
  const [settings, setSettings] = useState({ needs_pct: 50, wants_pct: 30, savings_pct: 20 });
  // 'date' | 'description' | 'amount' | 'amountAbs'
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [showHidden, setShowHidden] = useState(false);
  // Page lives here rather than in TxListPanel so resetFilters() can clear it
  // alongside the filters it belongs to.
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [uploadState, setUploadState] = useState('idle');
  const [uploadMsg, setUploadMsg] = useState('');
  const [isDrag, setIsDrag] = useState(false);
  const [newCat, setNewCat] = useState({ name:'', icon:'📦', color:C.sage, view:'both', type:'expense', bucket:'wants' });
  // id of the custom category currently showing its inline delete prompt
  const [delCatId, setDelCatId] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [envelopes, setEnvelopes] = useState([]);
  // All-months rows for the active envelope filter, fetched on demand. The
  // envelope card is all-time, so its filtered list must ignore the selected
  // month — and the client only ever holds one month in allTxs, so this needs
  // its own fetch. Empty whenever no envelope filter is active.
  const [envTxs, setEnvTxs] = useState([]);
  const [newEnv, setNewEnv] = useState({ name:'', budget:'', view:'household', notes:'' });
  const [envDelete, setEnvDelete] = useState(null);
  const [envError, setEnvError] = useState('');
  const [trends, setTrends] = useState([]);
  const [trendRange, setTrendRange] = useState('6M');
  const [preview, setPreview] = useState(null);
  const [adminTab, setAdminTab] = useState('upload');
  const [pendingFiles, setPendingFiles] = useState(null);
  const [invData, setInvData] = useState(null);
  // AUM is fetched separately from loadAll because it is the one payload that is
  // view-scoped server-side — the endpoint refuses the business view outright
  // for non-owners rather than filtering it in the client.
  const [aumData, setAumData] = useState(null);
  const [aumLoading, setAumLoading] = useState(false);
  const [aumError, setAumError] = useState('');
  // Manual-holding modals. `mhEdit` carries {holding, action}: a null holding
  // means "add", and action is the pre-chosen event type for a row button.
  const [mhEdit, setMhEdit] = useState(null);
  const [mhClose, setMhClose] = useState(null);
  const [mhDelete, setMhDelete] = useState(null);
  const [mhHistory, setMhHistory] = useState(null);
  const [mhEvents, setMhEvents] = useState([]);
  const [mhLoadingEvents, setMhLoadingEvents] = useState(false);
  const [mhBusy, setMhBusy] = useState(false);
  const [mhError, setMhError] = useState('');
  const [invSubTab, setInvSubTab] = useState('overview');
  const [acctError, setAcctError] = useState('');
  const [invPreview, setInvPreview] = useState(null);
  const [pendingInvFiles, setPendingInvFiles] = useState(null);
  const [newAcct, setNewAcct] = useState({ display_name:'', account_type:'chequing', view:'household', identifier:'' });
  const fileRef = useRef();
  const isMobile = useIsMobile();

  const canEdit = user?.role === 'owner' || user?.role === 'family';
  const isOwner = user?.role === 'owner';
  const isBiz = view === 'business';

  useEffect(() => {
    api.me().then(u => { setUser(u); setAuthLoading(false); }).catch(() => setAuthLoading(false));
  }, []);

  useEffect(() => { if (user) loadAll(); }, [user, month, showHidden, trendRange]);
  // Not month-scoped: AUM is a position at a point in time, not a period, so the
  // month navigator does not apply to it.
  useEffect(() => { if (user) loadAum(view); }, [user, view]);

  // Any narrowing of the list invalidates the current page — landing on page 4
  // of a 1-page result renders blank, which reads as "the filter broke". An
  // effect rather than wrapping six setters, so a future filter can't miss it.
  // Sorting doesn't narrow the list, but it does change which rows page 3 holds —
  // re-sorting and staying deep in the pages is disorienting, so reset too.
  useEffect(() => { setPage(1); },
    [filterStatus, filterCat, filterAccount, filterBucket, filterEnvelope, filterKpi, filterIds, search, showHidden, month.y, month.m, view, sortBy, sortDir]);

  // Fetch all-months rows for the active envelope filter; clear when it's off.
  // Keyed on filterEnvelope alone — the month deliberately does not refetch this,
  // because an envelope filter is month-agnostic. Refetches when switching pots.
  useEffect(() => {
    if (!filterEnvelope) { setEnvTxs([]); return; }
    let cancelled = false;
    api.getTxsByEnvelope(filterEnvelope)
      .then(rows => { if (!cancelled) setEnvTxs(rows); })
      .catch(e => { if (!cancelled) { console.error(e); setEnvTxs([]); } });
    return () => { cancelled = true; };
  }, [filterEnvelope]);

  async function loadAll() {
    setLoading(true);
    try {
      const emptyLog = { rows:[], total:0, page:1, pages:1 };
      // YTD anchors on today's real-world month, not the navigator's selected
      // month — it means "this calendar year so far", same as the toggle label says.
      const trendMonths = TREND_RANGES[trendRange] === 'ytd' ? new Date().getMonth() + 1 : (TREND_RANGES[trendRange] || 6);
      const [txData, ruleData, catData, medData, errData, acctData, trendData, logData, invRes, settingsData, envData] = await Promise.all([
        api.getTxs(month.y, String(month.m+1), showHidden),
        api.getRules(),
        api.getCats(),
        api.getMedical(),
        user?.role === 'owner' ? api.getUploadErrors().catch(() => []) : Promise.resolve([]),
        api.getAccounts().catch(() => []),
        api.getTrends(trendMonths).catch(() => []),
        user?.role === 'owner' ? api.getImportLog(1).catch(() => emptyLog) : Promise.resolve(emptyLog),
        api.getInvestments().catch(() => null),
        api.getSettings().catch(() => ({ needs_pct: 50, wants_pct: 30, savings_pct: 20 })),
        api.getEnvelopes().catch(() => []),
      ]);
      setAccounts(acctData);
      setEnvelopes(envData);
      setTrends(trendData);
      setAllTxs(txData);
      setRules(ruleData);
      setCustomCats(catData);
      setUploadErrors(errData);
      setImportLog(logData);
      setInvData(invRes);
      setSettings(settingsData);
      setMedTotal(parseFloat(medData?.total||0));
    } catch(e) { console.error(e); }
    setLoading(false);
  }

  async function loadImportLog(page) {
    try { setImportLog(await api.getImportLog(page)); } catch(e) { console.error(e); }
  }

  // Reloaded on every view switch: household and business are separate ledgers
  // here, and showing one view's total under the other's label would be worse
  // than showing nothing. Errors are kept rather than swallowed — an AUM figure
  // that silently falls back to null would render as "nothing under management",
  // which is a claim, not an absence.
  async function loadAum(currentView) {
    setAumLoading(true); setAumError('');
    try { setAumData(await api.getAum(currentView)); }
    catch (e) { setAumData(null); setAumError(e.message); }
    setAumLoading(false);
  }

  // Envelope spent/span are derived server-side across all time, so a tag/untag
  // must refetch rather than recompute from the current month's txs alone.
  async function reloadEnvelopes() {
    try { setEnvelopes(await api.getEnvelopes()); } catch(e) { console.error(e); }
  }

  async function handleUndoImport() {
    if (!confirmDeleteBatch) return;
    setDeletingBatch(true);
    try {
      await api.deleteImportBatch(confirmDeleteBatch.batch_id);
      setConfirmDeleteBatch(null);
      await loadAll();
    } catch(e) { console.error(e); }
    setDeletingBatch(false);
  }

  // Split transactions by view
  const bizTxs = allTxs.filter(t => isBusinessAccount(t.account, accounts));
  const houseTxs = allTxs.filter(t => !isBusinessAccount(t.account, accounts));
  const viewTxs = isBiz ? bizTxs : houseTxs;

  const visibleTxs = viewTxs.filter(t => !t.transfer_hide);
  const pendingTxs = visibleTxs.filter(t => t.status==='pending');
  const hiddenTxs = viewTxs.filter(t => t.transfer_hide);

  // When an envelope filter is active, the Transactions list is fed from the
  // all-months envelope fetch instead of the month-scoped set — LIST ONLY. The
  // dashboard, buckets, pills and pending count stay month-scoped (allTxs is
  // untouched). Split by view + hidden the same way visibleTxs/hiddenTxs are, so
  // the "Show hidden" toggle keeps working within the set.
  const envViewTxs   = envTxs.filter(t => isBusinessAccount(t.account, accounts) === isBiz);
  const envListTxs   = envViewTxs.filter(t => !t.transfer_hide);
  const envHiddenTxs = envViewTxs.filter(t => t.transfer_hide);
  const listTxs    = filterEnvelope ? envListTxs   : visibleTxs;
  const listHidden = filterEnvelope ? envHiddenTxs : hiddenTxs;

  // Derived, not fetched. The old /api/summary aggregated every account into one
  // list, so the view had to be guessed from the category name — which silently
  // dropped any category present in both CATS and BIZ_CATS from the household
  // side. visibleTxs is already split by account and already excludes
  // transfer_hide rows, matching what the server query filtered on, and being
  // derived means it can never lag a save.
  const viewSummary = (() => {
    const by = new Map();
    visibleTxs.forEach(t => {
      const r = by.get(t.category) || { category: t.category, total: 0, count: 0 };
      r.total += parseFloat(t.amount);
      r.count += 1;
      by.set(t.category, r);
    });
    return [...by.values()].sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
  })();

  // Pills derive from TYPE, not sign, and rank on |net| rather than outflow.
  // Ranking on outflow would still hide Savings & Investing this month: it has
  // zero outflow (2 rows, both positive) yet the largest movement of any expense
  // category. This is the identical list and order the dashboard breakdown uses,
  // so every category clickable there has a pill here to highlight.
  const sortedCats = viewSummary
    .filter(r => isExpenseCat(r.category, view, customCats))
    .map(r => [r.category, Math.abs(parseFloat(r.total))])
    .sort((a,b)=>b[1]-a[1]);
  // Income never takes a pill slot; it joins the dropdown's "With activity" group.
  const incomeCats = viewSummary
    .filter(r => isIncomeCat(r.category, view, customCats))
    .map(r => [r.category, Math.abs(parseFloat(r.total))])
    .sort((a,b)=>b[1]-a[1]);

  function handleTxUpdate(updated) {
    setAllTxs(prev => prev.map(t => t.id===updated.id ? updated : t));
    // Functional form reads the *current* selection rather than the one captured
    // when the modal rendered — otherwise a PATCH that resolves after the modal
    // closes re-opens it with a stale object.
    setSelected(prev => (prev && prev.id===updated.id ? updated : prev));
  }

  async function handleConfirm(id) {
    const tx = allTxs.find(t=>t.id===id);
    if (!tx) return;
    // The pill already routes uncategorized rows to the modal; this is the
    // choke point so no other caller can confirm one silently.
    if (isUncategorized(tx.category)) { setSelected(tx); return; }
    await api.updateTx(id, { status:'confirmed' });
    handleTxUpdate({ ...tx, status:'confirmed' });
  }

  async function handleConfirmAll() {
    await api.confirmAll(month.y, String(month.m+1));
    setAllTxs(prev => prev.map(t =>
      isBusinessAccount(t.account, accounts)===isBiz && t.status==='pending'
        ? { ...t, status:'confirmed' } : t
    ));
  }

  async function handleAddRule(keyword, category, view) {
    await api.addRule(keyword, category, view);
    const [updated, txData] = await Promise.all([api.getRules(), api.getTxs(month.y, String(month.m+1), showHidden)]);
    setRules(updated); setAllTxs(txData);
    setShowRuleModal(false);
  }

  // Files are re-sent on confirm rather than cached server-side, so a restart
  // between preview and import can't strand a half-finished upload.
  async function handleFiles(files) {
    const arr = Array.from(files);
    if (!arr.length) return;

    // Routed by extension. The two flows ask different questions — a statement
    // asks "which account is this?", a snapshot asks "what date is this?" — so
    // a mixed drop would have to open both modals at once. Ask for them
    // separately instead of guessing an order.
    const isXlsx = f => /\.xlsx$/i.test(f.name);
    const invFiles = arr.filter(isXlsx);
    const stmtFiles = arr.filter(f => !isXlsx(f));
    if (invFiles.length && stmtFiles.length) {
      setUploadMsg('Upload Questrade exports (.xlsx) separately from bank statements — they ask different questions.');
      setUploadState('error');
      setTimeout(()=>setUploadState('idle'), 6000);
      return;
    }
    if (invFiles.length) return handleInvestmentFiles(invFiles);

    setUploadState('scanning'); setUploadMsg('Reading statements...');
    try {
      const p = await api.previewUpload(arr);
      setPendingFiles(arr); setPreview(p); setUploadState('idle');
    } catch(e) {
      setUploadMsg(`Could not read files: ${e.message}`);
      setUploadState('error');
      setTimeout(()=>setUploadState('idle'), 5000);
    }
  }

  async function handleInvestmentFiles(arr) {
    setUploadState('scanning'); setUploadMsg('Reading Questrade export...');
    try {
      const p = await api.previewInvestments(arr);
      setPendingInvFiles(arr); setInvPreview(p); setUploadState('idle');
    } catch(e) {
      setUploadMsg(`Could not read files: ${e.message}`);
      setUploadState('error');
      setTimeout(()=>setUploadState('idle'), 5000);
    }
  }

  async function handleConfirmInvestmentImport(snapshotDate) {
    setUploadState('uploading');
    try {
      const r = await api.importInvestments(pendingInvFiles, snapshotDate);
      const bits = [`Imported ${r.written} rows for ${r.snapshot_date}`];
      if (r.replaced) bits.push(`${r.replaced} replaced`);
      if (r.skipped_accounts.length) bits.push(`${r.skipped_accounts.length} account(s) skipped`);
      if (r.unknown_accounts.length) bits.push(`${r.unknown_accounts.length} not in registry`);
      setUploadMsg(bits.join(' · '));
      setUploadState('done');
      setInvPreview(null); setPendingInvFiles(null);
      await loadAll();
      setTimeout(()=>setUploadState('idle'), 6000);
    } catch(e) {
      setUploadMsg(`Import failed: ${e.message}`);
      setUploadState('error');
      setInvPreview(null); setPendingInvFiles(null);
      setTimeout(()=>setUploadState('idle'), 8000);
    }
  }

  async function handleConfirmImport(overrides, excludedIds, forceImport) {
    setUploadState('uploading');
    try {
      const result = await api.upload(pendingFiles, overrides, excludedIds, forceImport);
      const bits = [`Imported ${result.imported} transactions (${result.duplicates} duplicates skipped)`];
      // Should be rare in practice — the modal's own refusal block already
      // requires "Import anyway" before this request is ever sent — but the
      // server re-checks independently, so a stale preview (another import
      // landed in between) can still block here. Surface it rather than
      // silently under-import.
      if (result.blocked?.length) bits.push(`${result.blocked.length} account row set${result.blocked.length===1?'':'s'} blocked by a source conflict — not imported`);
      setUploadMsg(bits.join(' · '));
      setUploadState('done');
      setPreview(null); setPendingFiles(null);
      await loadAll();
      setTimeout(()=>setUploadState('idle'), 5000);
    } catch(e) {
      setUploadMsg(`Upload failed: ${e.message}`);
      setUploadState('error');
      setPreview(null); setPendingFiles(null);
      setTimeout(()=>setUploadState('idle'), 5000);
    }
  }

  // Every manual-holding write goes through here: the server owns validation
  // and the event write, and loadAll() re-reads the payload afterwards so the
  // header total and the row can never disagree with what was stored.
  async function runManual(fn) {
    setMhBusy(true); setMhError('');
    try {
      await fn();
      setMhEdit(null); setMhClose(null); setMhDelete(null);
      await loadAll();
    } catch (e) {
      setMhError(e.message);
    }
    setMhBusy(false);
  }

  function handleManualSave(form) {
    const payload = {
      name: form.name, institution: form.institution || null, account_type: form.account_type || null,
      owner: form.owner || null, view: form.view, notes: form.notes || null, as_of: form.as_of,
      direction: form.direction || 'asset', group_name: form.group_name || null,
    };
    if (!mhEdit?.holding) {
      return runManual(() => api.addManualHolding({ ...payload, current_value: Number(form.new_value) }));
    }
    // direction is deliberately NOT sent on an update. The modal doesn't offer
    // it once a holding exists, and reclassifying asset↔debt re-signs every
    // past AUM figure the event log produces — a correction that big should be
    // a deliberate act, not a side effect of recording a payment.
    return runManual(() => api.updateManualHolding(mhEdit.holding.id, {
      event_type: form.event_type,
      new_value: Number(form.new_value),
      amount: form.event_type === 'revaluation' ? null : Number(form.amount),
      as_of: form.as_of, notes: form.notes || null,
    }));
  }

  async function openManualHistory(h) {
    setMhHistory(h); setMhEvents([]); setMhLoadingEvents(true);
    try { setMhEvents(await api.getManualEvents(h.id)); } catch (e) { console.error(e); }
    setMhLoadingEvents(false);
  }

  function resetFilters() { setSearch(''); setFilterCat('All'); setFilterAccount('All'); setFilterStatus('all'); setFilterBucket(null); setFilterEnvelope(null); setFilterKpi(null); setFilterIds(null); setSortBy('date'); setSortDir('desc'); setPage(1); }

  // Routing in from the dashboard sets ONE filter and clears the rest. Leaving
  // the others live means a filter set on an earlier visit silently intersects
  // with the new one, and the list reads as empty for no visible reason.
  // `ids` and `account` exist for the Monthly Analysis card: a finding points at
  // exact rows (a near-duplicate pair, a specific sign-bug row) or at one
  // account's activity this month, neither of which the other filters express.
  function openTxs({ cat = 'All', status = 'all', bucket = null, envelope = null, kpi = null, ids = null, account = null } = {}) {
    setFilterCat(cat); setFilterStatus(status); setFilterBucket(bucket); setFilterEnvelope(envelope); setFilterKpi(kpi);
    setFilterIds(ids ? new Set(ids) : null);
    // A near-duplicate or sign-bug row (Monthly Analysis checks 2-7) can be a
    // transfer_hide row itself — check 2 deliberately scans hidden rows too —
    // and the list hides those by default. An ids-filter that silently shows
    // fewer rows than the finding named is worse than useless, so force the
    // toggle on whenever a finding points at exact rows.
    if (ids) setShowHidden(true);
    setFilterAccount(account || 'All'); setSearch(''); setPage(1);
    setTab('transactions');
  }

  if (authLoading) return (
    <div style={{ minHeight:'100vh', background:C.cream, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'sans-serif' }}>
      <div style={{ color:C.stone }}>Loading...</div>
    </div>
  );
  if (!user) return <LoginScreen onLogin={setUser} />;

  const accentColor = isBiz ? C.navy : C.sage;
  const pendingHouse = houseTxs.filter(t=>!t.transfer_hide&&t.status==='pending').length;
  const pendingBiz = bizTxs.filter(t=>!t.transfer_hide&&t.status==='pending').length;

  const navTabs = [
    { key:'dashboard', label:'Dashboard' },
    { key:'transactions', label:`Transactions${pendingTxs.length>0?` · ${pendingTxs.length}`:''}`, amber:pendingTxs.length>0 },
    { key:'investments', label:'📈 Investments' },
    // Its own tab rather than a sub-tab of Investments: AUM carries a mortgage
    // and a chequing balance, and neither is an investment. Filing them under a
    // tab labelled 📈 Investments is a category error, and that tab's
    // "No snapshots yet" guard keys on Questrade data, so AUM would disappear
    // behind an empty state that has nothing to do with it.
    { key:'aum', label:'🌿 AUM' },
    ...(isOwner ? [{ key:'admin', label:'⚙️ Admin' }] : []),
  ];

  return (
    <div style={S.app}>
      {/* NAV */}
      <nav style={{ ...S.nav, ...(isMobile ? { flexWrap:'wrap', gap:8, padding:'8px 14px' } : {}) }}>
        <div style={S.logo}>🌿 Tally</div>

        {/* VIEW TOGGLE */}
        <div style={{ display:'flex', gap:0, marginRight:20, background:C.warm, borderRadius:10, padding:3 }}>
          {[['household','🏠 Household'+(pendingHouse>0?` · ${pendingHouse}`:'')],['business','💼 Business'+(pendingBiz>0?` · ${pendingBiz}`:'')]].map(([v,l]) => (
            <button key={v} onClick={()=>{ setView(v); setTab('dashboard'); resetFilters(); }}
              style={{ padding:'6px 14px', borderRadius:8, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:600,
                background: view===v ? (v==='business'?C.navy:C.sage) : 'transparent',
                color: view===v ? 'white' : C.stone, transition:'all .15s' }}>
              {l}
            </button>
          ))}
        </div>

        {!isMobile && navTabs.map(({ key, label, amber }) => (
          <button key={key} onClick={()=>setTab(key)}
            style={{ ...S.navTab, ...(tab===key ? S.navTabActive(isBiz) : {}), ...(amber?{color:C.amber}:{}) }}>
            {label}
          </button>
        ))}
        <div style={{ flex:1 }} />
        {!isMobile && (
          <div style={{ fontSize:13, color:C.stone, marginRight:12 }}>
            {user.name} · <span style={{ fontSize:11, background:C.warm, padding:'2px 6px', borderRadius:8, color:C.stone }}>{user.role}</span>
          </div>
        )}
        <button style={{ ...S.btnGhost, fontSize:12, padding:'6px 12px' }} onClick={()=>{ api.logout(); setUser(null); }}>Sign out</button>
      </nav>

      {/* BANNERS */}
      {uploadState!=='idle' && (
        <div style={{ background:uploadState==='done'?C.sageLight:uploadState==='error'?C.coralLight:C.goldLight, color:uploadState==='done'?C.sage:uploadState==='error'?C.coral:C.gold, padding:'10px 24px', textAlign:'center', fontSize:13, fontWeight:600 }}>
          {uploadState==='uploading'?'⏳ Importing...':uploadState==='scanning'?'⏳ Reading statements...':uploadMsg}
        </div>
      )}
      {pendingTxs.length>0 && tab==='dashboard' && (
        <div style={{ background:C.amberLight, borderBottom:`1px solid ${C.gold}22`, padding:'9px 20px', display:'flex', alignItems:'center', justifyContent:'flex-start', cursor:'pointer' }}
          onClick={()=>{ setFilterStatus('pending'); setTab('transactions'); }}>
          <span style={{ fontSize:13, color:C.amber, fontWeight:600 }}>🔍 {pendingTxs.length} transactions need review — tap to review</span>
        </div>
      )}

      <div style={{ ...S.content, ...(isMobile ? { paddingBottom:88 } : {}) }}>

        {/* ── DASHBOARD ── */}
        {tab==='dashboard' && (
          <>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:10 }}>
              <div style={{ ...S.monthNav, marginBottom:0 }}>
                <button style={S.monthBtn} onClick={()=>setMonth(p=>{ const d=new Date(p.y,p.m-1,1); return {y:d.getFullYear(),m:d.getMonth()}; })}>‹</button>
                <div style={S.monthLabel}>{monthLabel(month.y,month.m)}</div>
                <button style={S.monthBtn} onClick={()=>setMonth(p=>{ const d=new Date(p.y,p.m+1,1); return {y:d.getFullYear(),m:d.getMonth()}; })}>›</button>
              </div>
              {isBiz && (
                <div style={{ fontSize:13, color:C.navy, fontWeight:600, background:C.navyLight, padding:'4px 12px', borderRadius:20 }}>
                  Chase MM Studios Inc.
                </div>
              )}
            </div>
            <DashboardPanel
              txs={visibleTxs} summary={viewSummary} mode={view}
              customCats={customCats} medTotal={medTotal}
              onSelectTx={setSelected} onOpenTxs={openTxs}
              pendingCount={pendingTxs.length} loading={loading}
              trends={trends} accounts={accounts} month={month}
              trendRange={trendRange} onSetTrendRange={setTrendRange} onSelectMonth={setMonth}
              isMobile={isMobile} settings={settings} filterBucket={filterBucket} filterKpi={filterKpi}
              envelopes={envelopes}
            />
            {/* The way in to AUM on a phone. The bottom nav stays at three items
                — Investments was deliberately restored to it and the widths are
                tuned for three — so this card is the mobile route rather than a
                fourth icon. On desktop it is a shortcut; the nav tab is there too.

                Composition, not a headline: the card names what the money IS
                before what it totals, and carries no delta. A large balance here
                is house-purchase money awaiting closing and will drop by most of
                itself in one step; a card showing "AUM ▼ 90%" would read as a
                disaster rather than a house. */}
            {aumData && aumData.lines?.length > 0 && (
              <div style={{ ...S.card, marginTop:16, cursor:'pointer' }} onClick={()=>setTab('aum')}>
                <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', gap:8 }}>
                  <div style={{ ...S.secTitle, marginBottom:0 }}>Under management</div>
                  <div style={{ fontSize:11, color:accentColor, fontWeight:600 }}>View →</div>
                </div>
                <div style={{ fontSize:12, color:C.stone, margin:'10px 0 8px', lineHeight:1.6 }}>
                  {(() => {
                    const g = {};
                    for (const l of aumData.lines) {
                      if (l.balance === null || l.direction !== 'asset') continue;
                      g[l.group] = (g[l.group] || 0) + l.balance;
                    }
                    const top = Object.entries(g).sort((a,b) => b[1]-a[1]).slice(0,3);
                    return top.length
                      ? top.map(([n,v]) => `${n} ${fmt(v)}`).join(' · ')
                      : 'No balances on file yet.';
                  })()}
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:12,
                              borderTop:`1px solid ${C.warmDark}`, paddingTop:8 }}>
                  <span style={{ color:C.stone }}>
                    Assets {fmt(aumData.assets)}{aumData.debts ? ` · owed −${fmt(aumData.debts)}` : ''}
                  </span>
                  <span style={{ fontWeight:700 }}>{fmt(aumData.aum)}</span>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── TRANSACTIONS ── */}
        {tab==='transactions' && (
          <>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12, flexWrap:'wrap', gap:10 }}>
              {/* An envelope filter is all-time, so the month nav does not apply.
                  Disable and dim it, and swap the label to make it unmistakable the
                  list is not month-scoped — otherwise the arrows read as broken. */}
              {filterEnvelope ? (
                <div style={{ ...S.monthNav, marginBottom:0, opacity:0.45 }} title="Month navigation is off while an envelope filter is active">
                  <button style={{ ...S.monthBtn, cursor:'not-allowed' }} disabled>‹</button>
                  <div style={{ ...S.monthLabel, display:'flex', flexDirection:'column', alignItems:'center', lineHeight:1.15 }}>
                    <span>All months</span>
                    <span style={{ fontSize:10, fontWeight:600, letterSpacing:'0.04em', color:C.stoneMid }}>ENVELOPE FILTER</span>
                  </div>
                  <button style={{ ...S.monthBtn, cursor:'not-allowed' }} disabled>›</button>
                </div>
              ) : (
                <div style={{ ...S.monthNav, marginBottom:0 }}>
                  <button style={S.monthBtn} onClick={()=>setMonth(p=>{ const d=new Date(p.y,p.m-1,1); return {y:d.getFullYear(),m:d.getMonth()}; })}>‹</button>
                  <div style={S.monthLabel}>{monthLabel(month.y,month.m)}</div>
                  <button style={S.monthBtn} onClick={()=>setMonth(p=>{ const d=new Date(p.y,p.m+1,1); return {y:d.getFullYear(),m:d.getMonth()}; })}>›</button>
                </div>
              )}
              {isBiz && (
                <div style={{ fontSize:12, color:C.navy, fontWeight:600, background:C.navyLight, padding:'4px 10px', borderRadius:16 }}>
                  💼 Chase MM Studios
                </div>
              )}
            </div>
            <TxListPanel
              txs={listTxs} hiddenTxs={listHidden} mode={view}
              customCats={customCats} loading={loading} canEdit={canEdit}
              filterCat={filterCat} filterAccount={filterAccount} filterStatus={filterStatus}
              filterBucket={filterBucket} filterEnvelope={filterEnvelope} filterKpi={filterKpi} filterIds={filterIds} envelopes={envelopes} search={search}
              monthLabelStr={monthLabel(month.y,month.m)}
              showHidden={showHidden} pendingCount={pendingTxs.length}
              onSelect={setSelected} onConfirm={handleConfirm} onConfirmAll={handleConfirmAll}
              onSetFilter={setFilterCat} onSetAccount={setFilterAccount} onSetStatus={setFilterStatus}
              onSetSearch={setSearch} onToggleHidden={setShowHidden}
              onClearBucket={() => setFilterBucket(null)}
              onClearEnvelope={() => setFilterEnvelope(null)}
              onClearKpi={() => setFilterKpi(null)}
              onClearIds={() => setFilterIds(null)}
              sortedCats={sortedCats} incomeCats={incomeCats} isMobile={isMobile} openTxId={selected?.id}
              page={page} perPage={perPage} onSetPage={setPage} onSetPerPage={setPerPage}
              sortBy={sortBy} sortDir={sortDir}
              onSetSort={(by,dir)=>{ setSortBy(by); setSortDir(dir); }}
            />
          </>
        )}

        {/* ── INVESTMENTS ── */}
        {tab==='investments' && (
          <>
            {!isMobile && <div style={{ fontSize:20, fontWeight:700, marginBottom:14 }}>📈 Investments</div>}
            {mhError && (
              <div style={{ background:C.coralLight, border:`1px solid ${C.coral}`, borderRadius:10,
                            padding:'10px 12px', marginBottom:14, fontSize:12, color:C.ink, lineHeight:1.5 }}>
                {mhError}
                <button onClick={()=>setMhError('')}
                  style={{ background:'none', border:'none', cursor:'pointer', color:C.coral, fontWeight:700, marginLeft:6 }}>
                  Dismiss
                </button>
              </div>
            )}
            <InvestmentsPanel data={invData} view={view} isOwner={isOwner} isMobile={isMobile}
              subTab={invSubTab} onSetSubTab={setInvSubTab} loading={loading}
              onGoUpload={()=>{ setTab('admin'); setAdminTab('upload'); }}
              manualProps={{
                onAdd:     ()      => { setMhError(''); setMhEdit({ holding:null, action:null }); },
                onAction:  (h, a2) => { setMhError(''); setMhEdit({ holding:h, action:a2 }); },
                onClose:   h       => { setMhError(''); setMhClose(h); },
                onDelete:  h       => { setMhError(''); setMhDelete(h); },
                onHistory: openManualHistory,
              }} />
          </>
        )}

        {/* ── AUM ── */}
        {tab==='aum' && (
          <>
            {!isMobile && <div style={{ fontSize:20, fontWeight:700, marginBottom:14 }}>🌿 Assets Under Management</div>}
            <AumPanel data={aumData} loading={aumLoading} error={aumError} view={view}
              isMobile={isMobile} onReload={()=>loadAum(view)} />
          </>
        )}

        {/* ── ADMIN ── */}
        {tab==='admin' && isOwner && (
          <>
            {!isMobile && (<>
            <div style={{ fontSize:20, fontWeight:700, marginBottom:4 }}>Admin</div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', margin:'14px 0 20px' }}>
              {ADMIN_TABS.map(([key,label]) => (
                <button key={key} onClick={()=>setAdminTab(key)}
                  style={{ ...S.pill, background:adminTab===key?C.sage:'white',
                           color:adminTab===key?'white':C.stone,
                           borderColor:adminTab===key?C.sage:C.warmDeep }}>{label}</button>
              ))}
            </div>
            </>)}

            {adminTab==='upload' && (<>
            {/* UPLOAD */}
            <div style={S.card}>
              <div style={S.secTitle}>Upload statements</div>
              <div style={{ ...S.uploadZone, ...(isDrag?S.uploadDrag:{}) }}
                onDragOver={e=>{ e.preventDefault(); setIsDrag(true); }}
                onDragLeave={()=>setIsDrag(false)}
                onDrop={e=>{ e.preventDefault(); setIsDrag(false); handleFiles(e.dataTransfer.files); }}
                onClick={()=>fileRef.current?.click()}>
                <div style={{ fontSize:32, marginBottom:8 }}>📄</div>
                <div style={{ fontSize:15, fontWeight:600, marginBottom:4 }}>Drop statements or Questrade exports here</div>
                <div style={{ fontSize:13, color:C.stone, marginBottom:12, lineHeight:1.6 }}>
                  Household: Vancity chequing PDF · Vancity Visa PDF · CIBC Costco PDF or CSV · RBC WestJet PDF<br/>
                  Business: CIBC Aeroplan PDF or CSV (card ending 8334 → auto-routes to Chase MM view)<br/>
                  Investments: Questrade InvestmentSummary.xlsx · InvestmentReturns.xlsx — upload these separately from statements
                </div>
                <button style={S.btnPrimary(false)} onClick={e=>{ e.stopPropagation(); fileRef.current?.click(); }}>Choose files</button>
                <input ref={fileRef} type="file" accept=".pdf,.csv,.xlsx" multiple style={{ display:'none' }} onChange={e=>{ handleFiles(e.target.files); e.target.value=''; }} />
              </div>
              <div style={{ marginTop:12, fontSize:13, color:C.stone }}>
                <strong>💡 CIBC tip:</strong> Export CSV from CIBC online banking for multiple months at once. Card 2368 → CIBC Costco (household). Card 8334 → Chase MM Studios (business). Auto-detected from card number.
              </div>
            </div>

            {/* UPLOAD ERRORS */}
            {uploadErrors.length > 0 && (
              <div style={{ ...S.card, marginTop:16, borderColor:C.coral }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                  <div style={{ ...S.secTitle, color:C.coral, marginBottom:0 }}>Upload errors ({uploadErrors.length})</div>
                  <button style={{ ...S.btnSm, background:'none', border:`1px solid ${C.warmDeep}`, color:C.stone }}
                    onClick={()=>api.clearUploadErrors().then(()=>setUploadErrors([]))}>Clear all</button>
                </div>
                {uploadErrors.map(e => (
                  <div key={e.id} style={{ padding:'12px 14px', background:C.coralLight, borderRadius:10, marginBottom:8 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:600, marginBottom:2 }}>📄 {e.filename}</div>
                        <div style={{ fontSize:12, color:C.ink, marginBottom:6 }}>{e.error_message}</div>
                        {e.suggested_fix && (
                          <div style={{ fontSize:12, color:C.stone, lineHeight:1.5 }}>
                            <strong>Try this:</strong> {e.suggested_fix}
                          </div>
                        )}
                        <div style={{ fontSize:11, color:C.stoneMid, marginTop:6 }}>{new Date(e.created_at).toLocaleString()}</div>
                      </div>
                      <button onClick={()=>api.deleteUploadError(e.id).then(()=>setUploadErrors(p=>p.filter(x=>x.id!==e.id)))}
                        style={{ background:'none', border:'none', cursor:'pointer', color:C.stoneMid, fontSize:18, lineHeight:1, padding:'0 4px' }}>×</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* IMPORT HISTORY */}
            <div style={{ ...S.card, marginTop:16 }}>
              <div style={S.secTitle}>Import history</div>
              {importLog.rows.length === 0 ? (
                <div style={{ fontSize:13, color:C.stone }}>No imports recorded yet.</div>
              ) : (
                <>
                  {importLog.rows.map(l => (
                    <div key={l.id} style={{ padding:'10px 12px', background:C.warm, borderRadius:10, marginBottom:6 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                        <div style={{ flex:'2 1 180px', minWidth:0, fontSize:12, fontWeight:600, wordBreak:'break-all' }}>
                          📄 {l.filename}
                        </div>
                        <div style={{ fontSize:11, color:C.stoneMid, flex:'1 1 140px' }}>{l.statement_type} · {l.account_name}</div>
                        <div style={{ fontSize:12, color:C.sage, fontWeight:600, flex:'0 0 auto' }}>{l.imported_count} imported</div>
                        <div style={{ fontSize:12, color:C.stoneMid, flex:'0 0 auto' }}>
                          {l.duplicate_count} duplicate{l.duplicate_count===1?'':'s'} skipped
                        </div>
                        {isOwner && (
                          <button style={{ ...S.btnSm, background:'none', border:`1px solid ${C.coral}`, color:C.coral, flex:'0 0 auto' }}
                            onClick={()=>setConfirmDeleteBatch({ batch_id:l.batch_id, filename:l.filename, imported_count:l.imported_count })}>
                            Undo
                          </button>
                        )}
                      </div>
                      <div style={{ fontSize:11, color:C.stoneMid, marginTop:4 }}>
                        {l.uploaded_by || 'Unknown'} · {new Date(l.uploaded_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:10, marginTop:10 }}>
                    <button style={{ ...S.btnGhost, opacity:importLog.page<=1?0.5:1, cursor:importLog.page<=1?'not-allowed':'pointer' }}
                      disabled={importLog.page<=1} onClick={()=>loadImportLog(importLog.page-1)}>Prev</button>
                    <div style={{ fontSize:12, color:C.stone }}>Page {importLog.page} of {importLog.pages}</div>
                    <button style={{ ...S.btnGhost, opacity:importLog.page>=importLog.pages?0.5:1, cursor:importLog.page>=importLog.pages?'not-allowed':'pointer' }}
                      disabled={importLog.page>=importLog.pages} onClick={()=>loadImportLog(importLog.page+1)}>Next</button>
                  </div>
                </>
              )}
            </div>
            </>)}

            {!isMobile && adminTab==='rules' && (
            /* RULES */
            <div style={S.card}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                <div style={S.secTitle}>Merchant rules</div>
                <button style={S.btnPrimary(false)} onClick={()=>{ setRuleInitial(null); setShowRuleModal(true); }}>＋ Add rule</button>
              </div>
              <div style={{ fontSize:12, color:C.stone, marginBottom:14 }}>Applies retroactively to all existing transactions and all future imports.</div>
              {rules.map(rule => {
                const cfg = getCat(rule.category, rule.view || 'household', customCats);
                return (
                  <div key={rule.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 12px', background:C.warm, borderRadius:10, marginBottom:8 }}>
                    <div style={{ ...S.txIcon, background:cfg.light, width:30, height:30, fontSize:13 }}>{cfg.icon}</div>
                    <div style={{ flex:1, fontSize:13, fontWeight:500 }}>"{rule.keyword}"</div>
                    {/* Keyword is only unique per view now — two rules can share one. */}
                    <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:8,
                                   background: rule.view==='business' ? C.navyLight : C.sageLight,
                                   color: rule.view==='business' ? C.navy : C.sage }}>
                      {rule.view==='business' ? '💼 Business' : '🏠 Household'}
                    </span>
                    <div style={{ color:C.stoneMid }}>→</div>
                    <span style={{ fontSize:11, fontWeight:600, background:cfg.light, color:cfg.color, padding:'2px 8px', borderRadius:10 }}>{rule.category}</span>
                    <button onClick={()=>api.deleteRule(rule.id).then(()=>setRules(p=>p.filter(r=>r.id!==rule.id)))}
                      style={{ background:'none', border:'none', cursor:'pointer', color:C.stoneMid, fontSize:18, lineHeight:1, padding:'0 4px' }}>×</button>
                  </div>
                );
              })}
            </div>
            )}

            {!isMobile && adminTab==='categories' && (
            /* CATEGORIES */
            <div style={S.card}>
              <div style={S.secTitle}>Custom categories</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:8, marginBottom:16 }}>
                {customCats.map(cat => {
                  // PATCH returns the updated row, so state moves in place rather
                  // than paying for a full loadAll() on every colour nudge.
                  const saveCat = patch => api.updateCat(cat.id, patch)
                    .then(u => setCustomCats(p => p.map(x => x.id===cat.id ? u : x)))
                    .catch(e => console.error(e));
                  return (
                  <div key={cat.id} style={{ ...S.cardSm, display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                    <div style={{ width:30, height:30, borderRadius:8, background:cat.light_color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14 }}>{cat.icon}</div>
                    <div style={{ flex:1, minWidth:70 }}><div style={{ fontSize:13, fontWeight:600 }}>{cat.name}</div></div>
                    {delCatId===cat.id ? (
                      <div style={{ display:'flex', gap:4 }}>
                        <button onClick={()=>api.deleteCat(cat.id).then(()=>{ setDelCatId(null); loadAll(); }).catch(()=>setDelCatId(null))}
                          style={{ ...S.btnSm, padding:'3px 7px', fontSize:10, background:C.coral, color:'white', border:'none' }}>Delete?</button>
                        <button onClick={()=>setDelCatId(null)}
                          style={{ ...S.btnSm, padding:'3px 7px', fontSize:10, background:'transparent', color:C.stone, border:`1px solid ${C.warmDeep}` }}>Cancel</button>
                      </div>
                    ) : (
                      <button onClick={()=>setDelCatId(cat.id)} title="Delete category"
                        style={{ background:'none', border:'none', cursor:'pointer', color:C.stoneMid, fontSize:16 }}>×</button>
                    )}
                    <div style={{ display:'flex', gap:4, alignItems:'center', width:'100%' }}>
                      <input defaultValue={cat.icon} maxLength={2} title="Icon"
                        key={`ic${cat.id}${cat.icon}`}
                        onBlur={e=>e.target.value.trim()&&e.target.value!==cat.icon&&saveCat({icon:e.target.value})}
                        style={{ ...S.input, width:44, fontSize:12, padding:'4px 6px', textAlign:'center' }} />
                      <input type="color" defaultValue={cat.color} title="Color"
                        key={`co${cat.id}${cat.color}`}
                        onBlur={e=>e.target.value!==cat.color&&saveCat({color:e.target.value})}
                        style={{ ...S.input, width:40, padding:2, height:30 }} />
                      <select value={cat.view||'both'} onChange={e=>saveCat({view:e.target.value})} title="Show in"
                        style={{ ...S.input, flex:1, fontSize:11, padding:'4px 6px' }}>
                        <option value="both">🏠💼 Both</option>
                        <option value="household">🏠 Household</option>
                        <option value="business">💼 Business</option>
                      </select>
                      <select value={cat.type||'expense'} onChange={e=>saveCat({type:e.target.value})} title="Type"
                        style={{ ...S.input, flex:1, fontSize:11, padding:'4px 6px' }}>
                        <option value="expense">💸 Expense</option>
                        <option value="income">💵 Income</option>
                        <option value="gift">🎁 Gift</option>
                      </select>
                      {(cat.type||'expense')==='expense' && (
                        <select value={cat.bucket||'wants'} onChange={e=>saveCat({bucket:e.target.value})} title="Bucket"
                          style={{ ...S.input, flex:1, fontSize:11, padding:'4px 6px' }}>
                          <option value="needs">🏠 Needs</option>
                          <option value="wants">🎭 Wants</option>
                          <option value="savings">💰 Savings</option>
                        </select>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'flex-end' }}>
                <div style={{ flex:2, minWidth:140 }}>
                  <div style={S.modalLabel}>Name</div>
                  <input style={S.input} placeholder="e.g. Date Nights" value={newCat.name} onChange={e=>setNewCat(p=>({...p,name:e.target.value}))} />
                </div>
                <div style={{ width:70 }}>
                  <div style={S.modalLabel}>Icon</div>
                  <input style={S.input} maxLength={2} value={newCat.icon} onChange={e=>setNewCat(p=>({...p,icon:e.target.value}))} />
                </div>
                <div style={{ width:70 }}>
                  <div style={S.modalLabel}>Color</div>
                  <input type="color" style={{ ...S.input, padding:4, height:40 }} value={newCat.color} onChange={e=>setNewCat(p=>({...p,color:e.target.value}))} />
                </div>
                <div style={{ width:130 }}>
                  <div style={S.modalLabel}>Show in</div>
                  <select style={S.input} value={newCat.view} onChange={e=>setNewCat(p=>({...p,view:e.target.value}))}>
                    <option value="both">🏠💼 Both</option>
                    <option value="household">🏠 Household</option>
                    <option value="business">💼 Business</option>
                  </select>
                </div>
                <div style={{ width:120 }}>
                  <div style={S.modalLabel}>Type</div>
                  <select style={S.input} value={newCat.type} onChange={e=>setNewCat(p=>({...p,type:e.target.value}))}>
                    <option value="expense">💸 Expense</option>
                    <option value="income">💵 Income</option>
                    <option value="gift">🎁 Gift</option>
                  </select>
                </div>
                {newCat.type==='expense' && (
                  <div style={{ width:120 }}>
                    <div style={S.modalLabel}>Bucket</div>
                    <select style={S.input} value={newCat.bucket||'wants'} onChange={e=>setNewCat(p=>({...p,bucket:e.target.value}))}>
                      <option value="needs">🏠 Needs</option>
                      <option value="wants">🎭 Wants</option>
                      <option value="savings">💰 Savings</option>
                    </select>
                  </div>
                )}
                <button style={{ ...S.btnPrimary(false), alignSelf:'flex-end' }} onClick={()=>{ if(!newCat.name)return; api.addCat({ ...newCat, light_color:tintColor(newCat.color) }).then(loadAll); setNewCat({name:'',icon:'📦',color:C.sage,view:'both',type:'expense',bucket:'wants'}); }}>Add</button>
              </div>
            </div>
            )}

            {!isMobile && adminTab==='accounts' && (
            /* ACCOUNTS */
            <div style={S.card}>
              <div style={S.secTitle}>Accounts</div>
              <div style={{ fontSize:12, color:C.stone, marginBottom:14 }}>
                Identifier is a card's last 4 digits or a keyword that appears in the statement — it decides
                which account an imported transaction belongs to. Renaming an account updates its existing
                transactions too.
              </div>
              {acctError && (
                <div style={{ background:C.coralLight, border:`1px solid ${C.coral}`, borderRadius:10,
                              padding:'10px 12px', marginBottom:14, fontSize:12, color:C.ink, lineHeight:1.5 }}>
                  {acctError}
                  <button onClick={()=>setAcctError('')}
                    style={{ background:'none', border:'none', cursor:'pointer', color:C.coral, fontWeight:700, marginLeft:6 }}>
                    Dismiss
                  </button>
                </div>
              )}
              {accounts.map(a => {
                const acctInput = { ...S.input, width:'auto', fontSize:12, padding:'6px 9px' };
                const save = patch => api.updateAccount(a.id, patch)
                  .then(u => { setAccounts(p=>p.map(x=>x.id===a.id?u:x)); if (patch.display_name) loadAll(); })
                  .catch(e => console.error(e));
                const count = allTxs.filter(t=>t.account===a.display_name).length;
                return (
                  <div key={a.id} style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 12px', background:C.warm, borderRadius:10, marginBottom:6, flexWrap:'wrap' }}>
                    <div style={{ width:8, height:8, borderRadius:'50%', flexShrink:0,
                                  background:a.account_type==='credit'?C.coral:a.account_type==='savings'?C.gold:C.sage }} />
                    <input defaultValue={a.display_name} key={`n${a.id}${a.display_name}`}
                      onBlur={e=>e.target.value.trim()&&e.target.value!==a.display_name&&save({display_name:e.target.value})}
                      style={{ ...acctInput, flex:'2 1 150px' }} />
                    <select value={a.account_type} onChange={e=>save({account_type:e.target.value})}
                      style={{ ...acctInput, flex:'1 1 100px' }}>
                      <option value="chequing">Chequing</option>
                      <option value="savings">Savings</option>
                      <option value="credit">Credit</option>
                    </select>
                    <select value={a.view} onChange={e=>save({view:e.target.value})}
                      style={{ ...acctInput, flex:'1 1 105px' }}>
                      <option value="household">🏠 Household</option>
                      <option value="business">💼 Business</option>
                    </select>
                    <input defaultValue={a.identifier||''} placeholder="last 4 / keyword"
                      key={`i${a.id}${a.identifier}`}
                      onBlur={e=>e.target.value!==(a.identifier||'')&&save({identifier:e.target.value})}
                      style={{ ...acctInput, flex:'1 1 115px' }} />
                    <div style={{ fontSize:11, color:C.stoneMid, minWidth:42 }}>{count} tx</div>
                    <button onClick={()=>{ setAcctError(''); api.deleteAccount(a.id)
                        .then(()=>setAccounts(p=>p.filter(x=>x.id!==a.id)))
                        .catch(e=>setAcctError(e.message)); }}
                      style={{ background:'none', border:'none', cursor:'pointer', color:C.stoneMid, fontSize:18, lineHeight:1, padding:'0 4px' }}>×</button>
                  </div>
                );
              })}
              <div style={{ display:'flex', gap:6, alignItems:'center', marginTop:12, flexWrap:'wrap' }}>
                <input placeholder="Account name" value={newAcct.display_name}
                  onChange={e=>setNewAcct({...newAcct, display_name:e.target.value})}
                  style={{ ...S.input, width:'auto', flex:'2 1 150px', fontSize:12, padding:'6px 9px' }} />
                <select value={newAcct.account_type} onChange={e=>setNewAcct({...newAcct, account_type:e.target.value})}
                  style={{ ...S.input, width:'auto', flex:'1 1 100px', fontSize:12, padding:'6px 9px' }}>
                  <option value="chequing">Chequing</option>
                  <option value="savings">Savings</option>
                  <option value="credit">Credit</option>
                </select>
                <select value={newAcct.view} onChange={e=>setNewAcct({...newAcct, view:e.target.value})}
                  style={{ ...S.input, width:'auto', flex:'1 1 105px', fontSize:12, padding:'6px 9px' }}>
                  <option value="household">🏠 Household</option>
                  <option value="business">💼 Business</option>
                </select>
                <input placeholder="last 4 / keyword" value={newAcct.identifier}
                  onChange={e=>setNewAcct({...newAcct, identifier:e.target.value})}
                  style={{ ...S.input, width:'auto', flex:'1 1 115px', fontSize:12, padding:'6px 9px' }} />
                <button style={{ ...S.btnPrimary(false), fontSize:12, padding:'7px 13px' }}
                  onClick={()=>{ if(!newAcct.display_name.trim())return;
                    api.addAccount(newAcct)
                      .then(a=>{ setAccounts(p=>[...p,a].sort((x,y)=>x.display_name.localeCompare(y.display_name)));
                                 setNewAcct({ display_name:'', account_type:'chequing', view:'household', identifier:'' }); })
                      .catch(e=>console.error(e)); }}>Add</button>
              </div>
            </div>

            )}

            {!isMobile && adminTab==='envelopes' && (
            /* ENVELOPES */
            <div style={S.card}>
              <div style={S.secTitle}>Envelopes</div>
              <div style={{ fontSize:12, color:C.stone, marginBottom:14 }}>
                A funded spending pot — money set aside outside monthly income (wedding gifts, house-sale
                proceeds). Transactions tagged to an envelope leave the Needs/Wants/Savings percentages but
                still count in Spending and Cashflow. Budget is editable anytime. Closing hides it from the
                dashboard but keeps its history; deleting untags its transactions rather than deleting them.
              </div>
              {envError && (
                <div style={{ background:C.coralLight, border:`1px solid ${C.coral}`, borderRadius:10,
                              padding:'10px 12px', marginBottom:14, fontSize:12, color:C.ink, lineHeight:1.5 }}>
                  {envError}
                  <button onClick={()=>setEnvError('')}
                    style={{ background:'none', border:'none', cursor:'pointer', color:C.coral, fontWeight:700, marginLeft:6 }}>Dismiss</button>
                </div>
              )}
              {envelopes.map(e => {
                const envInput = { ...S.input, width:'auto', fontSize:12, padding:'6px 9px' };
                const save = patch => api.updateEnvelope(e.id, patch)
                  .then(u => setEnvelopes(p=>p.map(x=>x.id===e.id?{...x,...u}:x).sort((a,b)=>a.name.localeCompare(b.name))))
                  .catch(err => setEnvError(err.message));
                const spent = parseFloat(e.spent||0);
                const span = e.first_tagged ? `${fmtDate(e.first_tagged)} – ${fmtDate(e.last_tagged)}` : 'no tags';
                return (
                  <div key={e.id} style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 12px',
                       background: e.status==='closed'?C.warmDark:C.warm, borderRadius:10, marginBottom:6, flexWrap:'wrap',
                       opacity: e.status==='closed'?0.7:1 }}>
                    <input defaultValue={e.name} key={`n${e.id}${e.name}`}
                      onBlur={ev=>ev.target.value.trim()&&ev.target.value!==e.name&&save({name:ev.target.value})}
                      style={{ ...envInput, flex:'2 1 140px' }} />
                    <div style={{ display:'flex', alignItems:'center', gap:3, flex:'1 1 110px' }}>
                      <span style={{ fontSize:12, color:C.stoneMid }}>$</span>
                      <input type="number" step="0.01" defaultValue={e.budget} key={`b${e.id}${e.budget}`}
                        onBlur={ev=>ev.target.value!==String(e.budget)&&!isNaN(parseFloat(ev.target.value))&&save({budget:ev.target.value})}
                        style={{ ...envInput, width:'100%' }} />
                    </div>
                    <div style={{ fontSize:11, color:C.stoneMid, minWidth:110 }}>{fmt(spent)} spent · {e.tx_count} tx</div>
                    <div style={{ fontSize:11, color:C.stoneMid, minWidth:120 }}>{span}</div>
                    <button onClick={()=>save({ status: e.status==='open'?'closed':'open' })}
                      style={{ ...S.pill, fontSize:11, padding:'4px 10px', cursor:'pointer', background:'white', borderColor:C.warmDeep, color:C.stone }}>
                      {e.status==='open'?'Close':'Reopen'}
                    </button>
                    <button onClick={()=>{ setEnvError(''); setEnvDelete(e); }}
                      style={{ background:'none', border:'none', cursor:'pointer', color:C.stoneMid, fontSize:18, lineHeight:1, padding:'0 4px' }}>×</button>
                  </div>
                );
              })}
              <div style={{ display:'flex', gap:6, alignItems:'center', marginTop:12, flexWrap:'wrap' }}>
                <input placeholder="Envelope name" value={newEnv.name}
                  onChange={e=>setNewEnv({...newEnv, name:e.target.value})}
                  style={{ ...S.input, width:'auto', flex:'2 1 140px', fontSize:12, padding:'6px 9px' }} />
                <input type="number" step="0.01" placeholder="Budget" value={newEnv.budget}
                  onChange={e=>setNewEnv({...newEnv, budget:e.target.value})}
                  style={{ ...S.input, width:'auto', flex:'1 1 110px', fontSize:12, padding:'6px 9px' }} />
                <button style={{ ...S.btnPrimary(false), fontSize:12, padding:'7px 13px' }}
                  onClick={()=>{ if(!newEnv.name.trim()||isNaN(parseFloat(newEnv.budget)))return; setEnvError('');
                    api.addEnvelope({ name:newEnv.name, budget:newEnv.budget, view:'household', notes:newEnv.notes||null })
                      .then(ne=>{ setEnvelopes(p=>[...p,{...ne,spent:0,tx_count:0,first_tagged:null,last_tagged:null}].sort((a,b)=>a.name.localeCompare(b.name)));
                                  setNewEnv({ name:'', budget:'', view:'household', notes:'' }); })
                      .catch(err=>setEnvError(err.message)); }}>Add</button>
              </div>
            </div>
            )}

            {!isMobile && adminTab==='settings' && (<>
            {/* BUDGET TARGETS */}
            <div style={S.card}>
              <div style={S.secTitle}>Budget targets · Needs / Wants / Savings</div>
              <BudgetTargets settings={settings} onSave={async data => {
                const updated = await api.saveSettings(data);
                setSettings(updated);
              }} />
            </div>

            {/* EXPORT */}
            <div style={S.card}>
              <div style={S.secTitle}>Export</div>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                <button style={S.btnGhost} onClick={()=>api.exportCSV(month.y, String(month.m+1))}>📥 Export {monthLabel(month.y,month.m)} CSV</button>
                <button style={S.btnGhost} onClick={()=>api.exportCSV(month.y,'')}>📥 Export full year {month.y}</button>
              </div>
              <div style={{ fontSize:12, color:C.stone, marginTop:10 }}>Includes all transactions with categories and tax tags - useful for year-end with your accountant.</div>
            </div>

            {/* MEDICAL */}
            {medTotal>0 && (
              <div style={{ ...S.card, marginTop:16, background:C.mintLight, border:`1px solid ${C.sageMid}` }}>
                <div style={{ ...S.secTitle, color:C.sage }}>Tax: medical expenses (all time)</div>
                <div style={{ fontSize:22, fontWeight:700, marginBottom:4 }}>{fmt(medTotal)}</div>
                <div style={{ fontSize:13, color:C.stone }}>At 3% of $54K salary = $1,620 threshold. Export year-end CSV filtered by tax_tag = "medical" for your accountant.</div>
              </div>
            )}

            {/* USERS */}
            <div style={{ ...S.card, marginTop:16 }}>
              <div style={S.secTitle}>Users & access</div>
              {[
                { name:'Cory', role:'Owner', desc:'Full access - both views, admin panel, upload, rules, settings', col:C.sage },
                { name:'Ashley', role:'Family', desc:'Household view - dashboard, transactions (confirm, categorize, tag)', col:C.sky },
                { name:'Guest', role:'Viewer', desc:'Household view read-only', col:C.stoneMid },
              ].map(u => (
                <div key={u.name} style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'12px 0', borderBottom:`1px solid ${C.warmDark}` }}>
                  <div style={{ width:36, height:36, borderRadius:'50%', background:u.col+'22', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>
                    {u.name==='Cory'?'👨':u.name==='Ashley'?'👩':'👁️'}
                  </div>
                  <div>
                    <div style={{ fontSize:14, fontWeight:600 }}>{u.name} <span style={{ fontSize:11, background:u.col+'22', color:u.col, padding:'1px 7px', borderRadius:10, marginLeft:4 }}>{u.role}</span></div>
                    <div style={{ fontSize:12, color:C.stone, marginTop:2 }}>{u.desc}</div>
                  </div>
                </div>
              ))}
              <div style={{ fontSize:12, color:C.stone, marginTop:12 }}>Credentials are set in Railway environment variables. To change a password, update the env var and redeploy.</div>
            </div>
            </>)}
          </>
        )}
      </div>

      {/* BOTTOM NAV (mobile only) — three items. This reverses 913a3af, which cut
          three to two because Admin does not belong on a phone. Investments does:
          it is read-only, it is the thing most worth glancing at away from a desk,
          and it needs no typing. Each item keeps ~33% width at minHeight 56. */}
      {isMobile && (
        <nav style={S.bottomNav}>
          <button style={S.bottomNavItem(tab==='dashboard', accentColor)} onClick={()=>setTab('dashboard')}>
            <span style={{ fontSize:20 }}>📊</span>Dashboard
          </button>
          <button style={S.bottomNavItem(tab==='transactions', accentColor)} onClick={()=>setTab('transactions')}>
            <span style={{ fontSize:20, position:'relative' }}>📋
              {pendingTxs.length>0 && <span style={{ position:'absolute', top:-4, right:-10, background:C.amber, color:'white', fontSize:9, fontWeight:700, borderRadius:8, padding:'0 4px' }}>{pendingTxs.length}</span>}
            </span>Transactions
          </button>
          <button style={S.bottomNavItem(tab==='investments', accentColor)} onClick={()=>setTab('investments')}>
            <span style={{ fontSize:20 }}>📈</span>Investments
          </button>
        </nav>
      )}

      {/* MODALS */}
      {preview && (
        <UploadPreviewModal preview={preview} accounts={accounts}
          busy={uploadState==='uploading'}
          onCancel={()=>{ setPreview(null); setPendingFiles(null); }}
          onImport={handleConfirmImport} />
      )}
      {invPreview && (
        <InvestmentPreviewModal preview={invPreview}
          busy={uploadState==='uploading'}
          onCancel={()=>{ setInvPreview(null); setPendingInvFiles(null); }}
          onImport={handleConfirmInvestmentImport} />
      )}
      {selected && (
        <TxModal tx={selected} mode={view} customCats={customCats} rules={rules} accounts={accounts} envelopes={envelopes} onClose={()=>setSelected(null)} onUpdate={handleTxUpdate}
          canEdit={canEdit} onEnvelopeChange={reloadEnvelopes}
          onCreateRule={tx=>{ setRuleInitial({ keyword:tx.description.split(' ').slice(0,3).join(' ').toLowerCase(), category:tx.category }); setShowRuleModal(true); }} />
      )}
      {showRuleModal && (
        <RuleModal initial={ruleInitial} mode={view} customCats={customCats} onSave={handleAddRule} onClose={()=>setShowRuleModal(false)} />
      )}
      {envDelete && (
        <ConfirmDeleteEnvelopeModal envelope={envDelete}
          onCancel={()=>setEnvDelete(null)}
          onConfirm={async ()=>{ try { await api.deleteEnvelope(envDelete.id); setEnvDelete(null); await loadAll(); }
                                 catch(e){ setEnvError(e.message); setEnvDelete(null); } }} />
      )}
      {mhEdit && (
        <ManualHoldingModal holding={mhEdit.holding} action={mhEdit.action} busy={mhBusy}
          onCancel={()=>{ setMhEdit(null); setMhError(''); }} onSave={handleManualSave} />
      )}
      {mhClose && (
        <ManualCloseModal holding={mhClose} busy={mhBusy}
          onCancel={()=>{ setMhClose(null); setMhError(''); }}
          onConfirm={d=>runManual(()=>api.closeManualHolding(mhClose.id, d))} />
      )}
      {mhDelete && (
        <ManualDeleteModal holding={mhDelete} busy={mhBusy}
          onCancel={()=>{ setMhDelete(null); setMhError(''); }}
          onConfirm={()=>runManual(()=>api.deleteManualHolding(mhDelete.id))} />
      )}
      {mhHistory && (
        <ManualHistoryModal holding={mhHistory} events={mhEvents} loading={mhLoadingEvents}
          onClose={()=>{ setMhHistory(null); setMhEvents([]); }} />
      )}
      {confirmDeleteBatch && (
        <ConfirmDeleteBatchModal batch={confirmDeleteBatch} busy={deletingBatch}
          onCancel={()=>setConfirmDeleteBatch(null)} onConfirm={handleUndoImport} />
      )}
    </div>
  );
}
